import { fmtMoneda } from '../../utils/moneda';
import { marcaImpresion, getAbrev } from '../../utils/marca';
import { imprimirHTML } from '../../utils/imprimir';
import { analizarPolizaIA, fileADatosIA, primaNetaIA, matchCatalogo } from '../../utils/docIA';
import { describirCambioHTML } from '../../utils/endosoDetalle';
import { authHeaders } from '../../utils/apiIA';
import { abrirDocumento } from '../../utils/archivos';
import PolizaSelect from '../../components/PolizaSelect';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, insertRow, updateRow, deleteRow, fetchAutoLimiteOpciones, fetchCoberturaDescripciones, fetchReferidos } from '../../db/engine';
import { useApp } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit2, Trash2, Eye, X, CheckSquare,
  ArrowRight, AlertTriangle, ChevronDown, ChevronUp,
  Paperclip, Save, RefreshCw, Download, Trash2 as TrashIcon, ExternalLink, Printer, Search,
} from 'lucide-react';
import SearchSelect from '../../components/SearchSelect';
import ClienteSelect from '../../components/ClienteSelect';
import DocManager from '../../components/DocManager';
import CoberturaDesc from '../../components/CoberturaDesc';
import VehiculoListInput from '../../components/VehiculoListInput';
import EndosoCambiosMulti from '../../components/EndosoCambiosMulti';
import EndosoCambioForm from '../../components/EndosoCambioForm';
import BeneficiarioConfidencial from '../../components/BeneficiarioConfidencial';
import { aplicarCambioEndoso, targetAplica } from '../../db/endosos';
import { detectAutoTipo, autoOpcionesPorTipo } from '../../utils/autoLimites';
import { reaplicarSacoPoliza } from '../../db/saco';
import { cerrarPeriodoDeclarado } from '../../db/periodos';
import { generarComisionNegativa } from '../../db/comisiones';
import { fmtFecha, sumarUnAnioISO } from '../../utils/fecha';


// ── Helpers ──────────────────────────────────────────────────────
const FRECUENCIAS = [
  { value:'mensual',    label:'Mensual',    meses:1  },
  { value:'bimensual',  label:'Bimensual',  meses:2  },
  { value:'trimestral', label:'Trimestral', meses:3  },
  { value:'semestral',  label:'Semestral',  meses:6  },
  { value:'anual',      label:'Anual',      meses:12 },
];

function calcularCuotas(prima, impuesto, frecuencia, numCuotas, fechaInicio) {
  const total = parseFloat(prima||0) * (1 + parseFloat(impuesto||0)/100);
  const n = parseInt(numCuotas)||1;
  const frec = FRECUENCIAS.find(f=>f.value===frecuencia)||FRECUENCIAS[0];
  const montoCuota = Math.round(total/n*100)/100;
  const cuotas = [];
  for (let i=0;i<n;i++){
    const fecha = fechaInicio ? new Date(fechaInicio+'T12:00:00') : null;
    if (fecha) fecha.setMonth(fecha.getMonth()+i*frec.meses);
    const monto = i===n-1 ? Math.round((total-montoCuota*(n-1))*100)/100 : montoCuota;
    cuotas.push({num:i+1,monto,fecha:fecha?fecha.toISOString().slice(0,10):null});
  }
  return {total,cuotas,mesesTotal:n*frec.meses};
}

function formatDeducible(c) {
  if(!c.tiene_deducible) return null;
  switch(c.deducible_tipo){
    case 'persona_evento': return `$${c.ded_por_persona||0} por persona / $${c.ded_por_evento||0} por evento`;
    case 'evento':         return `$${c.ded_por_evento||0} por evento`;
    case 'pct_minimo':     return `${c.ded_pct||0}% de la SA, mínimo $${c.ded_minimo||0}`;
    case 'pct':            return `${c.ded_pct||0}% de la SA`;
    case 'personalizado':  return c.ded_personalizado||'';
    default: return null;
  }
}

function newCobFromTemplate(c) {
  return {
    cobertura_id:c.id, nombre:c.nombre, descripcion:c.descripcion||'', tipo_limite:'limite',
    suma_asegurada:'', incluida:false, es_personalizada:false, // ganchito NUNCA automático: la persona decide
    tiene_deducible:false, deducible_tipo:'evento',
    ded_por_persona:'',ded_por_evento:'',ded_pct:'',ded_minimo:'',ded_personalizado:'',
    otros:'', expanded:false,
  };
}

const fmt$ = fmtMoneda;

function traducirError(e) {
  const msg = e?.message || '';
  const code = e?.code || '';
  if (code === '23505' || msg.includes('unique')) {
    if (msg.includes('polizas_numero_fecha') || msg.includes('polizas_numero')) return 'Ya existe una póliza con ese número y la misma fecha de inicio. Si es una renovación, verifica que la fecha de inicio sea diferente a la vigencia anterior.';
    if (msg.includes('clientes'))      return 'Ya existe un cliente con ese dato. Verifica en el módulo de Clientes.';
    return 'Ya existe un registro con ese dato. Verifica que no esté duplicado.';
  }
  if (code === '23503' || msg.includes('foreign key')) return 'No se puede completar la operación porque falta un dato relacionado (cliente, aseguradora o ramo).';
  if (code === '23502' || msg.includes('not-null'))    return 'Faltan datos obligatorios. Revisa que todos los campos requeridos estén completos.';
  if (msg.includes('row-level security'))              return 'Sin permiso para realizar esta acción. Contacta al administrador.';
  if (msg.includes('JWT') || msg.includes('auth'))     return 'Tu sesión expiró. Recarga la página e inicia sesión de nuevo.';
  return msg || 'Ocurrió un error inesperado. Intenta de nuevo.';
}
const numSol = id => 'SOL-'+String(id).padStart(4,'0');
const fmtBytes = b => b > 1048576 ? `${(b/1048576).toFixed(1)} MB` : `${(b/1024).toFixed(0)} KB`;

function generateSlipHTML({ sol, coberturas = [], autoData = null, agentes = [] }) {
  const M = marcaImpresion();
  const today = fmtFecha(new Date());
  const tipoLabel = { cotizacion:'Cotización', nueva:'Solicitud de Póliza Nueva', endoso:'Endoso', renovacion:'Renovación' }[sol.tipo] || sol.tipo;

  const row = (label, value) => value != null && value !== ''
    ? `<tr><td class="lbl">${label}</td><td>${value}</td></tr>`
    : '';

  const primaVal = sol.prima || sol.prima_estimada;
  const comisionStr = sol.es_fee
    ? `Fee ${sol.monto_fee ? '$'+Number(sol.monto_fee).toLocaleString('es-PA',{minimumFractionDigits:2}) : ''}`
    : sol.comision_ramo != null ? `${sol.comision_ramo}%` : '';

  const cobHtml = coberturas.length > 0 ? `
    <h3>COBERTURAS</h3>
    <table class="cob-table">
      <thead><tr><th>Cobertura</th><th>Tipo</th><th>Suma Asegurada</th><th>Deducible</th></tr></thead>
      <tbody>
        ${coberturas.map(c => `<tr>
          <td>${c.nombre || ''}</td>
          <td>${c.tipo_limite === 'sub_limite' ? 'Sub-límite' : 'Límite'}</td>
          <td>${c.suma_asegurada != null ? '$'+Number(c.suma_asegurada).toLocaleString('es-PA',{minimumFractionDigits:2}) : '—'}</td>
          <td>${c.deducible || '—'}</td>
        </tr>${c.otros ? `<tr><td colspan="4" class="otros">${c.otros}</td></tr>` : ''}`).join('')}
      </tbody>
    </table>` : '';

  const autoHtml = autoData ? `
    <h3>DATOS DEL VEHÍCULO</h3>
    <table class="info-table">
      <tbody>
        ${row('Año', autoData.anio)}
        ${row('Marca', autoData.marca)}
        ${row('Modelo', autoData.modelo)}
        ${row('Color', autoData.color)}
        ${row('Tipo', autoData.tipo_vehiculo)}
        ${row('Pasajeros', autoData.pasajeros)}
        ${row('No. Motor', autoData.no_motor)}
        ${row('Chasis', autoData.chasis_igual_motor ? autoData.no_motor : autoData.chasis)}
        ${row('Placa', autoData.placa_por_reportar ? 'Por reportar' : autoData.placa)}
      </tbody>
    </table>` : '';

  const agentesHtml = agentes.length > 0 ? `
    <h3>AGENTES</h3>
    <table class="cob-table">
      <thead><tr><th>Agente</th><th>% Comisión</th></tr></thead>
      <tbody>
        ${agentes.map(a => `<tr><td>${a.agentes?.nombre || a.nombre || ''}</td><td>${a.porcentaje}%</td></tr>`).join('')}
      </tbody>
    </table>` : '';

  const partesRows = [
    sol.asegurado_nombre ? `<tr><td class="lbl">Asegurado</td><td>${sol.asegurado_nombre}${sol.asegurado_cedula ? ' — ' + sol.asegurado_cedula : ''}</td></tr>` : '',
    sol.tomador_nombre   ? `<tr><td class="lbl">Tomador</td><td>${sol.tomador_nombre}${sol.tomador_cedula ? ' — ' + sol.tomador_cedula : ''}</td></tr>` : '',
    sol.beneficiario_nombre ? `<tr><td class="lbl">Beneficiario</td><td>${sol.beneficiario_nombre}${sol.beneficiario_cedula ? ' — ' + sol.beneficiario_cedula : ''}</td></tr>` : '',
  ].filter(Boolean).join('');
  const partesHtml = partesRows ? `<h3>PARTES</h3><table class="info-table"><tbody>${partesRows}</tbody></table>` : '';

  return `<!DOCTYPE html><html lang="es"><head>
<meta charset="utf-8"/>
<title>Slip — ${numSol(sol.id)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Outfit', Arial, sans-serif; font-size: 12px; color: #1a2535; padding: 24px 32px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid ${M.colores.navy}; padding-bottom: 12px; margin-bottom: 16px; }
  .brand { font-size: 20px; font-weight: 700; color: ${M.colores.navy}; letter-spacing: 0.5px; }
  .tagline { font-size: 10px; color: ${M.colores.silver}; margin-top: 2px; }
  .doc-title { text-align: right; }
  .doc-title .tipo { font-size: 14px; font-weight: 700; color: ${M.colores.steel}; }
  .doc-title .num { font-size: 22px; font-weight: 700; color: ${M.colores.sky}; }
  .doc-title .fecha { font-size: 10px; color: ${M.colores.silver}; margin-top: 2px; }
  h3 { font-size: 10px; font-weight: 700; letter-spacing: 1px; color: ${M.colores.silver}; text-transform: uppercase; margin: 16px 0 6px; border-top: 1px solid #e8edf2; padding-top: 10px; }
  .info-table { width: 100%; border-collapse: collapse; }
  .info-table td { padding: 4px 8px; vertical-align: top; }
  .info-table td.lbl { font-weight: 600; color: ${M.colores.steel}; width: 160px; font-size: 11px; }
  .cob-table { width: 100%; border-collapse: collapse; }
  .cob-table th { background: ${M.colores.navy}; color: white; font-size: 10px; font-weight: 600; text-align: left; padding: 6px 8px; }
  .cob-table td { border-bottom: 1px solid #e8edf2; padding: 5px 8px; font-size: 11px; }
  .cob-table td.otros { font-size: 10px; color: ${M.colores.silver}; padding: 2px 8px 6px; font-style: italic; }
  .notas-box { background: #f4f7fb; border-left: 3px solid ${M.colores.sky}; padding: 8px 12px; margin-top: 16px; font-size: 11px; color: #1a2535; }
  .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e8edf2; font-size: 10px; color: ${M.colores.silver}; text-align: center; }
  @media print { body { padding: 0; } }
</style>
</head><body>
<div class="header">
  <div>
    <div class="brand" style="text-transform:uppercase">${M.nombre}</div>
    <div class="tagline">${M.tagline}</div>
  </div>
  <div class="doc-title">
    <div class="tipo">${tipoLabel}</div>
    <div class="num">${numSol(sol.id)}</div>
    <div class="fecha">Generado: ${today}</div>
  </div>
</div>

<h3>DATOS GENERALES</h3>
<table class="info-table"><tbody>
  ${row('Cliente', sol.clienteNombre || sol.cliente_nombre)}
  ${row('Aseguradora', sol.aseguradoraNombre || sol.aseguradora_nombre)}
  ${row('Ramo', sol.ramoNombre || sol.ramo_nombre)}
  ${sol.subramoNombre ? row('Subramo', sol.subramoNombre) : ''}
  ${row('Prima', primaVal ? '$'+Number(primaVal).toLocaleString('es-PA',{minimumFractionDigits:2}) : null)}
  ${row('Impuesto', sol.impuesto != null ? sol.impuesto + '%' : null)}
  ${row('Comisión', comisionStr || null)}
  ${row('Forma de Pago', sol.forma_pago)}
  ${row('Frecuencia', sol.frecuencia_pago)}
  ${row('Cuotas', sol.num_cuotas ? String(sol.num_cuotas) : null)}
  ${row('Fecha Inicio', fmtFecha(sol.fecha_inicio))}
  ${row('Fecha Vencimiento', fmtFecha(sol.fecha_vencimiento))}
  ${row('Ejecutivo', sol.ejecutivoNombre || sol.ejecutivo_nombre)}
</tbody></table>

${partesHtml}
${autoHtml}
${cobHtml}
${agentesHtml}

${sol.descripcion_riesgo ? `<div class="notas-box"><strong>Descripción del Riesgo:</strong><br/>${sol.descripcion_riesgo}</div>` : ''}
${sol.notas ? `<div class="notas-box"><strong>Notas:</strong><br/>${sol.notas}</div>` : ''}

<div class="footer">${M.nombre} · Corretaje de Seguros · Panamá &nbsp;|&nbsp; ${numSol(sol.id)} &nbsp;|&nbsp; ${today}</div>
<script>window.onload = () => { window.print(); }</script>
</body></html>`;
}

// describirCambioHTML vive en src/utils/endosoDetalle.js (compartida con Polizas).

// Documento formal de la SOLICITUD DE ENDOSO, para enviar a la aseguradora.
// Deja por escrito exactamente qué se pidió (para comparar contra el endoso recibido).
function generateEndosoSolicitudHTML({ sol, cambios, poliza }) {
  const M = marcaImpresion();
  const today = fmtFecha(new Date());
  const row = (label, value) => value != null && value !== ''
    ? `<tr><td class="lbl">${label}</td><td>${value}</td></tr>` : '';
  // `cambios` es la LISTA de cambios [{nombre, target, cambios}].
  const lista = Array.isArray(cambios) ? cambios : [];
  const cambiosHtml = lista.map((ch, i) => {
    const detalle = describirCambioHTML(ch.target, ch.cambios);
    return `<div class="cambio-box"><table class="info-table"><tbody>`
      + `<tr><td class="lbl">Cambio ${i + 1}</td><td><strong>${ch.nombre || ''}</strong></td></tr>`
      + detalle + `</tbody></table></div>`;
  }).join('');
  const primaVal = sol.prima != null && sol.prima !== '' ? sol.prima : null;
  return `<!DOCTYPE html><html lang="es"><head>
<meta charset="utf-8"/>
<title>Solicitud de Endoso — ${numSol(sol.id)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Outfit', Arial, sans-serif; font-size: 12px; color: #1a2535; padding: 24px 32px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid ${M.colores.navy}; padding-bottom: 12px; margin-bottom: 16px; }
  .brand { font-size: 20px; font-weight: 700; color: ${M.colores.navy}; letter-spacing: 0.5px; }
  .tagline { font-size: 10px; color: ${M.colores.silver}; margin-top: 2px; }
  .doc-title { text-align: right; }
  .doc-title .tipo { font-size: 14px; font-weight: 700; color: ${M.colores.steel}; }
  .doc-title .num { font-size: 22px; font-weight: 700; color: ${M.colores.sky}; }
  .doc-title .fecha { font-size: 10px; color: ${M.colores.silver}; margin-top: 2px; }
  h3 { font-size: 10px; font-weight: 700; letter-spacing: 1px; color: ${M.colores.silver}; text-transform: uppercase; margin: 16px 0 6px; border-top: 1px solid #e8edf2; padding-top: 10px; }
  .info-table { width: 100%; border-collapse: collapse; }
  .info-table td { padding: 4px 8px; vertical-align: top; }
  .info-table td.lbl { font-weight: 600; color: ${M.colores.steel}; width: 200px; font-size: 11px; }
  .cambio-box { background: #f4f7fb; border: 1px solid #d6e2ef; border-left: 3px solid ${M.colores.sky}; padding: 10px 14px; margin-top: 6px; border-radius: 4px; }
  .notas-box { background: #fff8ec; border-left: 3px solid #f59e0b; padding: 8px 12px; margin-top: 16px; font-size: 11px; }
  .firma { margin-top: 40px; display: flex; justify-content: space-between; gap: 40px; }
  .firma .linea { flex: 1; border-top: 1px solid #1a2535; padding-top: 6px; font-size: 10px; color: ${M.colores.silver}; text-align: center; }
  .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e8edf2; font-size: 10px; color: ${M.colores.silver}; text-align: center; }
  @media print { body { padding: 0; } }
</style>
</head><body>
<div class="header">
  <div>
    <div class="brand" style="text-transform:uppercase">${M.nombre}</div>
    <div class="tagline">${M.tagline}</div>
  </div>
  <div class="doc-title">
    <div class="tipo">Solicitud de Endoso</div>
    <div class="num">${numSol(sol.id)}</div>
    <div class="fecha">Generado: ${today}</div>
  </div>
</div>

<h3>Datos de la póliza</h3>
<table class="info-table"><tbody>
  ${row('Póliza N°', poliza?.numero)}
  ${row('Vigencia', poliza && (poliza.fecha_inicio || poliza.fecha_vencimiento) ? `${fmtFecha(poliza.fecha_inicio) || '—'} → ${fmtFecha(poliza.fecha_vencimiento) || '—'}` : null)}
  ${row('Cliente', sol.clienteNombre)}
  ${row('Aseguradora', sol.aseguradoraNombre)}
  ${row('Ramo', sol.ramoNombre)}
</tbody></table>

<h3>Cambios solicitados</h3>
${cambiosHtml || '<div style="font-size:11px;color:${M.colores.silver}">Sin cambios estructurados capturados.</div>'}
<table class="info-table" style="margin-top:8px"><tbody>
  ${row('Movimiento de prima (sin imp.)', primaVal != null ? '$' + Number(primaVal).toLocaleString('es-PA', { minimumFractionDigits: 2 }) : null)}
</tbody></table>

${sol.notas ? `<div class="notas-box"><strong>Descripción del cambio solicitado:</strong><br/>${sol.notas}</div>` : ''}

<div class="firma">
  <div class="linea">Solicitado por — Riesgo Unido</div>
  <div class="linea">Recibido / Confirmado por — Aseguradora</div>
</div>

<div class="footer">${M.nombre} · Corretaje de Seguros · Panamá &nbsp;|&nbsp; ${numSol(sol.id)} &nbsp;|&nbsp; ${today}<br/>Este documento detalla exactamente el cambio solicitado. El endoso emitido debe corresponder a lo aquí descrito.</div>
<script>window.onload = () => { window.print(); }</script>
</body></html>`;
}

const TIPOS_LABEL = { cotizacion:'Cotización', nueva:'Póliza Nueva', endoso:'Endoso', renovacion:'Renovación' };
const TIPO_BADGE  = { cotizacion:'badge-info', nueva:'badge-success', endoso:'badge-warning', renovacion:'badge-neutral' };

const ESTADOS = {
  cotizacion: {
    borrador:            { label:'Borrador',               badge:'badge-neutral' },
    enviada_aseg:        { label:'Enviada a Aseguradoras', badge:'badge-info'    },
    propuesta_recibida:  { label:'Propuesta Recibida',     badge:'badge-warning' },
    comparativo_enviado: { label:'Comparativo Enviado',    badge:'badge-info'    },
    convertida:          { label:'Convertida a Póliza',    badge:'badge-success' },
    cerrada:             { label:'Cerrada',                badge:'badge-neutral' },
    cancelada:           { label:'Cancelada',              badge:'badge-danger'  },
  },
  nueva: {
    borrador:        { label:'Borrador',              badge:'badge-neutral' },
    enviada_aseg:    { label:'Enviada a Aseguradora', badge:'badge-info'    },
    poliza_recibida: { label:'Póliza Recibida',       badge:'badge-warning' },
    convertida:      { label:'Convertida',            badge:'badge-success' },
    enviada:         { label:'Enviada',               badge:'badge-info'    },
    aprobada:        { label:'Aprobada',              badge:'badge-success' },
    rechazada:       { label:'Rechazada',             badge:'badge-danger'  },
    cancelada:       { label:'Cancelada',             badge:'badge-danger'  },
  },
  endoso: {
    borrador:        { label:'Borrador',              badge:'badge-neutral' },
    enviada_aseg:    { label:'Enviada a Aseguradora', badge:'badge-info'    },
    endoso_recibido: { label:'Endoso Recibido',       badge:'badge-warning' },
    enviado_cliente: { label:'Enviado al Cliente',    badge:'badge-success' },
    convertida:      { label:'Registrado',            badge:'badge-success' },
    enviada:         { label:'Enviada',               badge:'badge-info'    },
    aprobada:        { label:'Aprobada',              badge:'badge-success' },
    cancelada:       { label:'Cancelada',             badge:'badge-danger'  },
  },
  renovacion: {
    borrador:   { label:'Borrador',   badge:'badge-neutral' },
    enviada:    { label:'Enviada',    badge:'badge-info'    },
    aprobada:   { label:'Aprobada',   badge:'badge-success' },
    rechazada:  { label:'Rechazada',  badge:'badge-danger'  },
    convertida: { label:'Convertida', badge:'badge-warning' },
    cancelada:  { label:'Cancelada',  badge:'badge-danger'  },
  },
};

const FLUJO = {
  cotizacion: ['borrador','enviada_aseg','propuesta_recibida','comparativo_enviado'],
  nueva:      ['borrador','enviada_aseg','poliza_recibida'],
  endoso:     ['borrador','enviada_aseg','endoso_recibido','enviado_cliente'],
  renovacion: ['borrador','enviada','aprobada'],
};

const getEstado = (tipo, estado) => (ESTADOS[tipo]||ESTADOS.nueva)[estado] || { label:estado, badge:'badge-neutral' };
const getProxEstados = (tipo, estadoActual) => {
  const flujo = FLUJO[tipo] || FLUJO.nueva;
  const idx = flujo.indexOf(estadoActual);
  return idx === -1 ? [] : flujo.slice(idx + 1);
};

// backward compat aliases
const ESTADO_BADGE = {};
const ESTADO_LABEL = {};

// Normaliza cambios_json a una LISTA [{tipo_endoso_id, cambios}].
// Tolera el formato viejo (un solo objeto de cambios + tipo_endoso_id en la fila).
const cambiosDesde = (row) => {
  const cj = row?.cambios_json;
  if (Array.isArray(cj)) return cj;
  if (cj && typeof cj === 'object') return [{ tipo_endoso_id: row?.tipo_endoso_id || '', cambios: cj }];
  return [];
};

const EMPTY = {
  cliente_id:'', aseguradora_id:'', ramo_id:'', subramo_id:'', ejecutivo_id:'',
  tipo:'nueva', poliza_endoso_id:'', numero_poliza:'', prima:'', impuesto:'5', comision_ramo:'',
  forma_pago:'', frecuencia_pago:'mensual', num_cuotas:'1',
  fecha_inicio:'', fecha_vencimiento:'', fecha_emision:'',
  notas:'', es_fee:false, monto_fee:'',
  tipo_endoso_id:'', cambiosList:[],
  referido_id:'', referido_comision_pct:'',
};

const EMPTY_AUTO = {
  anio:'', marca:'', modelo:'', color:'', tipo_vehiculo:'',
  pasajeros:'', no_motor:'', chasis:'', chasis_igual_motor:true,
  placa:'', placa_por_reportar:false,
  dueno_igual:true, dueno_nombre:'', dueno_cedula:'',
};

const EMPTY_POL = { numero:'', fecha_inicio:'', fecha_vencimiento:'', fecha_emision:'',
  prima:'', impuesto:5, comision_ramo:'', forma_pago:'', frecuencia_pago:'mensual', num_cuotas:1,
  tipo_endoso:'normal', tipo_endoso_id:'', tipo_endoso_nombre:'', cambiosList:[], prima_devuelta:'',
  metodo_cancelacion:'prorrata', factor_penalizacion:'', nueva_fecha_vencimiento:'' };

// ── Componente principal ─────────────────────────────────────────
export default function Solicitudes() {
  const { canEdit, user, agenteScope, esAgenteRestringido } = useApp();
  const abrev = getAbrev();
  const agScopeAgenteId = esAgenteRestringido ? (user?.agente_id ?? -1) : null;
  const navigate = useNavigate();
  const autoOpenRef = useRef(false);
  const nuevoEndosoRef = useRef(false);
  // Si el modal se abrió desde una póliza (crear/facturar endoso), guarda su id para
  // regresar solo a esa póliza al cerrar (X o guardar), sin caer en la lista de Solicitudes.
  const backToPolizaRef = useRef(null);
  // Si el "Registrar Endoso" se abrió desde el ciclo del CRM, al cerrar volver al CRM.
  const backToCrmRef = useRef(false);

  // List state
  const [rows, setRows]               = useState([]);
  const [filterPoliza, setFilterPoliza] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterAseguradora, setFilterAseguradora] = useState('');
  const [filterRamo, setFilterRamo]   = useState('');
  const [filterFechaTipo, setFilterFechaTipo] = useState('');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');
  const [sortCol, setSortCol]         = useState('id');
  const [sortDir, setSortDir]         = useState('desc');
  const [filterTipo, setFilterTipo]   = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [showCanceladas, setShowCanceladas] = useState(false);
  const [modal, setModal]             = useState(null);
  const [selected, setSelected]       = useState(null);
  const [toast, setToast]             = useState(null); // { type:'success'|'error', msg:string }
  const [cancelModal, setCancelModal] = useState(null); // row a cancelar
  const [cancelReason, setCancelReason] = useState('');
  const [reactivarModal, setReactivarModal] = useState(null); // row a reactivar
  const [reactivarReason, setReactivarReason] = useState('');
  const [reactivarConfirm, setReactivarConfirm] = useState(false);
  const [saving, setSaving]           = useState(false);

  // Cotización por aseguradora
  const [cotizAsegList, setCotizAsegList]   = useState([]);
  const [cotizAsegForm, setCotizAsegForm]   = useState({ aseguradora_id:'', prima_cotizada:'', notas:'' });
  const [savingCotizAseg, setSavingCotizAseg] = useState(false);
  const [uploadingCotizAseg, setUploadingCotizAseg] = useState(false);
  const cotizAsegFileRef = useRef(null);
  const [comparativoFile, setComparativoFile] = useState(null);
  const [uploadingComparativo, setUploadingComparativo] = useState(false);
  const comparativoFileRef = useRef(null);

  // Form state
  const [form, setForm]               = useState(EMPTY);
  const [polizaAgentes, setPolizaAgentes] = useState([]);
  const [polizaCoberturas, setPolizaCoberturas] = useState([]);
  // Catálogo del ramo/subramo para el BUSCADOR de coberturas (se empieza vacío y se agregan).
  const [coberturasCatalogo, setCoberturasCatalogo] = useState([]);
  const [cobSelector, setCobSelector] = useState('');
  const [agenteSelector, setAgenteSelector] = useState('');
  const [comisionRamoBase, setComisionRamoBase] = useState(null);
  const [ejecutivoBase, setEjecutivoBase]     = useState(null);
  const [nuevaCobertura, setNuevaCobertura]   = useState('');
  const [showAddCob, setShowAddCob]           = useState(false);
  const [isAuto, setIsAuto]                   = useState(false);
  const [autoLimiteOpciones, setAutoLimiteOpciones] = useState([]);
  const [autoForm, setAutoForm]               = useState(EMPTY_AUTO);
  // Emisión de flota: varias unidades en la solicitud (nueva/cotización/renovación).
  const [autoList, setAutoList]               = useState([{ ...EMPTY_AUTO }]);

  // File state
  const [archivos, setArchivos]         = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef                    = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const pendingFileRef                  = useRef(null);

  // View detail state
  const [viewCoberturas, setViewCoberturas] = useState([]);
  const [viewAutoData, setViewAutoData]     = useState(null);
  const [viewAgentes, setViewAgentes]       = useState([]);
  const [viewPolizaEndoso, setViewPolizaEndoso] = useState(null); // póliza endosada (número + vigencia) en la vista de un endoso

  // Validaciones en tiempo real
  const [autoWarning, setAutoWarning]       = useState(null); // { campo, poliza }
  const [polNumWarning, setPolNumWarning]   = useState(null); // string mensaje
  const polNumTimer                         = useRef(null);

  // Convert state
  const [polForm, setPolForm]           = useState(EMPTY_POL);
  const [renovInfo, setRenovInfo]       = useState(null); // ref. de la póliza anterior (renovación)
  const [converting, setConverting]     = useState(false);
  const [convertFileFactura, setConvertFileFactura] = useState(null);
  const [convertFileCond, setConvertFileCond]       = useState(null);
  const [endosoDocExistente, setEndosoDocExistente] = useState(null); // { url, nombre } del endoso recibido ya adjunto en el ciclo
  // "Enviado al Cliente" (endoso) exige evidencia del correo: pegar el texto o adjuntar PDF.
  const [envioCliente, setEnvioCliente] = useState(null); // null | { modo:'file'|'texto', texto, file }
  const [enviandoCliente, setEnviandoCliente] = useState(false);
  // Leer la póliza adjunta con IA al convertir (autocompleta los datos; el usuario revisa).
  const [leyendoPolizaIA, setLeyendoPolizaIA] = useState(false);
  // Validar con IA el ENDOSO recibido vs lo solicitado (opcional, en el registro).
  const [validandoEndosoIA, setValidandoEndosoIA] = useState(false);
  const [iaEndosoResultado, setIaEndosoResultado] = useState(null);
  // Facturación por partes: índices de los cambios PENDIENTES marcados como recibidos en este endoso.
  const [recibidosSel, setRecibidosSel] = useState([]);
  const [uploadingConvert, setUploadingConvert] = useState(false);
  const convertFileFacturaRef           = useRef(null);
  const convertFileCondRef              = useRef(null);

  // Catalogs
  const [clientes, setClientes]         = useState([]);
  const [agentes, setAgentes]           = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [ramos, setRamos]               = useState([]);
  const [subramos, setSubramos]         = useState([]);
  const [ejecutivos, setEjecutivos]     = useState([]);
  const [referidos, setReferidos]       = useState([]);
  const [polizasActivas, setPolizasActivas] = useState([]);
  // Póliza a endosar cuando el endoso se abre desde una póliza: se inyecta a las opciones
  // para que el selector la muestre SIEMPRE, aunque su estado no sea activa/en_renovacion.
  const [polizaEndosoActual, setPolizaEndosoActual] = useState(null);
  const [catalogoDocs, setCatalogoDocs] = useState([]);
  const [endosoTipos, setEndosoTipos] = useState([]); // catálogo de tipos de endoso (Admin)
  const [endosoDeclarativa, setEndosoDeclarativa] = useState(false);

  // Cliente inline
  const [clienteModal, setClienteModal] = useState(false);
  const [clienteForm, setClienteForm]   = useState({ tipo:'natural', primer_nombre:'', segundo_nombre:'', primer_apellido:'', segundo_apellido:'', nombre:'', telefono:'', whatsapp:'', email:'' });
  const [savingCliente, setSavingCliente] = useState(false);

  const loadCatalogos = async () => {
    const [c,a,s,r,e] = await Promise.all([
      supabase.from('clientes').select('id,nombre,numero_cliente,ejecutivo_id').order('nombre'),
      supabase.from('agentes').select('id,nombre,comision_nueva_agente,comision_renovacion_agente').eq('activo',true).order('nombre'),
      supabase.from('aseguradoras').select('id,nombre').eq('activa',true).order('nombre'),
      supabase.from('ramos').select('id,nombre').eq('activo',true).order('nombre'),
      supabase.from('ejecutivos').select('id,nombre').eq('activo',true).order('nombre'),
    ]);
    setClientes((c.data||[]).map(x=>({...x,nombre:x.numero_cliente?`${x.nombre} (${x.numero_cliente})`:x.nombre})));
    setAgentes(a.data||[]);
    setAseguradoras(s.data||[]);
    const { data: pols } = await supabase.from('polizas').select('id,numero,cliente_id,ramo_id,aseguradora_id,fecha_inicio,fecha_vencimiento,es_declarativa,clientes(nombre)').in('estado',['activa','en_renovacion']).order('numero');
    setPolizasActivas((pols||[]).map(p=>({id:p.id,numero:p.numero,cliente:p.clientes?.nombre,cliente_id:p.cliente_id,ramo_id:p.ramo_id,aseguradora_id:p.aseguradora_id,fecha_inicio:p.fecha_inicio,fecha_vencimiento:p.fecha_vencimiento,es_declarativa:p.es_declarativa})));
    setRamos(r.data||[]);
    setEjecutivos(e.data||[]);
    const { data: catDocs } = await supabase.from('catalogo_documentos').select('id,nombre,orden').eq('activo',true).order('orden').order('nombre');
    setCatalogoDocs(catDocs||[]);
    // Catálogo de tipos de endoso (administrable en Admin). Tolerante si la tabla aún no existe.
    try {
      const { data: catTipos } = await supabase.from('catalogo_endoso_tipos')
        .select('id,nombre,comportamiento,target,ramo_id,orden').eq('activo',true).order('orden').order('nombre');
      setEndosoTipos(catTipos||[]);
    } catch (e) { setEndosoTipos([]); }
    // Niveles de límite de Auto (tolerante: si la tabla no existe, queda vacío)
    setAutoLimiteOpciones(await fetchAutoLimiteOpciones());
    // Referidos activos (tolerante: si la tabla no existe, queda vacío)
    setReferidos(await fetchReferidos());
  };

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (esAgenteRestringido && !agenteScope) return;
    let q = supabase.from('solicitudes')
      .select('*, clientes(nombre), ramos(nombre), aseguradoras(nombre)')
      .order('creado_en',{ascending:false});
    if (showCanceladas) {
      q = q.eq('estado','cancelada');
    } else {
      q = q.neq('estado','cancelada');
      if (filterEstado) q = q.eq('estado',filterEstado);
    }
    if (filterTipo) q = q.eq('tipo',filterTipo);
    if (filterCliente) q = q.eq('cliente_id', filterCliente);
    // Agente restringido: solo sus solicitudes (por agente o por póliza vinculada)
    if (esAgenteRestringido) {
      const polIds = agenteScope?.polizaIds || [];
      const orParts = [`agente_id.eq.${agScopeAgenteId}`];
      if (polIds.length) orParts.push(`poliza_id.in.(${polIds.join(',')})`, `poliza_endoso_id.in.(${polIds.join(',')})`);
      q = q.or(orParts.join(','));
    }
    const {data, error} = await q;
    if (error) { console.error('Error cargando solicitudes:', error); return; }
    // REGLA: toda solicitud va asociada a una póliza. La nueva/renovación la guarda
    // en `poliza_id`; el ENDOSO la guarda en `poliza_endoso_id`. La póliza vinculada
    // es SIEMPRE `poliza_id || poliza_endoso_id` (nunca dejar la columna vacía).
    const linkId = s => s.poliza_id || s.poliza_endoso_id || null;
    let arr = (data||[]).map(s=>({
      ...s,
      clienteNombre:     s.clientes?.nombre,
      ramoNombre:        s.ramos?.nombre,
      aseguradoraNombre: s.aseguradoras?.nombre,
      ejecutivoNombre:   null,
      polizaNumero:      null,
      polizaIdVinculada: linkId(s),
    }));
    // Enriquecer con número de póliza vinculada (incluye los endosos por poliza_endoso_id)
    const idsConPoliza = [...new Set(arr.map(linkId).filter(Boolean))];
    if (idsConPoliza.length > 0) {
      const { data: pols } = await supabase.from('polizas').select('id, numero').in('id', idsConPoliza);
      const polMap = Object.fromEntries((pols||[]).map(p=>[p.id, p.numero]));
      arr = arr.map(s => { const lid = linkId(s); return lid ? { ...s, polizaNumero: polMap[lid] || null } : s; });
    }

    // Filtro por póliza vinculada (PolizaSelect): la lista solo se filtra al seleccionar.
    if (filterPoliza) arr = arr.filter(s => String(s.polizaIdVinculada) === String(filterPoliza));
    setRows(arr);
  }, [filterEstado, filterTipo, filterCliente, filterPoliza, showCanceladas, esAgenteRestringido, agScopeAgenteId, agenteScope]);

  useEffect(()=>{load();loadCatalogos();},[load]);

  // Auto-abrir solicitud si viene navegando desde Pólizas
  useEffect(() => {
    const solicitudId = sessionStorage.getItem('openSolicitudId');
    if (!solicitudId || autoOpenRef.current) return;
    autoOpenRef.current = true;
    sessionStorage.removeItem('openSolicitudId');
    const veniaDePoliza = sessionStorage.getItem('backFrom') === 'polizas';
    supabase.from('solicitudes')
      .select('*, clientes(nombre), ramos(nombre), aseguradoras(nombre)')
      .eq('id', solicitudId).single()
      .then(({ data }) => {
        if (!data) return;
        if (veniaDePoliza) backToPolizaRef.current = data.poliza_id || data.poliza_endoso_id || null;
        openView({ ...data, clienteNombre: data.clientes?.nombre, ramoNombre: data.ramos?.nombre, aseguradoraNombre: data.aseguradoras?.nombre });
      });
  }, []); // eslint-disable-line

  // Abrir directo el "Registrar Endoso" (facturar) si viene del ciclo del CRM
  useEffect(() => {
    const convId = sessionStorage.getItem('openConvertSolicitudId');
    if (!convId || autoOpenRef.current) return;
    autoOpenRef.current = true;
    sessionStorage.removeItem('openConvertSolicitudId');
    if (sessionStorage.getItem('backFrom') === 'crm') { backToPolizaRef.current = null; backToCrmRef.current = true; }
    supabase.from('solicitudes')
      .select('*, clientes(nombre), ramos(nombre), aseguradoras(nombre)')
      .eq('id', convId).single()
      .then(({ data }) => {
        if (!data) return;
        openConvert({ ...data, clienteNombre: data.clientes?.nombre, ramoNombre: data.ramos?.nombre, aseguradoraNombre: data.aseguradoras?.nombre });
      });
  }, []); // eslint-disable-line

  // Crear solicitud de endoso si viene navegando desde Pólizas (abre el form de endoso prellenado)
  useEffect(() => {
    const polId = sessionStorage.getItem('nuevoEndosoPolizaId');
    if (!polId || nuevoEndosoRef.current) return;
    nuevoEndosoRef.current = true;
    sessionStorage.removeItem('nuevoEndosoPolizaId');
    backToPolizaRef.current = polId; // al cerrar/guardar, regresar a esta póliza
    (async () => {
      openCreate('endoso');
      const { data: pol } = await supabase.from('polizas')
        .select('id, numero, cliente_id, ramo_id, aseguradora_id, fecha_inicio, fecha_vencimiento, es_declarativa, clientes(nombre)')
        .eq('id', polId).maybeSingle();
      // Fijar la póliza a endosar para que el selector la muestre siempre (sin buscarla a mano)
      if (pol) setPolizaEndosoActual({
        id: pol.id, numero: pol.numero, cliente: pol.clientes?.nombre,
        cliente_id: pol.cliente_id, ramo_id: pol.ramo_id, aseguradora_id: pol.aseguradora_id,
        fecha_inicio: pol.fecha_inicio, fecha_vencimiento: pol.fecha_vencimiento, es_declarativa: pol.es_declarativa,
      });
      setForm(prev => ({
        ...prev,
        tipo: 'endoso',
        poliza_endoso_id: String(polId),
        cliente_id:     pol?.cliente_id     ? String(pol.cliente_id)     : prev.cliente_id,
        ramo_id:        pol?.ramo_id        ? String(pol.ramo_id)        : prev.ramo_id,
        aseguradora_id: pol?.aseguradora_id ? String(pol.aseguradora_id) : prev.aseguradora_id,
      }));
    })();
  }, []); // eslint-disable-line

  const f   = (k,v) => setForm(p=>({...p,[k]:v}));
  const pf  = (k,v) => setPolForm(p=>({...p,[k]:v}));

  // ── Cliente: auto-fill ejecutivo ──────────────────────────────
  const handleClienteChange = (clienteId) => {
    f('cliente_id', clienteId);
    const cli = clientes.find(c=>String(c.id)===String(clienteId));
    if (cli?.ejecutivo_id) { setEjecutivoBase(cli.ejecutivo_id); f('ejecutivo_id', cli.ejecutivo_id); }
    else setEjecutivoBase(null);
  };
  const ejecutivoCambiado = ejecutivoBase!=null && form.ejecutivo_id!=='' && String(form.ejecutivo_id)!==String(ejecutivoBase);

  // ── Ramo ─────────────────────────────────────────────────────
  const handleRamoChange = async (ramoId) => {
    f('ramo_id', ramoId);
    f('subramo_id', '');
    setSubramos([]);
    if (!ramoId) { setPolizaCoberturas([]); setCoberturasCatalogo([]); setCobSelector(''); setComisionRamoBase(null); setIsAuto(false); return; }
    const [{data:ramo},{data:subs},{data:cobs}] = await Promise.all([
      supabase.from('ramos').select('*').eq('id',ramoId).single(),
      supabase.from('subramos').select('*').eq('ramo_id',ramoId).eq('activo',true).order('nombre'),
      supabase.from('coberturas').select('*').eq('ramo_id',ramoId).is('subramo_id',null).eq('activa',true).order('orden').order('nombre'),
    ]);
    setSubramos(subs || []);
    if (ramo) {
      const autoRamo = ramo.nombre?.toLowerCase().includes('auto');
      setIsAuto(autoRamo);
      f('impuesto', autoRamo ? '6' : '5');
      setComisionRamoBase(ramo.comision??null);
      let comisionFinal = ramo.comision??'';
      if (form.aseguradora_id) {
        const {data:link} = await supabase.from('aseguradora_ramos').select('comision').eq('aseguradora_id',form.aseguradora_id).eq('ramo_id',ramoId).maybeSingle();
        if (link?.comision!=null) comisionFinal = link.comision;
      }
      f('comision_ramo', comisionFinal!=='' ? String(comisionFinal) : '');
    }
    // Buscador: guarda el catálogo del ramo y empieza VACÍO (la persona busca y agrega).
    setCoberturasCatalogo((!subs || subs.length === 0) ? (cobs || []) : []);
    setPolizaCoberturas([]);
    setCobSelector('');
  };

  // ── Subramo ───────────────────────────────────────────────────
  const handleSubramoChange = async (subramoId) => {
    f('subramo_id', subramoId);
    if (!subramoId) { setPolizaCoberturas([]); setCoberturasCatalogo([]); return; }
    const {data:cobs} = await supabase.from('coberturas')
      .select('*').eq('subramo_id', subramoId).eq('activa', true)
      .order('es_basica', { ascending:false }).order('orden').order('nombre');
    // Buscador: catálogo cargado, lista vacía (la persona busca y agrega lo que aplique).
    setCoberturasCatalogo(cobs || []);
    setPolizaCoberturas([]);
    setCobSelector('');
  };

  const handleAsegChange = async (asegId) => {
    f('aseguradora_id', asegId);
    if (!asegId||!form.ramo_id) return;
    const {data:link} = await supabase.from('aseguradora_ramos').select('comision').eq('aseguradora_id',asegId).eq('ramo_id',form.ramo_id).maybeSingle();
    if (link?.comision!=null) f('comision_ramo', String(link.comision));
  };

  // ── Endoso: auto-rellenar de la póliza seleccionada ──────────
  const handlePolizaEndosoChange = (polId) => {
    f('poliza_endoso_id', polId);
    if (!polId) return;
    const pol = polizasActivas.find(p=>String(p.id)===String(polId))
      || (String(polizaEndosoActual?.id)===String(polId) ? polizaEndosoActual : null);
    if (!pol) return;
    setForm(prev=>({
      ...prev,
      poliza_endoso_id: polId,
      cliente_id:     pol.cliente_id     ? String(pol.cliente_id)     : prev.cliente_id,
      ramo_id:        pol.ramo_id        ? String(pol.ramo_id)        : prev.ramo_id,
      aseguradora_id: pol.aseguradora_id ? String(pol.aseguradora_id) : prev.aseguradora_id,
    }));
    if (pol.cliente_id) {
      const cli = clientes.find(c=>String(c.id)===String(pol.cliente_id));
      if (cli?.ejecutivo_id) {
        setEjecutivoBase(cli.ejecutivo_id);
        setForm(p=>({...p, ejecutivo_id: cli.ejecutivo_id}));
      }
    }
  };

  const comisionRamoCambiada = comisionRamoBase!=null && form.comision_ramo!=='' && parseFloat(form.comision_ramo)!==parseFloat(comisionRamoBase);

  // Opciones del selector "Póliza a endosar": las activas + la póliza fijada (si vino desde
  // una póliza y no está entre las activas), para que siempre se muestre seleccionada.
  const polizasEndosoOpts = polizaEndosoActual && !polizasActivas.some(p=>String(p.id)===String(polizaEndosoActual.id))
    ? [polizaEndosoActual, ...polizasActivas]
    : polizasActivas;

  // ── Validar vehículo duplicado ────────────────────────────────
  const checkAutoExistente = async (campo, valor) => {
    if (!valor || valor === 'Por reportar') { setAutoWarning(null); return; }
    const { data } = await supabase
      .from('poliza_autos')
      .select('poliza_id, polizas(numero, estado, clientes(nombre))')
      .eq(campo, valor)
      .limit(5);
    if (!data || data.length === 0) { setAutoWarning(null); return; }
    const vigente = (data).find(r => ['activa','en_renovacion'].includes(r.polizas?.estado));
    if (vigente) {
      setAutoWarning(`Esta unidad ya está asegurada bajo la póliza ${vigente.polizas.numero} — ${vigente.polizas.clientes?.nombre || ''}.`);
    } else {
      setAutoWarning(null);
    }
  };

  // ── Validar número de póliza duplicado ────────────────────────
  const checkPolizaNumero = (numero) => {
    pf('numero', numero);
    setPolNumWarning(null);
    if (polNumTimer.current) clearTimeout(polNumTimer.current);
    if (!numero.trim()) return;
    polNumTimer.current = setTimeout(async () => {
      // Buscar pólizas activas/en_renovacion con este número (puede haber varias vigencias)
      const { data } = await supabase.from('polizas')
        .select('id, numero, fecha_inicio, fecha_vencimiento, estado')
        .eq('numero', numero.trim())
        .in('estado', ['activa', 'en_renovacion'])
        .order('fecha_inicio', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) {
        // Existe una vigencia activa — podría ser renovación legítima
        setPolNumWarning(`⚠️ Ya existe una vigencia activa con este número (${data.fecha_inicio} → ${data.fecha_vencimiento}). Si es una renovación, asegúrate de que las fechas sean diferentes.`);
      } else {
        setPolNumWarning(null);
      }
    }, 500);
  };

  // ── Cliente inline ────────────────────────────────────────────
  const handleSaveClienteInline = async () => {
    const cf = clienteForm;
    if (cf.tipo === 'natural') {
      if (!cf.primer_nombre.trim()) return alert('El primer nombre es requerido.');
      if (!cf.primer_apellido.trim()) return alert('El primer apellido es requerido.');
    } else {
      if (!cf.nombre.trim()) return alert('La razón social es requerida.');
    }
    if (!cf.telefono.trim() && !cf.whatsapp.trim() && !cf.email.trim())
      return alert('Ingresa al menos un contacto: teléfono, WhatsApp o correo.');
    setSavingCliente(true);
    try {
      const nombreCompleto = cf.tipo === 'juridica' ? cf.nombre :
        [cf.primer_nombre, cf.segundo_nombre, cf.primer_apellido, cf.segundo_apellido].filter(Boolean).join(' ');
      const nuevo = await insertRow('clientes', {
        tipo: cf.tipo,
        primer_nombre: cf.tipo==='natural' ? cf.primer_nombre||null : null,
        segundo_nombre: cf.tipo==='natural' ? cf.segundo_nombre||null : null,
        primer_apellido: cf.tipo==='natural' ? cf.primer_apellido||null : null,
        segundo_apellido: cf.tipo==='natural' ? cf.segundo_apellido||null : null,
        nombre: nombreCompleto,
        telefono: cf.telefono||null,
        whatsapp: cf.whatsapp||null,
        email: cf.email||null,
      });
      // Recarga catálogo y selecciona el nuevo cliente
      const { data: cls } = await supabase.from('clientes').select('id,nombre,numero_cliente,ejecutivo_id').order('nombre');
      setClientes((cls||[]).map(x=>({...x,nombre:x.numero_cliente?`${x.nombre} (${x.numero_cliente})`:x.nombre})));
      handleClienteChange(String(nuevo.id));
      setClienteModal(false);
      setClienteForm({ tipo:'natural', primer_nombre:'', segundo_nombre:'', primer_apellido:'', segundo_apellido:'', nombre:'', telefono:'', whatsapp:'', email:'' });
    } catch(e) { alert('Error: ' + (e.message||'')); }
    finally { setSavingCliente(false); }
  };

  // ── Agentes ───────────────────────────────────────────────────
  const handleAgenteAdd = async () => {
    if (!agenteSelector) return;
    if (polizaAgentes.find(a=>String(a.agente_id)===String(agenteSelector))) { setAgenteSelector(''); return; }
    const {data:agente} = await supabase.from('agentes').select('*').eq('id',agenteSelector).single();
    if (agente) {
      const pct = form.tipo==='renovacion' ? agente.comision_renovacion_agente : agente.comision_nueva_agente;
      setPolizaAgentes(prev=>[...prev,{agente_id:agenteSelector,nombre:agente.nombre,porcentaje:pct!=null?String(pct):'',comision_negociada:pct,comision_manual:false}]);
    }
    setAgenteSelector('');
  };
  const sumAgentePct = polizaAgentes.reduce((s,a)=>s+(parseFloat(a.porcentaje)||0),0);
  const pctRU = Math.max(0,(parseFloat(form.comision_ramo)||0)-sumAgentePct);
  const pctExcedido = sumAgentePct>(parseFloat(form.comision_ramo)||0) && (parseFloat(form.comision_ramo)||0)>0;
  const agentesDisponibles = agentes.filter(a=>!polizaAgentes.find(p=>String(p.agente_id)===String(a.id)));

  // ── Coberturas ────────────────────────────────────────────────
  const updateCobertura = (idx,field,val) => setPolizaCoberturas(prev=>prev.map((c,i)=>i===idx?{...c,[field]:val}:c));
  const toggleCobExpanded = idx => setPolizaCoberturas(prev=>prev.map((c,i)=>i===idx?{...c,expanded:!c.expanded}:c));
  const handleAddCobertura = () => {
    // Cobertura del catálogo elegida en el buscador
    if (cobSelector && cobSelector !== '__custom__') {
      const tpl = coberturasCatalogo.find(c=>String(c.id)===String(cobSelector));
      if (!tpl) return;
      if (polizaCoberturas.find(c=>c.cobertura_id===tpl.id)) { setCobSelector(''); return; } // dedup
      setPolizaCoberturas(prev=>[...prev,{ ...newCobFromTemplate(tpl), incluida:true, expanded:true }]);
      setCobSelector('');
      return;
    }
    // Cobertura personalizada (texto libre)
    if (!nuevaCobertura.trim()) return;
    setPolizaCoberturas(prev=>[...prev,{
      cobertura_id:null,nombre:nuevaCobertura.trim(),tipo_limite:'limite',suma_asegurada:'',
      incluida:true,es_personalizada:true,tiene_deducible:false,deducible_tipo:'evento',
      ded_por_persona:'',ded_por_evento:'',ded_pct:'',ded_minimo:'',ded_personalizado:'',
      otros:'',expanded:true,
    }]);
    setNuevaCobertura(''); setCobSelector('');
  };

  // ── Archivos ──────────────────────────────────────────────────
  const loadArchivos = async (solicitudId) => {
    const {data} = await supabase.from('solicitud_archivos').select('*').eq('solicitud_id',solicitudId).order('creado_en');
    setArchivos(data||[]);
  };

  const handleUploadFile = async (e, solicitudId, tipo='solicitud') => {
    const file = e.target.files?.[0];
    if (!file||!solicitudId) return;
    setUploadingFile(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${solicitudId}/${Date.now()}.${ext}`;
      const {error} = await supabase.storage.from('solicitud-archivos').upload(path, file, {upsert:true});
      if (error) throw error;
      const {data:{publicUrl}} = supabase.storage.from('solicitud-archivos').getPublicUrl(path);
      await insertRow('solicitud_archivos',{solicitud_id:solicitudId,nombre:file.name,url:publicUrl,tipo,tamanio:file.size});
      await loadArchivos(solicitudId);
    } catch(e){ alert('Error al subir archivo: '+(e.message||'')); }
    finally { setUploadingFile(false); if(fileInputRef.current) fileInputRef.current.value=''; }
  };

  const handleDeleteFile = async (archivoId, url) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      const parts = url.split('/solicitud-archivos/');
      if (parts[1]) await supabase.storage.from('solicitud-archivos').remove([decodeURIComponent(parts[1])]);
      await deleteRow('solicitud_archivos', archivoId);
      setArchivos(prev=>prev.filter(a=>a.id!==archivoId));
    } catch(e){ alert('Error al eliminar: '+(e.message||'')); }
  };

  // ── Open modals ───────────────────────────────────────────────
  const openCreate = (tipo = 'nueva') => {
    setForm({ ...EMPTY, tipo });
    setPolizaEndosoActual(null);
    setPolizaAgentes([]); setPolizaCoberturas([]); setCoberturasCatalogo([]); setCobSelector('');
    setComisionRamoBase(null); setEjecutivoBase(null); setAgenteSelector('');
    setIsAuto(false); setAutoForm(EMPTY_AUTO); setAutoList([{ ...EMPTY_AUTO }]); setArchivos([]);
    setShowAddCob(false); setNuevaCobertura(''); setSelected(null);
    setSubramos([]); setPendingFiles([]);
    loadCatalogos(); setModal('create');
  };

  const openEdit = async (row) => {
    setSelected(row);
    setForm({
      cliente_id:       row.cliente_id       ||'',
      aseguradora_id:   row.aseguradora_id   ||'',
      ramo_id:          row.ramo_id          ||'',
      subramo_id:       row.subramo_id       ||'',
      ejecutivo_id:     row.ejecutivo_id     ||'',
      tipo:             row.tipo             ||'nueva',
      poliza_endoso_id: row.poliza_endoso_id ||'',
      numero_poliza:    row.numero_poliza    ||'',
      tipo_endoso_id:   row.tipo_endoso_id   ||'',
      cambiosList:      cambiosDesde(row),
      prima:            row.prima!=null      ? String(row.prima) : (row.prima_estimada||''),
      impuesto:         row.impuesto!=null   ? String(row.impuesto) : '5',
      comision_ramo:    row.comision_ramo!=null ? String(row.comision_ramo) : '',
      forma_pago:       row.forma_pago       ||'',
      frecuencia_pago:  row.frecuencia_pago  ||'mensual',
      num_cuotas:       row.num_cuotas       ? String(row.num_cuotas) : '1',
      fecha_inicio:     row.fecha_inicio     ||'',
      fecha_vencimiento:row.fecha_vencimiento||'',
      fecha_emision:    row.fecha_emision    ||'',
      notas:            row.notas            ||'',
      es_fee:           row.es_fee           ||false,
      monto_fee:        row.monto_fee!=null  ? String(row.monto_fee) : '',
      referido_id:      row.referido_id!=null ? String(row.referido_id) : '',
      referido_comision_pct: row.referido_comision_pct!=null ? String(row.referido_comision_pct) : '',
    });

    // Renovación: cargar la póliza anterior para mostrar la referencia (número, aseguradora).
    setRenovInfo(null);
    if (row.tipo === 'renovacion' && row.poliza_endoso_id) {
      supabase.from('polizas').select('numero, fecha_inicio, fecha_vencimiento, aseguradoras(nombre)')
        .eq('id', row.poliza_endoso_id).maybeSingle()
        .then(({ data: prev }) => { if (prev) setRenovInfo({ numero: prev.numero, aseg: prev.aseguradoras?.nombre, fi: prev.fecha_inicio, fv: prev.fecha_vencimiento }); });
    }

    const autoRamo = ramos.find(rm=>rm.id===row.ramo_id)?.nombre?.toLowerCase().includes('auto');
    setIsAuto(!!autoRamo);

    // Cargar subramos del ramo
    if (row.ramo_id) {
      const { data: subs } = await supabase.from('subramos').select('*').eq('ramo_id', row.ramo_id).eq('activo', true).order('nombre');
      setSubramos(subs || []);
    }

    const [{data:pAgentes},{data:pCobs},{data:ramo},{data:autoData}] = await Promise.all([
      supabase.from('solicitud_agentes').select('*, agentes(nombre,comision_nueva_agente,comision_renovacion_agente)').eq('solicitud_id',row.id),
      supabase.from('solicitud_coberturas').select('*').eq('solicitud_id',row.id),
      row.ramo_id ? supabase.from('ramos').select('comision').eq('id',row.ramo_id).single() : Promise.resolve({data:null}),
      supabase.from('solicitud_autos').select('*').eq('solicitud_id',row.id).order('id'),
    ]);
    const cobDescMap = await fetchCoberturaDescripciones((pCobs||[]).map(c=>c.cobertura_id));

    setComisionRamoBase(ramo?.data?.comision??null);
    if (row.cliente_id) {
      const cli = clientes.find(c=>String(c.id)===String(row.cliente_id));
      setEjecutivoBase(cli?.ejecutivo_id??null);
    }

    setPolizaAgentes((pAgentes||[]).map(a=>({
      agente_id:a.agente_id, nombre:a.agentes?.nombre||'',
      porcentaje:String(a.porcentaje),
      comision_negociada:row.tipo==='renovacion'?a.agentes?.comision_renovacion_agente:a.agentes?.comision_nueva_agente,
    })));

    // Coberturas a NIVEL de solicitud (sin vehículo). Las por unidad se cargan al vehículo abajo.
    const cobsPolizaSol = (pCobs||[]).filter(c=>!c.solicitud_auto_id);
    // Catálogo del ramo/subramo para el BUSCADOR (agregar más coberturas al editar).
    if (row.ramo_id) {
      const { data: catCobs } = row.subramo_id
        ? await supabase.from('coberturas').select('*').eq('subramo_id', row.subramo_id).eq('activa', true).order('orden').order('nombre')
        : await supabase.from('coberturas').select('*').eq('ramo_id', row.ramo_id).is('subramo_id', null).eq('activa', true).order('orden').order('nombre');
      setCoberturasCatalogo(catCobs || []);
    } else setCoberturasCatalogo([]);
    if (cobsPolizaSol.length>0) {
      setPolizaCoberturas(cobsPolizaSol.map(c=>({
        cobertura_id:c.cobertura_id, nombre:c.nombre||'',
        descripcion:cobDescMap[c.cobertura_id]||'',
        tipo_limite:c.tipo_limite||'limite',
        suma_asegurada:c.suma_asegurada!=null?String(c.suma_asegurada):'',
        incluida:true, es_personalizada:!c.cobertura_id,
        tiene_deducible:!!c.deducible, deducible_tipo:'personalizado',
        ded_por_persona:'',ded_por_evento:'',ded_pct:'',ded_minimo:'',
        ded_personalizado:c.deducible||'', otros:c.otros||'', expanded:false,
      })));
    } else { setPolizaCoberturas([]); }

    // Cargar TODAS las unidades de la solicitud (flota) con sus coberturas por unidad.
    const cobsPorAutoSol = {};
    (pCobs || []).filter(c => c.solicitud_auto_id).forEach(c => { (cobsPorAutoSol[c.solicitud_auto_id] = cobsPorAutoSol[c.solicitud_auto_id] || []).push(c); });
    const autosArr = (autoData || []).map(a => ({
      anio: a.anio ? String(a.anio) : '', marca: a.marca || '',
      modelo: a.modelo || '', color: a.color || '',
      tipo_vehiculo: a.tipo_vehiculo || '',
      pasajeros: a.pasajeros ? String(a.pasajeros) : '',
      no_motor: a.no_motor || '', chasis: a.chasis || '',
      chasis_igual_motor: a.chasis_igual_motor ?? true,
      placa: a.placa_por_reportar ? '' : (a.placa || ''), placa_por_reportar: a.placa_por_reportar ?? false,
      dueno_nombre: a.dueno_nombre || '', dueno_cedula: a.dueno_cedula || '',
      dueno_igual: !(a.dueno_nombre || a.dueno_cedula),
      coberturas: (cobsPorAutoSol[a.id] || []).map(c => ({
        nombre: c.nombre || '', tipo_limite: c.tipo_limite || 'limite',
        suma_asegurada: c.suma_asegurada != null ? String(c.suma_asegurada) : '', deducible: c.deducible || '',
      })),
    }));
    setAutoList(autosArr.length ? autosArr : [{ ...EMPTY_AUTO }]);
    setAutoForm(EMPTY_AUTO);

    await loadArchivos(row.id);
    setAgenteSelector(''); loadCatalogos(); setModal('edit');
  };

  const openViewPoliza = (polizaId) => {
    sessionStorage.setItem('openPolizaId', String(polizaId));
    sessionStorage.setItem('backFrom', 'solicitudes');
    navigate('/polizas');
  };

  const openView = async (row) => {
    setSelected(row);
    const [cobsResult, autoResult, agResult] = await Promise.all([
      supabase.from('solicitud_coberturas').select('*').eq('solicitud_id', row.id).order('id'),
      supabase.from('solicitud_autos').select('*').eq('solicitud_id', row.id).order('id'),
      supabase.from('solicitud_agentes').select('*, agentes(nombre)').eq('solicitud_id', row.id),
    ]);
    const cobRows = cobsResult.data || [];
    const viewDescMap = await fetchCoberturaDescripciones(cobRows.map(c=>c.cobertura_id));
    setViewCoberturas(cobRows.map(c=>({ ...c, _descripcion: viewDescMap[c.cobertura_id]||'' })));
    // Flota: la vista/slip muestran la primera unidad (el detalle completo va en la póliza).
    setViewAutoData((autoResult.data || [])[0] || null);
    setViewAgentes(agResult.data || []);
    // Endoso: traer la póliza endosada (número + vigencia) para mostrarla en la vista.
    setViewPolizaEndoso(null);
    if (row.tipo === 'endoso' && row.poliza_endoso_id) {
      const { data: pol } = await supabase.from('polizas')
        .select('numero, fecha_inicio, fecha_vencimiento').eq('id', row.poliza_endoso_id).maybeSingle();
      if (pol) setViewPolizaEndoso(pol);
    }
    await loadArchivos(row.id);
    if (row.tipo === 'cotizacion') await loadCotizAseg(row.id);
    setModal('view');
  };

  const openConvert = async (row) => {
    setSelected(row);
    setIaEndosoResultado(null);
    await loadArchivos(row.id);
    // Endoso: si ya se adjuntó el "Endoso recibido" en el ciclo del CRM, reutilizarlo
    // (no obligar a re-adjuntar el mismo documento aquí).
    setEndosoDocExistente(null);
    if (row.tipo === 'endoso') {
      const { data: arch } = await supabase.from('solicitud_archivos')
        .select('url, nombre').eq('solicitud_id', row.id).eq('tipo_doc', 'Endoso recibido')
        .order('creado_en', { ascending:false }).limit(1);
      if (arch?.[0]) setEndosoDocExistente(arch[0]);
    }

    let fecha_inicio = row.fecha_inicio || '';
    let fecha_vencimiento = row.fecha_vencimiento || '';

    // Para renovaciones: calcular automáticamente el nuevo período desde la póliza original
    // y recordar su ASEGURADORA: si la renovación se coloca en otra compañía, para nosotros
    // sigue siendo renovación, pero para la aseguradora nueva es emisión NUEVA.
    let asegAnterior = '';
    if (row.tipo === 'renovacion' && row.poliza_endoso_id) {
      const { data: polOrig } = await supabase.from('polizas')
        .select('fecha_inicio, fecha_vencimiento, aseguradora_id')
        .eq('id', row.poliza_endoso_id)
        .single();
      asegAnterior = polOrig?.aseguradora_id ? String(polOrig.aseguradora_id) : '';
      if (polOrig?.fecha_inicio && polOrig?.fecha_vencimiento) {
        const inicioOrig = new Date(polOrig.fecha_inicio + 'T12:00:00');
        const vencOrig   = new Date(polOrig.fecha_vencimiento + 'T12:00:00');
        const duracionMs = vencOrig - inicioOrig;
        const nuevaVenc  = new Date(vencOrig.getTime() + duracionMs);
        fecha_inicio      = vencOrig.toISOString().slice(0, 10);
        fecha_vencimiento = nuevaVenc.toISOString().slice(0, 10);
      }
    }

    // Endoso sobre póliza DECLARATIVA: marcar el modo declarativo (la declaración se
    // registrará como su propia letra en la fecha de vigencia del endoso).
    let esDecl = false;
    setPolizaEndosoActual(null);
    if (row.tipo === 'endoso' && row.poliza_endoso_id) {
      const { data: polDecl } = await supabase.from('polizas')
        .select('id, numero, cliente_id, ramo_id, aseguradora_id, fecha_inicio, fecha_vencimiento, es_declarativa, clientes(nombre)')
        .eq('id', row.poliza_endoso_id).maybeSingle();
      // Fijar la póliza a endosar para que el selector la muestre aunque no esté "activa"
      if (polDecl) setPolizaEndosoActual({
        id: polDecl.id, numero: polDecl.numero, cliente: polDecl.clientes?.nombre,
        cliente_id: polDecl.cliente_id, ramo_id: polDecl.ramo_id, aseguradora_id: polDecl.aseguradora_id,
        fecha_inicio: polDecl.fecha_inicio, fecha_vencimiento: polDecl.fecha_vencimiento, es_declarativa: polDecl.es_declarativa,
      });
      esDecl = !!polDecl?.es_declarativa;
    }
    setEndosoDeclarativa(esDecl);

    // Comportamiento del endoso (normal/cancelacion/prorroga) derivado del TIPO elegido
    // al crear la solicitud (catálogo administrable). Si la solicitud no tiene tipo, queda 'normal'.
    const tipoCat = (endosoTipos || []).find(t => String(t.id) === String(row.tipo_endoso_id));
    const comportamiento = tipoCat?.comportamiento || 'normal';

    // Para endosos: prellenar la info que el usuario puso al crear la solicitud
    // (descripción del cambio → notas; diferencial de prima → prima). La fecha del
    // endoso en la aseguradora la completa el usuario al facturar.
    setPolForm({
      numero: row.numero_poliza || '', fecha_inicio, fecha_vencimiento, fecha_emision:'',
      // Renovación: la vigencia que se renueva viene de la solicitud, pero queda EDITABLE
      // en el modal para poder corregirla si llegó vacía (o mal) — antes no había forma.
      poliza_anterior_id: row.tipo === 'renovacion' && row.poliza_endoso_id ? String(row.poliza_endoso_id) : '',
      // Aseguradora de la póliza que se crea: arranca con la de la solicitud y se puede
      // cambiar (a mano o la detecta la IA). `tipo_emision` = cómo la registra la COMPAÑÍA:
      // renovación en la misma aseguradora → 'renovada'; en otra (o póliza nueva) → 'nueva'.
      aseguradora_id: row.aseguradora_id ? String(row.aseguradora_id) : '',
      aseguradora_anterior_id: asegAnterior,
      tipo_emision: row.tipo === 'renovacion'
        ? (asegAnterior && row.aseguradora_id && String(row.aseguradora_id) !== asegAnterior ? 'nueva' : 'renovada')
        : 'nueva',
      // Otra aseguradora no puede arrastrar el saldo de la anterior (es póliza nueva para ella).
      arrastra_saldo: !(asegAnterior && row.aseguradora_id && String(row.aseguradora_id) !== asegAnterior),
      // Datos financieros prellenados de la solicitud/renovación — EDITABLES al crear la
      // póliza, porque la aseguradora pudo renovar/emitir con valores distintos.
      prima:           (row.prima != null ? row.prima : (row.prima_estimada != null ? row.prima_estimada : '')),
      impuesto:        row.impuesto != null ? row.impuesto : 5,
      comision_ramo:   row.comision_ramo != null ? row.comision_ramo : '',
      forma_pago:      row.forma_pago || '',
      frecuencia_pago: row.frecuencia_pago || 'mensual',
      num_cuotas:      row.num_cuotas != null ? row.num_cuotas : 1,
      descripcion_endoso: row.tipo === 'endoso' ? (row.notas || '') : '',
      delta_prima:        row.tipo === 'endoso' && row.prima != null ? String(row.prima) : '',
      fecha_endoso:       '',
      fecha_vencimiento_cobro: '',
      numero_endoso:      '',
      mes_pago_id:        '',
      cambio_mensualidad: false,   // Vida/Salud: cambiar la mensualidad completa (monto nuevo)
      nueva_mensualidad:  '',
      tipo_endoso:        comportamiento,
      tipo_endoso_id:     row.tipo_endoso_id || '',
      tipo_endoso_nombre: tipoCat?.nombre || '',
      // Lista de cambios capturados al crear la solicitud (Opción A). Editable al
      // facturar para confirmarla contra el endoso recibido de la aseguradora.
      cambiosList:        cambiosDesde(row),
      prima_devuelta:     '',
      metodo_cancelacion: 'prorrata',
      factor_penalizacion:'',
      nueva_fecha_vencimiento:'',
    });
    setConvertFileFactura(null);
    setConvertFileCond(null);
    // Facturación por partes: por defecto se marcan como recibidos TODOS los pendientes
    // (caso "llegó todo junto"); el usuario desmarca los que aún no llegaron.
    const listaConv = cambiosDesde(row);
    setRecibidosSel(listaConv.map((ch, i) => ch.estado === 'facturado' ? -1 : i).filter(i => i >= 0));
    setModal('convert');
  };

  const closeModal = () => {
    setModal(null); setSelected(null); setForm(EMPTY); setPolForm(EMPTY_POL); setRenovInfo(null);
    setPolizaAgentes([]); setPolizaCoberturas([]); setAutoForm(EMPTY_AUTO); setAutoList([{ ...EMPTY_AUTO }]);
    setArchivos([]); setConvertFileFactura(null); setConvertFileCond(null); setEndosoDocExistente(null); setComisionRamoBase(null); setPendingFiles([]); setRecibidosSel([]);
    setIaEndosoResultado(null);
    setEjecutivoBase(null); setShowAddCob(false); setNuevaCobertura(''); setAgenteSelector('');
    setCotizAsegList([]); setCotizAsegForm({ aseguradora_id:'', prima_cotizada:'', notas:'' });
    setComparativoFile(null); setSubramos([]);
    setViewCoberturas([]); setViewAutoData(null); setViewPolizaEndoso(null);
    setAutoWarning(null); setPolNumWarning(null);
    setEndosoDeclarativa(false);
    setPolizaEndosoActual(null);
    // Si este modal se abrió desde una póliza, regresar a esa póliza (reabre su vista)
    // en vez de dejar al usuario en la lista de Solicitudes.
    const backPoliza = backToPolizaRef.current;
    if (backPoliza) {
      backToPolizaRef.current = null;
      sessionStorage.removeItem('backFrom');
      sessionStorage.setItem('openPolizaId', String(backPoliza));
      sessionStorage.setItem('openPolizaTab', 'endosos');
      navigate('/polizas');
    } else if (backToCrmRef.current) {
      // Volver al CRM tras registrar el endoso desde el ciclo.
      backToCrmRef.current = false;
      sessionStorage.removeItem('backFrom');
      navigate('/crm');
    }
  };

  // ── Imprimir Slip ─────────────────────────────────────────────
  const handlePrintSlip = () => {
    if (!selected) return;
    const html = generateSlipHTML({ sol: selected, coberturas: viewCoberturas, autoData: viewAutoData, agentes: viewAgentes });
    imprimirHTML(html);   // iframe aislado: la ventana nueva ejecutaba lo escrito en el documento
  };

  const handlePrintSlipFromEdit = () => {
    const clienteObj = clientes.find(c => String(c.id) === String(form.cliente_id));
    const asegObj    = aseguradoras.find(a => String(a.id) === String(form.aseguradora_id));
    const ramoObj    = ramos.find(r => String(r.id) === String(form.ramo_id));
    const execObj    = ejecutivos.find(e => String(e.id) === String(form.ejecutivo_id));
    const sol = {
      ...form,
      id: selected?.id || 0,
      clienteNombre:     clienteObj?.nombre || '',
      aseguradoraNombre: asegObj?.nombre    || '',
      ramoNombre:        ramoObj?.nombre    || '',
      ejecutivoNombre:   execObj?.nombre    || '',
    };
    const coberturas = polizaCoberturas.filter(c => c.incluida).map(c => ({
      nombre: c.nombre, tipo_limite: c.tipo_limite,
      suma_asegurada: c.suma_asegurada || null,
      deducible: formatDeducible(c), otros: c.otros,
    }));
    const agentes = polizaAgentes.map(a => ({ agentes: { nombre: a.nombre }, porcentaje: a.porcentaje }));
    const autoDataEdit = isAuto ? {
      anio: autoForm.anio, marca: autoForm.marca, modelo: autoForm.modelo,
      color: autoForm.color, tipo_vehiculo: autoForm.tipo_vehiculo,
      pasajeros: autoForm.pasajeros, no_motor: autoForm.no_motor,
      chasis: autoForm.chasis, chasis_igual_motor: autoForm.chasis_igual_motor,
      placa: autoForm.placa, placa_por_reportar: autoForm.placa_por_reportar,
    } : null;
    const html = generateSlipHTML({ sol, coberturas, autoData: autoDataEdit, agentes });
    imprimirHTML(html);   // iframe aislado: la ventana nueva ejecutaba lo escrito en el documento
  };

  // Imprime la SOLICITUD DE ENDOSO (documento para la aseguradora) desde el formulario.
  const handlePrintEndosoFromForm = () => {
    const clienteObj = clientes.find(c => String(c.id) === String(form.cliente_id));
    const asegObj    = aseguradoras.find(a => String(a.id) === String(form.aseguradora_id));
    const ramoObj    = ramos.find(r => String(r.id) === String(form.ramo_id));
    const pol        = polizasEndosoOpts.find(p => String(p.id) === String(form.poliza_endoso_id));
    const sol = {
      id: selected?.id || 0,
      clienteNombre:     clienteObj?.nombre || '',
      aseguradoraNombre: asegObj?.nombre    || '',
      ramoNombre:        ramoObj?.nombre    || '',
      prima:             form.prima,
      notas:             form.notas,
    };
    // Enriquecer cada cambio con su nombre y target para el documento.
    const cambios = (form.cambiosList || []).map(ch => {
      const t = endosoTipos.find(x => String(x.id) === String(ch.tipo_endoso_id));
      return { nombre: t?.nombre || '', target: t?.target || '', cambios: ch.cambios };
    });
    const html = generateEndosoSolicitudHTML({ sol, cambios, poliza: pol });
    imprimirHTML(html);   // iframe aislado: la ventana nueva ejecutaba lo escrito en el documento
  };

  // ── Cotización por aseguradora ────────────────────────────────
  const loadCotizAseg = async (solicitudId) => {
    const {data} = await supabase.from('solicitud_cotizaciones_aseg')
      .select('*, aseguradoras(nombre)').eq('solicitud_id', solicitudId).order('id');
    setCotizAsegList(data || []);
  };

  const saveCotizAseg = async () => {
    if (!cotizAsegForm.aseguradora_id || !selected) return;
    setSavingCotizAseg(true);
    try {
      await insertRow('solicitud_cotizaciones_aseg', {
        solicitud_id: selected.id,
        aseguradora_id: parseInt(cotizAsegForm.aseguradora_id),
        prima_cotizada: cotizAsegForm.prima_cotizada ? parseFloat(cotizAsegForm.prima_cotizada) : null,
        notas: cotizAsegForm.notas || null,
      });
      setCotizAsegForm({ aseguradora_id:'', prima_cotizada:'', notas:'' });
      await loadCotizAseg(selected.id);
    } catch(e) { alert('Error: ' + e.message); }
    finally { setSavingCotizAseg(false); }
  };

  const deleteCotizAseg = async (id) => {
    if (!window.confirm('¿Eliminar esta cotización?')) return;
    await deleteRow('solicitud_cotizaciones_aseg', id);
    await loadCotizAseg(selected.id);
  };

  const toggleSeleccionada = async (item) => {
    // Desmarcar todas, marcar la seleccionada
    for (const c of cotizAsegList) {
      await updateRow('solicitud_cotizaciones_aseg', c.id, { seleccionada: c.id === item.id ? !item.seleccionada : false });
    }
    await loadCotizAseg(selected.id);
  };

  const uploadCotizAsegFile = async (file, itemId) => {
    if (!file) return;
    setUploadingCotizAseg(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `cotiz/${selected.id}/${itemId}_${Date.now()}.${ext}`;
      const {error} = await supabase.storage.from('solicitud-archivos').upload(path, file, {upsert:true});
      if (error) throw error;
      const {data:{publicUrl}} = supabase.storage.from('solicitud-archivos').getPublicUrl(path);
      await updateRow('solicitud_cotizaciones_aseg', itemId, { archivo_url: publicUrl, archivo_nombre: file.name });
      await loadCotizAseg(selected.id);
    } catch(e) { alert('Error al subir: ' + e.message); }
    finally { setUploadingCotizAseg(false); }
  };

  // ── Auto-tarea CRM ────────────────────────────────────────────
  const crearCRMTask = async (sol, nuevoEstado) => {
    const titulos = {
      cotizacion: {
        enviada_aseg:        `Seguimiento cotización — esperar propuesta aseguradora`,
        propuesta_recibida:  `Enviar comparativo al cliente`,
        comparativo_enviado: `Seguimiento propuesta — respuesta del cliente`,
      },
      nueva: { enviada_aseg: `Seguimiento solicitud — recibir póliza de aseguradora` },
      endoso: { enviada_aseg: `Seguimiento endoso — recibir documento de aseguradora` },
    };
    const titulo = titulos[sol.tipo]?.[nuevoEstado];
    if (!titulo) return;
    const { data: existe } = await supabase.from('crm_gestiones')
      .select('id').eq('solicitud_id', sol.id).eq('estado','abierto').limit(1);
    if (existe?.length) return;
    const en2 = new Date(); en2.setDate(en2.getDate() + 2);
    await insertRow('crm_gestiones', {
      tipo: sol.tipo === 'cotizacion' ? 'cotizacion' : 'seguimiento',
      titulo,
      descripcion: `${numSol(sol.id)} · ${sol.clienteNombre||''} · ${sol.ramoNombre||''}`,
      cliente_id: sol.cliente_id ? parseInt(sol.cliente_id) : null,
      solicitud_id: sol.id,
      estado: 'abierto',
      prioridad: 'normal',
      fecha_vencimiento: en2.toISOString().slice(0,10),
    });
  };

  const handleCambiarEstado = async (row, nuevoEstado) => {
    try {
      await updateRow('solicitudes', row.id, { estado: nuevoEstado, actualizado_en: new Date().toISOString() });
      await crearCRMTask({ ...row, estado: nuevoEstado }, nuevoEstado);
      await load();
    } catch(e) { alert('Error: ' + e.message); }
  };

  // Lee con IA la póliza adjunta en el "Crear Póliza" (convert) y autocompleta los
  // DATOS DE LA PÓLIZA (número, fechas, prima, impuesto, frecuencia). El usuario
  // siempre revisa antes de confirmar — o llena todo manual.
  // Cambia la aseguradora de la póliza que se va a crear (a mano o detectada por la IA) y
  // ajusta lo que depende de ella:
  //   · Emisión p/ aseguradora: en una RENOVACIÓN, si la compañía es distinta a la de la
  //     vigencia anterior, para nosotros sigue siendo renovación pero para ella es NUEVA.
  //   · Arrastre de saldo: otra aseguradora no arrastra el saldo de la anterior.
  //   · % de comisión: el del vínculo aseguradora-ramo, si está configurado.
  // Devuelve true si cambió de aseguradora respecto a la vigencia anterior.
  const aplicarAseguradoraConvert = async (asegId) => {
    const id = asegId ? String(asegId) : '';
    const esRenov = selected?.tipo === 'renovacion';
    const otraCia = esRenov && !!polForm.aseguradora_anterior_id && !!id && id !== String(polForm.aseguradora_anterior_id);
    setPolForm(p => ({
      ...p,
      aseguradora_id: id,
      ...(esRenov ? { tipo_emision: otraCia ? 'nueva' : 'renovada', arrastra_saldo: !otraCia } : {}),
    }));
    if (id && selected?.ramo_id && !selected?.es_fee) {
      const { data: link } = await supabase.from('aseguradora_ramos')
        .select('comision').eq('aseguradora_id', id).eq('ramo_id', selected.ramo_id).maybeSingle();
      if (link?.comision != null) pf('comision_ramo', String(link.comision));
    }
    return otraCia;
  };

  const autocompletarConvertIA = async () => {
    if (!convertFileFactura) return;
    setLeyendoPolizaIA(true);
    try {
      const res = await analizarPolizaIA(convertFileFactura);
      if (!res.ok) { alert(res.error); return; }
      const d = res.datos || {};
      const set = (k, v) => { if (v != null && String(v).trim() !== '') pf(k, String(v).trim()); };
      // Aseguradora del documento: si es otra (p. ej. la renovación se colocó en SURA y la
      // solicitud venía con ASSA), se toma la del documento — es lo que dice la póliza.
      let avisoAseg = '';
      const asegMatch = matchCatalogo(d.aseguradora, aseguradoras);
      if (asegMatch && String(asegMatch.id) !== String(polForm.aseguradora_id || '')) {
        const otraCia = await aplicarAseguradoraConvert(asegMatch.id);
        avisoAseg = `\n\nAseguradora tomada del documento: ${asegMatch.nombre}.`
          + (otraCia ? ' Es distinta a la de la vigencia anterior: queda como RENOVACIÓN para nosotros y emisión NUEVA para la aseguradora, sin arrastre de saldo. Revisa también el % de comisión.' : '');
      } else if (d.aseguradora && !asegMatch) {
        avisoAseg = `\n\n⚠️ El documento dice "${d.aseguradora}" y no está en el catálogo de aseguradoras: elígela a mano.`;
      }
      set('numero', d.numero_poliza);
      set('fecha_inicio', d.fecha_inicio);
      set('fecha_vencimiento', d.fecha_vencimiento);
      set('fecha_emision', d.fecha_emision);
      if (d.impuesto_pct != null) set('impuesto', d.impuesto_pct);
      // Prima neta (ya con descuentos), cuadrada contra el total con impuesto.
      const pn = primaNetaIA(d, parseFloat(polForm.impuesto || 5));
      if (pn.prima != null) set('prima', pn.prima);
      if (['mensual','bimensual','trimestral','semestral','anual'].includes(d.frecuencia_pago)) {
        pf('frecuencia_pago', d.frecuencia_pago);
        if (d.num_cuotas != null) set('num_cuotas', d.num_cuotas);
      }
      alert('✓ Datos leídos del documento. Revisa número, fechas y prima antes de confirmar.'
        + avisoAseg
        + (pn.aviso ? `\n\n⚠️ ${pn.aviso}` : ''));
    } finally { setLeyendoPolizaIA(false); }
  };

  // Valida con IA que el ENDOSO recibido coincide con lo solicitado (opcional), desde
  // el propio modal de registro: usa el documento adjunto (nuevo o el ya adjuntado en
  // el ciclo) y la lista de cambios de la solicitud. Mismo endpoint del CRM.
  const validarEndosoConvertIA = async () => {
    if (!selected) return;
    setValidandoEndosoIA(true); setIaEndosoResultado(null);
    try {
      // Documento: el archivo recién adjuntado o el "Endoso recibido" ya guardado.
      let media_type, dataB64;
      if (convertFileFactura) {
        const prep = await fileADatosIA(convertFileFactura);
        media_type = prep.media_type; dataB64 = prep.data;
      } else if (endosoDocExistente?.url) {
        const resp = await fetch(endosoDocExistente.url);
        const blob = await resp.blob();
        media_type = blob.type || (/\.pdf$/i.test(endosoDocExistente.nombre || '') ? 'application/pdf' : 'image/jpeg');
        dataB64 = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(String(fr.result).split(',')[1]);
          fr.onerror = rej; fr.readAsDataURL(blob);
        });
      } else { setIaEndosoResultado({ error: 'Adjunta primero el documento del endoso.' }); return; }

      // Lo solicitado: lista de cambios con su detalle (para que compare y señale faltantes).
      const cambiosTexto = (polForm.cambiosList || []).map((c, i) => {
        const nombre = (endosoTipos || []).find(t => String(t.id) === String(c.tipo_endoso_id))?.nombre || 'Cambio';
        const det = c.cambios && typeof c.cambios === 'object'
          ? Object.entries(c.cambios).filter(([k, v]) => v !== '' && v != null && k !== 'vehiculo_id' && k !== 'cobertura_id')
              .map(([k, v]) => `${k}=${v}`).join(', ') : '';
        const est = c.estado === 'facturado' ? ' [ya facturado]' : '';
        return `${i + 1}) ${nombre}${det ? ': ' + det : ''}${est}`;
      }).join('  ·  ');

      const r = await fetch('/api/analizar-endoso', {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({
          media_type, data: dataB64,
          solicitado: {
            tipo_endoso: polForm.tipo_endoso_nombre || '',
            descripcion: [selected.notas || '', cambiosTexto ? `Cambios solicitados: ${cambiosTexto}` : ''].filter(Boolean).join(' | '),
            prima: selected.prima ?? '',
            poliza: selected.numero_poliza || polizaEndosoActual?.numero || '',
            cliente: selected.clienteNombre || '',
          },
        }),
      });
      const out = await r.json().catch(() => null);
      if (!out?.ok) {
        // El mensaje debe decir POR QUÉ falló (incl. cupo agotado del plan).
        setIaEndosoResultado({ error:
          out?.reason === 'no_configurado' ? 'La IA aún no está activada en tu sistema.'
          : out?.reason === 'cupo' ? (out?.detalle || 'Llegaste al límite de análisis con IA de tu plan este mes.')
          : 'No se pudo analizar el endoso.' + (out?.detalle ? ` (${out.detalle})` : '') });
        return;
      }
      setIaEndosoResultado(out.resultado || { error: 'Sin resultado.' });
    } catch (e) { setIaEndosoResultado({ error: 'Error al analizar: ' + (e.message || '') }); }
    finally { setValidandoEndosoIA(false); }
  };

  // "Enviado al Cliente" (endoso) = paso FINAL: exige la evidencia del correo enviado
  // al asegurado (pegar el texto o adjuntar el PDF/imagen). Se guarda como archivo
  // "Correo al cliente" de la solicitud y cierra la tarea del CRM (mismo patrón que
  // confirmarEnvioCliente del CRM).
  const confirmarEnvioClienteSol = async () => {
    if (!selected || !envioCliente) return;
    const esTexto = envioCliente.modo === 'texto';
    if (esTexto && !envioCliente.texto.trim()) return alert('Pega el texto del correo enviado al cliente.');
    if (!esTexto && !envioCliente.file) return alert('Adjunta el PDF/imagen del correo enviado al cliente.');
    setEnviandoCliente(true);
    try {
      let url, nombre;
      if (esTexto) {
        const blob = new Blob([envioCliente.texto], { type: 'text/plain;charset=utf-8' });
        const path = `${selected.id}/correo_cliente_${Date.now()}.txt`;
        const { error } = await supabase.storage.from('solicitud-archivos').upload(path, blob, { upsert:true, contentType:'text/plain' });
        if (error) throw error;
        url = supabase.storage.from('solicitud-archivos').getPublicUrl(path).data.publicUrl;
        nombre = 'Correo al cliente (texto).txt';
      } else {
        const ext = envioCliente.file.name.split('.').pop();
        const path = `${selected.id}/correo_cliente_${Date.now()}.${ext}`;
        const { error } = await supabase.storage.from('solicitud-archivos').upload(path, envioCliente.file, { upsert:true });
        if (error) throw error;
        url = supabase.storage.from('solicitud-archivos').getPublicUrl(path).data.publicUrl;
        nombre = envioCliente.file.name;
      }
      await insertRow('solicitud_archivos', { solicitud_id: selected.id, nombre, url, tipo: 'solicitud', tipo_doc: 'Correo al cliente' });
      await updateRow('solicitudes', selected.id, { estado: 'enviado_cliente', actualizado_en: new Date().toISOString() });
      // Cerrar la tarea del CRM ligada a esta solicitud (fin del ciclo del endoso).
      const { data: tareas } = await supabase.from('crm_gestiones')
        .select('id').eq('solicitud_id', selected.id).in('estado', ['abierto','en_proceso']);
      for (const t of (tareas || [])) {
        await updateRow('crm_gestiones', t.id, { estado: 'cerrado', actualizado_en: new Date().toISOString() });
        await insertRow('crm_historial', { gestion_id: t.id, usuario_id: user?.id, accion: 'cerrar', detalle: 'Endoso enviado al cliente — tarea cerrada.' });
      }
      setEnvioCliente(null);
      setSelected(s => ({ ...s, estado: 'enviado_cliente' }));
      await loadArchivos(selected.id);
      await load();
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setEnviandoCliente(false); }
  };

  // Al CREAR una solicitud de endoso: abrir una tarea en el CRM para el ejecutivo de cuenta
  // de la póliza ("Endoso pendiente"), para darle seguimiento. Idempotente por solicitud_id
  // (si ya hay una tarea abierta para esta solicitud, no crea otra — igual que la generación
  // automática del CRM al entrar al módulo).
  const crearTareaEndosoPendiente = async (solId, polizaEndosoId, clienteId, notas) => {
    if (!solId || !polizaEndosoId) return;
    const { data: existe } = await supabase.from('crm_gestiones')
      .select('id').eq('solicitud_id', solId).in('estado', ['abierto','en_proceso']).limit(1);
    if (existe?.length) return;
    const { data: pol } = await supabase.from('polizas')
      .select('numero, ejecutivo_id').eq('id', polizaEndosoId).maybeSingle();
    let asignado = null;
    if (pol?.ejecutivo_id) {
      const { data: u } = await supabase.from('usuarios')
        .select('id').eq('ejecutivo_id', pol.ejecutivo_id).eq('activo', true).limit(1);
      asignado = u?.[0]?.id || null;
    }
    await insertRow('crm_gestiones', {
      tipo: 'endoso', prioridad: 'normal',
      titulo: `Endoso pendiente — Póliza ${pol?.numero || polizaEndosoId}`,
      descripcion: `Solicitud de endoso ${numSol(solId)} pendiente de gestión.${notas ? ' ' + notas : ''}`,
      poliza_id: polizaEndosoId, cliente_id: clienteId ? parseInt(clienteId) : null, solicitud_id: solId,
      asignado_a: asignado,
      estado: 'abierto', fecha_vencimiento: new Date().toISOString().slice(0,10),
    });
  };

  // ── Convertir cotización → solicitud de póliza nueva ─────────
  const handleConvertCotizToSol = async () => {
    if (!selected) return;
    const seleccionada = cotizAsegList.find(c => c.seleccionada);
    const prima = seleccionada?.prima_cotizada || '';
    const asegId = seleccionada?.aseguradora_id || '';
    try {
      const nueva = await insertRow('solicitudes', {
        cliente_id: selected.cliente_id,
        ramo_id: selected.ramo_id,
        aseguradora_id: asegId || null,
        ejecutivo_id: selected.ejecutivo_id || null,
        creado_por: user?.id || null,
        tipo: 'nueva',
        estado: 'borrador',
        prima: prima || null,
        impuesto: selected.impuesto == null ? 5 : selected.impuesto,
        forma_pago: selected.forma_pago || null,
        frecuencia_pago: selected.frecuencia_pago || 'mensual',
        num_cuotas: selected.num_cuotas || 1,
        notas: selected.notas || null,
        cotizacion_origen_id: selected.id,
      });
      // Copiar coberturas
      const {data: cobs} = await supabase.from('solicitud_coberturas').select('*').eq('solicitud_id', selected.id);
      for (const c of (cobs||[])) {
        await insertRow('solicitud_coberturas', { ...c, id:undefined, solicitud_id: nueva.id });
      }
      // Copiar agentes
      const {data: ags} = await supabase.from('solicitud_agentes').select('*').eq('solicitud_id', selected.id);
      for (const a of (ags||[])) {
        await insertRow('solicitud_agentes', { ...a, id:undefined, solicitud_id: nueva.id });
      }
      await updateRow('solicitudes', selected.id, { estado:'convertida', actualizado_en: new Date().toISOString() });
      await load(); closeModal();
      alert(`✓ Solicitud de póliza ${numSol(nueva.id)} creada. Ábrela para completar los datos de emisión.`);
    } catch(e) { alert('Error: ' + e.message); }
  };

  // ── Subir comparativo ─────────────────────────────────────────
  const uploadComparativo = async (file) => {
    if (!file || !selected) return;
    setUploadingComparativo(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `cotiz/${selected.id}/comparativo_${Date.now()}.${ext}`;
      const {error} = await supabase.storage.from('solicitud-archivos').upload(path, file, {upsert:true});
      if (error) throw error;
      const {data:{publicUrl}} = supabase.storage.from('solicitud-archivos').getPublicUrl(path);
      await updateRow('solicitudes', selected.id, { comparativo_url: publicUrl, comparativo_nombre: file.name });
      setSelected(s => ({ ...s, comparativo_url: publicUrl, comparativo_nombre: file.name }));
      setComparativoFile(null);
    } catch(e) { alert('Error al subir comparativo: ' + e.message); }
    finally { setUploadingComparativo(false); }
  };

  // ── Guardar solicitud ─────────────────────────────────────────
  const handleSave = async (estado = 'borrador', abrirRegistro = false) => {
    if (!form.cliente_id) return alert('El cliente es requerido.');
    if (!form.ramo_id)    return alert('El ramo es requerido.');
    if (abrirRegistro && form.tipo === 'endoso' && !form.poliza_endoso_id)
      return alert('Selecciona la póliza a endosar antes de registrar el endoso.');
    if ((form.tipo==='nueva'||form.tipo==='renovacion') && !form.es_fee && !form.comision_ramo)
      return showToast('error','El % de comisión del ramo es obligatorio. Si la póliza no lleva comisión, márcala como Fee.');
    if (form.es_fee && !form.monto_fee)
      return showToast('error','Ingresa el monto del Fee.');
    try {
      setSaving(true);
      const payload = {
        cliente_id:       parseInt(form.cliente_id),
        ramo_id:          parseInt(form.ramo_id),
        aseguradora_id:   form.aseguradora_id ? parseInt(form.aseguradora_id) : null,
        ejecutivo_id:     form.ejecutivo_id   ? parseInt(form.ejecutivo_id)   : null,
        agente_id:        polizaAgentes.length>0 ? parseInt(polizaAgentes[0].agente_id) : null,
        tipo:             form.tipo,
        subramo_id:       form.subramo_id ? parseInt(form.subramo_id) : null,
        // La póliza vinculada se conserva para ENDOSO (la que se endosa) y para RENOVACIÓN
        // (la vigencia que se está renovando). Si aquí se pierde, la renovación queda
        // huérfana: no se enlaza la vigencia nueva con la vieja (renovacion_de_poliza_id)
        // y el CRM nunca se entera de que la póliza ya se renovó.
        poliza_endoso_id: ['endoso','renovacion'].includes(form.tipo) && form.poliza_endoso_id ? parseInt(form.poliza_endoso_id) : null,
        numero_poliza:    form.numero_poliza?.trim() || null,
        // tipo_endoso_id: para tracking se guarda el tipo del PRIMER cambio (con tipo).
        ...(form.tipo === 'endoso' && (form.cambiosList||[]).find(ch=>ch.tipo_endoso_id)
          ? { tipo_endoso_id: parseInt((form.cambiosList||[]).find(ch=>ch.tipo_endoso_id).tipo_endoso_id) } : {}),
        // cambios_json (Opción A): LISTA de cambios [{tipo_endoso_id, cambios}]; se filtran los vacíos.
        ...(form.tipo === 'endoso' && (form.cambiosList||[]).some(ch=>ch.tipo_endoso_id)
          ? { cambios_json: (form.cambiosList||[]).filter(ch=>ch.tipo_endoso_id) } : {}),
        prima:            form.prima          ? parseFloat(form.prima)          : null,
        prima_estimada:   form.prima          ? parseFloat(form.prima)          : null,
        impuesto:         form.impuesto       ? parseFloat(form.impuesto)       : null,
        comision_ramo:    form.es_fee ? 0 : (form.comision_ramo ? parseFloat(form.comision_ramo) : null),
        es_fee:           form.es_fee || false,
        monto_fee:        form.es_fee && form.monto_fee ? parseFloat(form.monto_fee) : null,
        forma_pago:       form.forma_pago     || null,
        frecuencia_pago:  form.frecuencia_pago||'mensual',
        num_cuotas:       form.num_cuotas     ? parseInt(form.num_cuotas)       : 1,
        fecha_inicio:     form.fecha_inicio   || null,
        fecha_vencimiento:form.fecha_vencimiento||null,
        fecha_emision:    form.fecha_emision  || null,
        notas:            form.notas          || null,
        referido_id:      form.referido_id ? parseInt(form.referido_id) : null,
        referido_comision_pct: form.referido_id && form.referido_comision_pct !== ''
          ? parseFloat(form.referido_comision_pct) : null,
        estado,
      };

      let solicitudId;
      if (modal === 'create') {
        // creado_por: quién crea la solicitud (para asignar el seguimiento de cotización a esa persona)
        const nueva = await insertRow('solicitudes', { ...payload, creado_por: user?.id || null });
        solicitudId = nueva.id;
        setSelected(nueva);
      } else {
        await updateRow('solicitudes', selected.id, {...payload, actualizado_en:new Date().toISOString()});
        solicitudId = selected.id;
        await supabase.from('solicitud_agentes').delete().eq('solicitud_id',solicitudId);
        await supabase.from('solicitud_coberturas').delete().eq('solicitud_id',solicitudId);
        await supabase.from('solicitud_autos').delete().eq('solicitud_id',solicitudId);
      }

      // Agentes
      for (const ag of polizaAgentes) {
        await supabase.from('solicitud_agentes').insert({
          solicitud_id:solicitudId, agente_id:parseInt(ag.agente_id), porcentaje:parseFloat(ag.porcentaje)||0,
        });
      }

      // Coberturas
      for (const c of polizaCoberturas.filter(c=>c.incluida)) {
        let cobId = c.cobertura_id;
        if (c.es_personalizada&&!cobId&&form.ramo_id) {
          const newCob = await insertRow('coberturas',{ramo_id:parseInt(form.ramo_id),nombre:c.nombre,activa:true,orden:99});
          cobId = newCob.id;
        }
        await supabase.from('solicitud_coberturas').insert({
          solicitud_id:solicitudId, cobertura_id:cobId||null,
          nombre:c.nombre, tipo_limite:c.tipo_limite||'limite',
          suma_asegurada:c.suma_asegurada?parseFloat(c.suma_asegurada):null,
          deducible:formatDeducible(c), otros:c.otros||null,
          incluida:true, nota:null,
        });
      }

      // Auto: se guardan TODAS las unidades (flota) con sus coberturas por unidad.
      // Ya se borraron arriba las anteriores (solicitud_autos y solicitud_coberturas).
      if (isAuto) {
        const unidades = (autoList && autoList.length) ? autoList : [autoForm];
        for (const veh of unidades) {
          const sa = await insertRow('solicitud_autos', {
            solicitud_id: solicitudId,
            anio: veh.anio ? parseInt(veh.anio) : null,
            marca: veh.marca || null, modelo: veh.modelo || null,
            color: veh.color || null, tipo_vehiculo: veh.tipo_vehiculo || null,
            pasajeros: veh.pasajeros ? parseInt(veh.pasajeros) : null,
            no_motor: veh.no_motor || null,
            chasis_igual_motor: veh.chasis_igual_motor,
            chasis: veh.chasis_igual_motor ? veh.no_motor : (veh.chasis || null),
            placa: veh.placa_por_reportar ? 'Por reportar' : (veh.placa || null),
            placa_por_reportar: veh.placa_por_reportar,
            dueno_nombre: veh.dueno_igual ? null : (veh.dueno_nombre || null),
            dueno_cedula: veh.dueno_igual ? null : (veh.dueno_cedula || null),
          });
          for (const c of (veh.coberturas || [])) {
            if (!c.nombre) continue;
            await insertRow('solicitud_coberturas', {
              solicitud_id: solicitudId, solicitud_auto_id: sa?.id || null, cobertura_id: null,
              nombre: c.nombre, tipo_limite: c.tipo_limite || 'limite',
              suma_asegurada: c.suma_asegurada !== '' && c.suma_asegurada != null ? parseFloat(c.suma_asegurada) : null,
              deducible: c.deducible || null, otros: null, incluida: true, nota: null,
            });
          }
        }
      }

      // Subir archivos pendientes (adjuntados durante la creación)
      if (pendingFiles.length > 0) {
        for (const pf of pendingFiles) {
          try {
            const ext = pf.file.name.split('.').pop();
            const path = `${solicitudId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
            const { error } = await supabase.storage.from('solicitud-archivos').upload(path, pf.file, { upsert:true });
            if (!error) {
              const { data: { publicUrl } } = supabase.storage.from('solicitud-archivos').getPublicUrl(path);
              const tipoDoc = pf.tipo_doc === 'Otro'
                ? `Otro: ${(pf.otro_desc || '').trim()}`
                : (pf.tipo_doc || null);
              await insertRow('solicitud_archivos', { solicitud_id: solicitudId, nombre: pf.file.name, url: publicUrl, tipo: 'solicitud', tipo_doc: tipoDoc, tamanio: pf.file.size });
            }
          } catch {}
        }
        setPendingFiles([]);
      }

      // Endoso: abrir la tarea de seguimiento en el CRM (al ejecutivo de cuenta de la póliza)
      if (form.tipo === 'endoso' && form.poliza_endoso_id) {
        try { await crearTareaEndosoPendiente(solicitudId, parseInt(form.poliza_endoso_id), form.cliente_id, form.notas); }
        catch (e) { console.error('crear tarea de endoso', e); }
      }

      await load();
      const tipo = form.tipo === 'cotizacion' ? 'Cotización' : form.tipo === 'endoso' ? 'Endoso' : 'Solicitud';
      showToast('success', `${tipo} guardada exitosamente.`);
      // ENDOSO DIRECTO (ya recibido de la aseguradora): tras crear la solicitud, abrir de
      // una el "Registrar Endoso" — así no hay que crear la solicitud y volver a buscarla.
      if (abrirRegistro && form.tipo === 'endoso' && solicitudId) {
        const { data: solFull } = await supabase.from('solicitudes')
          .select('*, clientes(nombre), ramos(nombre), aseguradoras(nombre)').eq('id', solicitudId).maybeSingle();
        if (solFull) {
          await openConvert({ ...solFull, clienteNombre: solFull.clientes?.nombre, ramoNombre: solFull.ramos?.nombre, aseguradoraNombre: solFull.aseguradoras?.nombre });
          return; // openConvert ya cambió al modal de registro; no cerrar
        }
      }
      closeModal();
    } catch(e){
      showToast('error', traducirError(e));
    }
    finally { setSaving(false); }
  };

  // ── Cambiar estado ────────────────────────────────────────────
  const handleEstado = async (row, estado) => {
    try {
      await updateRow('solicitudes', row.id, {estado, actualizado_en:new Date().toISOString()});
      await load();
    } catch(e){ alert('Error: '+(e.message||'')); }
  };

  // ── Cancelar solicitud (soft delete con motivo) ───────────────
  const handleDelete = (row) => {
    setCancelModal(row);
    setCancelReason('');
  };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) return;
    try {
      await updateRow('solicitudes', cancelModal.id, {
        estado: 'cancelada',
        motivo_cancelacion: cancelReason.trim(),
        actualizado_en: new Date().toISOString(),
      });
      // Si esta solicitud cerró una tarea de renovación del CRM, reabrirla: la renovación
      // vuelve a estar pendiente de gestionar (se desenlaza de la solicitud cancelada).
      try {
        await supabase.from('crm_gestiones')
          .update({ estado: 'abierto', solicitud_id: null, actualizado_en: new Date().toISOString() })
          .eq('solicitud_id', cancelModal.id).eq('tipo', 'renovacion');
      } catch (e) { console.error('reabrir tarea renovación', e); }
      // Endoso: cerrar de inmediato su tarea de seguimiento en el CRM (se eliminó la solicitud con motivo).
      try {
        await supabase.from('crm_gestiones')
          .update({ estado: 'cerrado', actualizado_en: new Date().toISOString() })
          .eq('solicitud_id', cancelModal.id).eq('tipo', 'endoso').in('estado', ['abierto', 'en_proceso']);
      } catch (e) { console.error('cerrar tarea endoso', e); }
      setCancelModal(null);
      setCancelReason('');
      await load();
      showToast('success', `Solicitud ${numSol(cancelModal.id)} cancelada.`);
    } catch(e) {
      showToast('error', 'Error al cancelar: ' + (e.message || ''));
    }
  };

  const confirmReactivar = async () => {
    if (!reactivarReason.trim() || !reactivarConfirm) return;
    try {
      await updateRow('solicitudes', reactivarModal.id, {
        estado: 'borrador',
        motivo_cancelacion: null,
        motivo_reactivacion: reactivarReason.trim(),
        actualizado_en: new Date().toISOString(),
      });
      setReactivarModal(null);
      setReactivarReason('');
      setReactivarConfirm(false);
      await load();
      showToast('success', `Solicitud ${numSol(reactivarModal.id)} reactivada como Borrador.`);
    } catch(e) {
      showToast('error', 'Error al reactivar: ' + (e.message || ''));
    }
  };

  // ── Convertir a Póliza ────────────────────────────────────────
  const handleConvert = async () => {
    const esEndoso = selected.tipo === 'endoso';

    if (esEndoso) {
      // FLUJO ENDOSO: modifica la póliza existente
      if (!selected.poliza_endoso_id) return alert('El endoso debe tener una póliza asociada. Edita la solicitud y selecciona la póliza a endosar.');
      if (!polForm.descripcion_endoso) return alert('La razón/descripción del endoso es requerida.');
      // El documento del endoso recibido de la aseguradora es OBLIGATORIO para registrar.
      // Puede venir de un archivo nuevo o del que ya se adjuntó en el ciclo (endosoDocExistente).
      if (!convertFileFactura && !endosoDocExistente) return alert('Adjunta el documento del endoso recibido de la aseguradora para poder registrarlo.');
      const tipoEndoso = polForm.tipo_endoso || 'normal';
      const esCancelacion = tipoEndoso === 'cancelacion';
      // CAMBIO DE MENSUALIDAD (Vida/Salud individual): en vez de un delta, el usuario pone
      // el NUEVO monto mensual completo y se actualizan las cuotas pendientes a ese monto
      // (igual que el botón "Cambiar prima mensual" de la póliza).
      const cambiarMensualidad = !esCancelacion && !endosoDeclarativa && tipoEndoso === 'normal'
        && !!polForm.cambio_mensualidad && (parseFloat(polForm.nueva_mensualidad) || 0) > 0;
      if (cambiarMensualidad && !polForm.fecha_endoso)
        return alert('Indica la Fecha del Endoso: es la fecha desde la que aplica la nueva mensualidad.');
      // DECLARATIVA (modelo "una letra por declaración"): la prima de esta declaración
      // se registra como su PROPIA letra en el mes de la FECHA DEL ENDOSO (vigencia).
      // Por eso la fecha del endoso es obligatoria. Una reducción/devolución (delta < 0)
      // se registra como nota de crédito global (no requiere mes).
      if (!esCancelacion && endosoDeclarativa && (parseFloat(polForm.delta_prima) || 0) > 0 && !polForm.fecha_endoso)
        return alert('Indica la Fecha del Endoso (vigencia): es el mes que se declara. La prima entra como su propia letra en ese mes.');
      if (esCancelacion && (parseFloat(polForm.prima_devuelta) || 0) < 0)
        return alert('La prima devuelta no puede ser negativa.');
      // Facturación por PARTES: índices de los cambios PENDIENTES marcados como recibidos.
      const idxSel = (recibidosSel || []).filter(i => polForm.cambiosList?.[i] && polForm.cambiosList[i].estado !== 'facturado');
      const hayPendientes = (polForm.cambiosList || []).some(ch => ch.estado !== 'facturado');
      if (hayPendientes && idxSel.length === 0)
        return alert('Marca al menos un cambio recibido en este endoso (los que no marques quedan pendientes).');
      try {
        setConverting(true);
        const polizaId = parseInt(selected.poliza_endoso_id);
        const seleccionados = idxSel.map(i => polForm.cambiosList[i]);
        // Cancelación: la "prima devuelta" (positiva) es una reducción → delta negativo.
        const deltaPrima = esCancelacion
          ? -(parseFloat(polForm.prima_devuelta) || 0)
          : (cambiarMensualidad ? 0 : (parseFloat(polForm.delta_prima) || 0));

        // IMPORTANTE: la prima de la póliza NO se modifica con el endoso. Queda fija
        // como la prima de emisión. El endoso solo mueve el SALDO del cliente (se crea
        // una cuota por el diferencial más abajo, con vencimiento = fecha de facturado).
        const { data: polActual } = await supabase.from('polizas').select('impuesto, forma_pago').eq('id', polizaId).single();
        const imp = polActual?.impuesto == null ? 5 : (parseFloat(polActual?.impuesto) || 0);

        // Subir documento del endoso
        let urlDoc = null; let nombreDoc = null;
        if (convertFileFactura) {
          setUploadingConvert(true);
          try {
            const ext = convertFileFactura.name.split('.').pop();
            const path = `endosos/${polizaId}/${Date.now()}.${ext}`;
            const { error } = await supabase.storage.from('solicitud-archivos').upload(path, convertFileFactura, { upsert:true });
            // No tragar el error en silencio: si falla la subida, abortar para no
            // registrar el endoso sin su documento (que es obligatorio).
            if (error) throw error;
            const { data: { publicUrl } } = supabase.storage.from('solicitud-archivos').getPublicUrl(path);
            urlDoc = publicUrl; nombreDoc = convertFileFactura.name;
            // Guardarlo también como archivo de la solicitud ("Endoso recibido"): así un
            // re-registro (facturación por partes) lo reutiliza y NO lo vuelve a pedir.
            try {
              await insertRow('solicitud_archivos', { solicitud_id: selected.id, nombre: nombreDoc, url: urlDoc, tipo: 'solicitud', tipo_doc: 'Endoso recibido' });
            } catch { /* no bloquear el registro si falla el vínculo del archivo */ }
          } finally { setUploadingConvert(false); }
        } else if (endosoDocExistente) {
          // Reutiliza el endoso recibido ya adjunto en el ciclo del CRM.
          urlDoc = endosoDocExistente.url; nombreDoc = endosoDocExistente.nombre;
        }

        // Registrar el endoso
        const endoso = await insertRow('poliza_endosos', {
          poliza_id: polizaId,
          solicitud_id: selected.id,
          fecha: polForm.fecha_endoso || new Date().toISOString().slice(0,10),
          descripcion: polForm.descripcion_endoso,
          numero_endoso: polForm.numero_endoso || null,
          delta_prima: deltaPrima,
          // Tracking del tipo de endoso elegido en la solicitud (columna nueva → condicional).
          ...(polForm.tipo_endoso_id ? { tipo_endoso_id: parseInt(polForm.tipo_endoso_id) } : {}),
          // Un endoso 'normal' guarda EXACTAMENTE como antes (sin tipo/metodo/factor): así
          // no referencia columnas que un entorno sin el SQL aditivo podría no tener. Solo
          // cancelación/prórroga (features nuevas que ya requieren el SQL) agregan esos campos.
          ...(tipoEndoso !== 'normal' ? { tipo: tipoEndoso } : {}),
          ...(esCancelacion ? {
            metodo_calculo: polForm.metodo_cancelacion || 'prorrata',
            ...(polForm.factor_penalizacion ? { factor_penalizacion: parseFloat(polForm.factor_penalizacion) } : {}),
          } : {}),
          url_documento: urlDoc,
          nombre_documento: nombreDoc,
          estado: 'activo',
        });

        // ── Aplicar solo los cambios RECIBIDOS en este endoso (facturación por partes) ──
        // Se aplican los seleccionados; los demás quedan pendientes en la solicitud. Se
        // guarda en el endoso qué cambios trajo (cambios_json) + el "antes" (snapshot).
        if (endoso?.id && seleccionados.length) {
          const snapshots = [];
          try {
            for (const ch of seleccionados) {
              const tCat = (endosoTipos || []).find(t => String(t.id) === String(ch.tipo_endoso_id));
              const target = tCat?.target;
              if (target && targetAplica(target)) {
                const snap = await aplicarCambioEndoso(polizaId, target, ch.cambios || {});
                snapshots.push(snap);
              }
            }
            await updateRow('poliza_endosos', endoso.id, {
              cambios_json: seleccionados,
              cambios_snapshot: snapshots,
            });
          } catch (e) {
            // Si un cambio falla, avisar pero no perder el endoso ya registrado.
            showToast('error', 'El endoso se registró, pero un cambio no se pudo aplicar: ' + traducirError(e));
          }
        }

        // CAMBIO DE MENSUALIDAD (Vida/Salud): pone las cuotas PENDIENTES futuras (venc. ≥ fecha
        // del endoso) al NUEVO monto mensual. Las pagadas/parciales no se tocan. Reaplica saco.
        if (cambiarMensualidad && endoso?.id) {
          const nueva = Math.round((parseFloat(polForm.nueva_mensualidad) || 0) * 100) / 100;
          const { data: cuotasPend } = await supabase.from('pagos')
            .select('id').eq('poliza_id', polizaId).eq('estado', 'pendiente').gte('fecha_vencimiento', polForm.fecha_endoso);
          const idsPend = (cuotasPend || []).map(c => c.id);
          if (idsPend.length) await supabase.from('pagos').update({ monto: nueva }).in('id', idsPend);
          await reaplicarSacoPoliza(polizaId);
        }

        // El endoso NO toca la prima inicial. Mueve el saldo del cliente DILUYENDO
        // el diferencial (con impuesto) entre las cuotas existentes cuyo vencimiento
        // cae entre la fecha del endoso y el vencimiento del cobro. Cada ajuste se
        // registra en poliza_endoso_ajustes para poder revertirlo al anular el endoso.
        if (deltaPrima !== 0 && endoso?.id) {
          const total = Math.round(deltaPrima * (1 + imp / 100) * 100) / 100;
          const recurrenteMensual = !!polForm.prima_recurrente_mensual && !endosoDeclarativa;

          if (recurrenteMensual) {
            // INCREMENTO MENSUAL RECURRENTE (ej. incluir dependiente en Vida/Salud individual):
            // el total (con imp.) se SUMA a CADA cuota FUTURA pendiente (venc. >= fecha del endoso),
            // no se diluye. Las cuotas ya vencidas/pagadas antes de esa fecha no se tocan. Cada
            // suma se registra en poliza_endoso_ajustes para poder revertirla al anular el endoso.
            const desde = polForm.fecha_endoso || new Date().toISOString().slice(0, 10);
            const { data: rango } = await supabase.from('pagos')
              .select('id, monto, cuota_num, fecha_vencimiento')
              .eq('poliza_id', polizaId).gte('fecha_vencimiento', desde).order('fecha_vencimiento');
            const futuras = (rango || []).filter(p => p.cuota_num != null);
            if (futuras.length) {
              for (const cu of futuras) {
                const nuevoMonto = Math.round(((parseFloat(cu.monto) || 0) + total) * 100) / 100;
                await updateRow('pagos', cu.id, { monto: nuevoMonto });
                await insertRow('poliza_endoso_ajustes', { endoso_id: endoso.id, pago_id: cu.id, monto: total });
              }
            } else {
              // Sin cuotas futuras (ej. plan ya cerrado): letra suelta por el incremento.
              await insertRow('pagos', {
                poliza_id: polizaId, monto: total, estado: 'pendiente',
                fecha_vencimiento: desde, metodo: polActual?.forma_pago || null,
                cuota_num: null, origen: 'endoso', endoso_id: endoso.id,
              });
            }
          } else if (total < 0) {
            // REDUCCIÓN / DEVOLUCIÓN (delta negativo): RU es corredor y NO le devuelve
            // dinero al cliente — eso lo maneja la aseguradora. Se registra UNA nota de
            // crédito (fila `pagos` negativa) que el saco netea contra la deuda (clamp ≥0);
            // y se genera la COMISIÓN NEGATIVA (clawback) sobre la prima devuelta. Al anular
            // el endoso, confirmAnularEndoso borra el pago por endoso_id y su comisión por pago_id.
            const credito = await insertRow('pagos', {
              poliza_id: polizaId, monto: total, estado: 'pendiente',
              fecha_vencimiento: polForm.fecha_endoso || new Date().toISOString().slice(0, 10),
              metodo: polActual?.forma_pago || null,
              cuota_num: null, origen: 'endoso', endoso_id: endoso.id,
            });
            if (credito?.id) await generarComisionNegativa(polizaId, credito.id, total);
          } else if (endosoDeclarativa) {
            // DECLARATIVA (modelo "una letra por declaración"): cada declaración es su
            // PROPIA letra, con vencimiento en la FECHA DEL ENDOSO (el mes que se declara).
            // Varias declaraciones pueden caer en el mismo mes (varias personas): cada una
            // su letra, sin bloquear el mes. Entra al saco como cualquier saldo.
            await insertRow('pagos', {
              poliza_id: polizaId, monto: total, estado: 'pendiente',
              fecha_vencimiento: polForm.fecha_endoso || new Date().toISOString().slice(0, 10),
              metodo: polActual?.forma_pago || null,
              cuota_num: null, origen: 'endoso', endoso_id: endoso.id,
            });
            // El mes ya quedó declarado → cierra su recordatorio en el CRM (si existe).
            try { await cerrarPeriodoDeclarado(polizaId, polForm.fecha_endoso || new Date().toISOString().slice(0, 10)); }
            catch (e) { /* no crítico: el recordatorio se puede cerrar a mano */ }
          } else {
            // NO declarativa: se diluye el diferencial entre las cuotas cuyo vencimiento
            // cae entre la fecha del endoso y el vencimiento del cobro (ajustes para revertir).
            const desde = polForm.fecha_endoso || new Date().toISOString().slice(0, 10);
            const hasta = polForm.fecha_vencimiento_cobro || null;
            const { data: rango } = await supabase.from('pagos')
              .select('id, monto, cuota_num, fecha_vencimiento')
              .eq('poliza_id', polizaId)
              .gte('fecha_vencimiento', desde)
              .order('fecha_vencimiento');
            const enRango = (rango || []).filter(p =>
              p.cuota_num != null && (!hasta || (p.fecha_vencimiento && p.fecha_vencimiento <= hasta)));

            if (enRango.length > 0) {
              const n = enRango.length;
              const base = Math.floor(total / n * 100) / 100;
              for (let i = 0; i < n; i++) {
                const share = i === n - 1 ? Math.round((total - base * (n - 1)) * 100) / 100 : base;
                const nuevoMonto = Math.round(((parseFloat(enRango[i].monto) || 0) + share) * 100) / 100;
                await updateRow('pagos', enRango[i].id, { monto: nuevoMonto });
                await insertRow('poliza_endoso_ajustes', { endoso_id: endoso.id, pago_id: enRango[i].id, monto: share });
              }
            } else {
              await insertRow('pagos', {
                poliza_id: polizaId, monto: total, estado: 'pendiente',
                fecha_vencimiento: hasta || desde, metodo: polActual?.forma_pago || null,
                cuota_num: null, origen: 'endoso', endoso_id: endoso.id,
              });
            }
          }
          await reaplicarSacoPoliza(polizaId);
        }

        // Cancelación: la póliza queda cancelada (sale de Cobros/Morosidad). La devolución
        // al cliente la maneja la aseguradora; RU solo registra el clawback de comisión.
        if (esCancelacion) {
          await updateRow('polizas', polizaId, { estado: 'cancelada' });
        }

        // Prórroga de vigencia (fianza): extiende la vigencia de la póliza. La comisión
        // por la extensión se cobró como delta positivo (letra suelta) más arriba.
        if (tipoEndoso === 'prorroga' && polForm.nueva_fecha_vencimiento) {
          await updateRow('polizas', polizaId, { fecha_vencimiento: polForm.nueva_fecha_vencimiento });
        }

        // Marcar en la SOLICITUD los cambios recibidos como facturados (con su endoso),
        // dejando los demás pendientes. Se guarda la lista actualizada en cambios_json.
        const listaActualizada = (polForm.cambiosList || []).map((ch, i) =>
          idxSel.includes(i) ? { ...ch, estado: 'facturado', endoso_id: endoso?.id || null } : ch);
        const pendientesRestantes = listaActualizada.filter(ch => ch.estado !== 'facturado').length;

        // El endoso YA quedó registrado/aplicado en la póliza. La solicitud pasa a
        // "endoso_recibido" (NO convertida): el ciclo sigue hasta "Enviado al cliente",
        // que es cuando se cierra la tarea del CRM. Si quedan cambios pendientes, se
        // podrá volver a "Registrar" para recibir las otras partes.
        await updateRow('solicitudes', selected.id, {
          estado:'endoso_recibido',
          ...(listaActualizada.length ? { cambios_json: listaActualizada } : {}),
          actualizado_en: new Date().toISOString(),
        });
        await load(); closeModal();
        alert(esCancelacion
          ? `✓ Endoso de cancelación registrado en la póliza ${selected.poliza_endoso_id}. Falta enviarlo al cliente para cerrar la tarea.`
          : pendientesRestantes > 0
            ? `✓ Endoso registrado en la póliza ${selected.poliza_endoso_id} con ${idxSel.length} cambio(s). Quedan ${pendientesRestantes} pendiente(s): vuelve a "Registrar" cuando lleguen.`
            : `✓ Endoso registrado en la póliza ${selected.poliza_endoso_id}. Todos los cambios quedaron facturados. Falta enviarlo al cliente para cerrar la tarea.`);
      } catch(e) { showToast('error', traducirError(e)); }
      finally { setConverting(false); }
      return;
    }

    if (!polForm.numero)            return alert('El número de póliza es requerido.');
    if (!polForm.fecha_inicio)      return alert('La fecha de inicio es requerida.');
    if (!polForm.fecha_vencimiento) return alert('La fecha de vencimiento es requerida.');
    if (!polForm.aseguradora_id && !selected.aseguradora_id) return alert('Elige la aseguradora de la póliza.');
    // La prima (y demás datos financieros) puede haberse editado en el modal de creación.
    const primaVal = (polForm.prima !== '' && polForm.prima != null) ? polForm.prima : (selected.prima || selected.prima_estimada);
    if (!primaVal) return alert('La prima no está definida. Complétala antes de crear la póliza.');
    const impuestoVal       = polForm.impuesto !== '' && polForm.impuesto != null ? parseFloat(polForm.impuesto) : (selected.impuesto != null ? parseFloat(selected.impuesto) : 5);
    const comisionVal       = polForm.comision_ramo !== '' && polForm.comision_ramo != null ? parseFloat(polForm.comision_ramo) : (selected.comision_ramo != null ? parseFloat(selected.comision_ramo) : null);
    const formaPagoVal      = polForm.forma_pago || selected.forma_pago || null;
    const frecuenciaVal     = polForm.frecuencia_pago || selected.frecuencia_pago || 'mensual';
    const numCuotasVal      = polForm.num_cuotas != null && polForm.num_cuotas !== '' ? parseInt(polForm.num_cuotas) : (selected.num_cuotas || 1);
    if (!selected.es_fee && !selected.comision_ramo)
      return alert('La solicitud no tiene % de comisión del ramo. Edítala y agrega la comisión o márcala como Fee antes de crear la póliza.');
    try {
      setConverting(true);

      // Leer agentes y coberturas de la solicitud
      const [{data:pAgentes},{data:pCobs},{data:autoData}] = await Promise.all([
        supabase.from('solicitud_agentes').select('*, agentes(nombre,comision_nueva_agente,comision_renovacion_agente,comision_nueva_ru,comision_renovacion_ru)').eq('solicitud_id',selected.id),
        supabase.from('solicitud_coberturas').select('*').eq('solicitud_id',selected.id),
        supabase.from('solicitud_autos').select('*').eq('solicitud_id',selected.id).order('id'),
      ]);

      // Vigencia que se está renovando (solo aplica a renovaciones). Es la que enlaza la
      // póliza nueva con la vieja: si se pierde, la renovación queda huérfana (el CRM sigue
      // pidiendo renovar, Renovaciones no la cuenta y el saco no arrastra el saldo).
      const renovDeId = selected.tipo === 'renovacion' && polForm.poliza_anterior_id
        ? parseInt(polForm.poliza_anterior_id) : null;
      const asegFinalId = polForm.aseguradora_id ? parseInt(polForm.aseguradora_id)
        : (selected.aseguradora_id ? parseInt(selected.aseguradora_id) : null);

      // 1. Crear póliza
      const pol = await insertRow('polizas', {
        solicitud_id: selected.id,
        numero:           polForm.numero,
        cliente_id:       parseInt(selected.cliente_id),
        // La aseguradora sale del modal (puede haber cambiado en la renovación); si quedó
        // vacía, la de la solicitud.
        aseguradora_id:   asegFinalId,
        ramo_id:          parseInt(selected.ramo_id),
        ejecutivo_id:     selected.ejecutivo_id ? parseInt(selected.ejecutivo_id) : null,
        agente_id:        pAgentes?.length>0 ? parseInt(pAgentes[0].agente_id) : null,
        tipo:             selected.tipo || 'nueva',
        // Cómo la registra la COMPAÑÍA (nueva/renovación), separado del tipo interno.
        tipo_emision:     polForm.tipo_emision || (selected.tipo === 'renovacion' ? 'renovada' : 'nueva'),
        estado:           'activa',
        // Enlace de la renovación a la vigencia anterior. Dos columnas con roles distintos:
        // · renovacion_de_poliza_id = MAPA de renovación → se setea SIEMPRE (aunque cambie
        //   número o aseguradora); es lo que muestra "renovó de X".
        // · poliza_anterior_id = ARRASTRE de saco (cross-vigencia) → se setea SOLO si la
        //   aseguradora arrastra el saldo pendiente. Si emitió póliza NUEVA (no renovó), va
        //   NULL y el saco trata la póliza como independiente (regla de negocio de Ricardo).
        // La vigencia anterior sale de `polForm.poliza_anterior_id`: viene prellenada con la
        // póliza de la solicitud y el usuario puede corregirla en el modal. Antes se leía
        // directo de la solicitud, así que si el vínculo venía vacío la cadena se perdía en
        // silencio y no había forma de arreglarlo al convertir.
        renovacion_de_poliza_id: renovDeId,
        poliza_anterior_id:      (renovDeId && polForm.arrastra_saldo !== false) ? renovDeId : null,
        prima:            parseFloat(primaVal),
        impuesto:         impuestoVal,
        comision_ramo:    selected.es_fee ? 0 : comisionVal,
        es_fee:           selected.es_fee || false,
        monto_fee:        selected.es_fee && selected.monto_fee ? parseFloat(selected.monto_fee) : null,
        forma_pago:       formaPagoVal,
        frecuencia_pago:  frecuenciaVal,
        num_cuotas:       numCuotasVal,
        fecha_inicio:     polForm.fecha_inicio,
        fecha_vencimiento:polForm.fecha_vencimiento,
        fecha_emision:    polForm.fecha_emision || null,
        notas:            selected.notas || null,
        referido_id:      selected.referido_id != null ? parseInt(selected.referido_id) : null,
        referido_comision_pct: selected.referido_comision_pct != null ? parseFloat(selected.referido_comision_pct) : null,
        suma_asegurada:   null, deducible: null,
        // Candado: la póliza nace bloqueada — los cambios de riesgo van por endoso.
        bloqueada_edicion: true,
      });
      const polId = pol.id;

      // 2. Coberturas a NIVEL de póliza (las por unidad se crean con cada vehículo abajo).
      for (const c of (pCobs||[]).filter(c=>!c.solicitud_auto_id)) {
        await insertRow('poliza_coberturas', {
          poliza_id:polId, cobertura_id:c.cobertura_id||null,
          nombre:c.nombre||'', tipo_limite:c.tipo_limite||'limite',
          suma_asegurada:c.suma_asegurada||null, deducible:c.deducible||null, otros:c.otros||null,
        });
      }

      // 3. Agentes (sin comisiones — se generan al aplicar cada pago)
      for (let i=0;i<(pAgentes||[]).length;i++) {
        const ag = pAgentes[i];
        await insertRow('poliza_agentes',{poliza_id:polId, agente_id:parseInt(ag.agente_id), porcentaje:parseFloat(ag.porcentaje)||0});
      }

      // 4. Auto — se crean TODAS las unidades (flota) con sus coberturas por unidad.
      const unidadesConv = autoData || [];
      const cobsPorAutoConv = {};
      (pCobs||[]).filter(c=>c.solicitud_auto_id).forEach(c=>{ (cobsPorAutoConv[c.solicitud_auto_id]=cobsPorAutoConv[c.solicitud_auto_id]||[]).push(c); });
      for (const veh of unidadesConv) {
        const av = await insertRow('poliza_autos',{
          poliza_id:polId, anio:veh.anio||null, marca:veh.marca||null,
          modelo:veh.modelo||null, color:veh.color||null,
          tipo_vehiculo:veh.tipo_vehiculo||null, pasajeros:veh.pasajeros||null,
          no_motor:veh.no_motor||null, chasis:veh.chasis||null,
          chasis_igual_motor:veh.chasis_igual_motor??true,
          placa:veh.placa||null, placa_por_reportar:veh.placa_por_reportar??false,
          dueno_nombre: veh.dueno_nombre || null, dueno_cedula: veh.dueno_cedula || null,
        });
        for (const c of (cobsPorAutoConv[veh.id]||[])) {
          await insertRow('poliza_coberturas', {
            poliza_id:polId, poliza_auto_id: av?.id||null, cobertura_id: c.cobertura_id||null,
            nombre:c.nombre||'', tipo_limite:c.tipo_limite||'limite',
            suma_asegurada:c.suma_asegurada||null, deducible:c.deducible||null, otros:c.otros||null,
          });
        }
      }
      // Una sola tarea de placa si alguna unidad quedó "Por reportar".
      if (unidadesConv.some(v => v.placa_por_reportar)) {
        const venc = new Date(); venc.setDate(venc.getDate()+30);
        await insertRow('crm_gestiones',{
          tipo:'seguimiento', prioridad:'alta',
          titulo:`Actualizar placa — Póliza ${polForm.numero}`,
          descripcion:`Una o más unidades quedaron con placa "Por reportar". Solicitar registro vehicular al cliente.`,
          poliza_id:polId, cliente_id:parseInt(selected.cliente_id),
          estado:'abierto', fecha_vencimiento:venc.toISOString().slice(0,10),
        });
      }

      // 5. Plan de cuotas (con los valores que confirmó/editó el ejecutivo)
      if (primaVal && polForm.fecha_inicio) {
        const {cuotas} = calcularCuotas(primaVal, impuestoVal, frecuenciaVal, numCuotasVal, polForm.fecha_inicio);
        for (const c of cuotas) {
          await insertRow('pagos',{
            poliza_id:polId, monto:c.monto, estado:'pendiente',
            fecha_vencimiento:c.fecha, metodo:selected.forma_pago||null, cuota_num:c.num,
          });
        }
      }

      // 6. Subir documentos de póliza
      setUploadingConvert(true);
      try {
        const uploadDoc = async (file, tipo) => {
          const ext = file.name.split('.').pop();
          const path = `${selected.id}/${tipo}_${Date.now()}.${ext}`;
          const { error } = await supabase.storage.from('solicitud-archivos').upload(path, file, { upsert:true });
          if (!error) {
            const { data: { publicUrl } } = supabase.storage.from('solicitud-archivos').getPublicUrl(path);
            await insertRow('solicitud_archivos', { solicitud_id: selected.id, nombre: file.name, url: publicUrl, tipo, tamanio: file.size });
          }
        };
        if (convertFileFactura) await uploadDoc(convertFileFactura, 'factura');
        if (convertFileCond)    await uploadDoc(convertFileCond, 'condiciones');
      } finally { setUploadingConvert(false); }

      // 7. Marcar solicitud como convertida y guardar vínculo a la póliza
      await updateRow('solicitudes', selected.id, {
        estado:'convertida', poliza_id: polId, actualizado_en:new Date().toISOString(),
        // Si en el modal se vinculó (o corrigió) la vigencia anterior, la solicitud se queda
        // con ese vínculo: es el rastro de qué se renovó, y de él viven el CRM y Renovaciones.
        ...(selected.tipo === 'renovacion' && renovDeId !== (selected.poliza_endoso_id || null)
          ? { poliza_endoso_id: renovDeId } : {}),
        // Y la aseguradora con la que realmente se emitió (p. ej. la renovación pasó a otra).
        ...(asegFinalId && asegFinalId !== (selected.aseguradora_id ? parseInt(selected.aseguradora_id) : null)
          ? { aseguradora_id: asegFinalId } : {}),
      });
      // La solicitud ya es póliza: cierra su tarea de seguimiento del CRM ("recibir
      // póliza de aseguradora") en el acto, sin esperar la auditoría del CRM.
      // Incluye 'renovacion': esa tarea quedó 'en_proceso' cuando se generó la solicitud
      // desde el CRM, y al existir la póliza nueva ya no hay nada que renovar.
      await supabase.from('crm_gestiones')
        .update({ estado:'cerrado', actualizado_en:new Date().toISOString() })
        .eq('solicitud_id', selected.id).in('estado', ['abierto','en_proceso'])
        .in('tipo', ['seguimiento','cotizacion','renovacion']);
      // Si la renovación se solicitó DESDE Solicitudes (sin pasar por el CRM), la tarea no
      // está enlazada por solicitud_id: se cierra por la póliza que se estaba renovando.
      if (renovDeId) {
        await supabase.from('crm_gestiones')
          .update({ estado:'cerrado', actualizado_en:new Date().toISOString() })
          .eq('poliza_id', renovDeId).eq('tipo','renovacion')
          .in('estado', ['abierto','en_proceso']);
      }
      await load();
      closeModal();
      alert(`✓ Póliza ${polForm.numero} creada exitosamente.`);
    } catch(e){ showToast('error', traducirError(e)); }
    finally { setConverting(false); }
  };

  // ── Render deducible ──────────────────────────────────────────
  const renderDeducibleFields = (c, idx) => (
    <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid rgba(var(--silver-rgb),0.15)'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
        <input type="checkbox" id={`ded-${idx}`} checked={c.tiene_deducible}
          onChange={e=>updateCobertura(idx,'tiene_deducible',e.target.checked)}
          style={{accentColor:'var(--sky)',width:14,height:14,cursor:'pointer'}} />
        <label htmlFor={`ded-${idx}`} style={{fontSize:12,color:'var(--silver)',cursor:'pointer'}}>¿Lleva deducible?</label>
      </div>
      {c.tiene_deducible && (
        <div style={{paddingLeft:22}}>
          <select style={{width:'100%',padding:'5px 8px',fontSize:12,borderRadius:6,background:'rgba(var(--steel-rgb),0.5)',border:'1px solid rgba(var(--silver-rgb),0.2)',color:'var(--text)',fontFamily:'Outfit,sans-serif',marginBottom:8}}
            value={c.deducible_tipo} onChange={e=>updateCobertura(idx,'deducible_tipo',e.target.value)}>
            <option value="evento">Monto fijo por evento</option>
            <option value="persona_evento">Por persona y por evento</option>
            <option value="pct_minimo">% de la SA con mínimo</option>
            <option value="pct">% de la SA</option>
            <option value="personalizado">Estructura personalizada</option>
          </select>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {c.deducible_tipo==='evento' && <div style={{display:'flex',alignItems:'center',gap:6}}><span style={{fontSize:11,color:'var(--silver)'}}>Por evento $</span><input type="number" value={c.ded_por_evento} onChange={e=>updateCobertura(idx,'ded_por_evento',e.target.value)} style={inputSm} placeholder="0"/></div>}
            {c.deducible_tipo==='persona_evento' && <><div style={{display:'flex',alignItems:'center',gap:6}}><span style={{fontSize:11,color:'var(--silver)'}}>Por persona $</span><input type="number" value={c.ded_por_persona} onChange={e=>updateCobertura(idx,'ded_por_persona',e.target.value)} style={inputSm} placeholder="0"/></div><div style={{display:'flex',alignItems:'center',gap:6}}><span style={{fontSize:11,color:'var(--silver)'}}>Por evento $</span><input type="number" value={c.ded_por_evento} onChange={e=>updateCobertura(idx,'ded_por_evento',e.target.value)} style={inputSm} placeholder="0"/></div></>}
            {(c.deducible_tipo==='pct'||c.deducible_tipo==='pct_minimo') && <div style={{display:'flex',alignItems:'center',gap:6}}><span style={{fontSize:11,color:'var(--silver)'}}>%</span><input type="number" value={c.ded_pct} onChange={e=>updateCobertura(idx,'ded_pct',e.target.value)} style={inputSm} placeholder="0"/></div>}
            {c.deducible_tipo==='pct_minimo' && <div style={{display:'flex',alignItems:'center',gap:6}}><span style={{fontSize:11,color:'var(--silver)'}}>Mínimo $</span><input type="number" value={c.ded_minimo} onChange={e=>updateCobertura(idx,'ded_minimo',e.target.value)} style={inputSm} placeholder="0"/></div>}
            {c.deducible_tipo==='personalizado' && <input type="text" value={c.ded_personalizado} onChange={e=>updateCobertura(idx,'ded_personalizado',e.target.value)} placeholder="Ej: 10% del valor del siniestro" style={{...inputSm,width:260}}/>}
          </div>
          {formatDeducible(c) && <div style={{marginTop:6,fontSize:11,color:'var(--sky-light)',fontStyle:'italic'}}>→ {formatDeducible(c)}</div>}
        </div>
      )}
    </div>
  );

  // ── JSX ───────────────────────────────────────────────────────
  return (
    <div>
      {/* Toast de feedback */}
      {toast && (
        <div style={{
          position:'fixed', bottom:24, right:24, zIndex:9999,
          padding:'12px 20px', borderRadius:10, maxWidth:380,
          display:'flex', alignItems:'center', gap:10,
          background: toast.type === 'success' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
          border: `1px solid ${toast.type === 'success' ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
          color: toast.type === 'success' ? '#22c55e' : '#f87171',
          fontFamily:'Outfit,sans-serif', fontSize:13, fontWeight:600,
          boxShadow:'0 4px 20px rgba(0, 0, 0, 0.4)',
        }}>
          <span style={{fontSize:16}}>{toast.type === 'success' ? '✓' : '✕'}</span>
          <span>{toast.msg}</span>
          <button onClick={()=>setToast(null)} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'inherit',fontSize:16,lineHeight:1}}>×</button>
        </div>
      )}
      <div className="page-header">
        <div>
          <div className="page-title">Solicitudes</div>
          <div className="page-subtitle">{rows.length} registros</div>
        </div>
        {canEdit('solicitudes') && (
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-secondary" onClick={()=>openCreate('cotizacion')}><Plus size={14}/> Cotización</button>
            <button className="btn btn-primary" onClick={()=>openCreate('nueva')}><Plus size={14}/> Solicitud de Póliza</button>
            <button className="btn btn-secondary" onClick={()=>openCreate('endoso')}><Plus size={14}/> Endoso</button>
          </div>
        )}
      </div>

      {/* Tabs de tipo */}
      <div style={{display:'flex',gap:8,marginBottom:16,alignItems:'center'}}>
        <div style={{display:'flex',gap:4,background:'rgba(var(--navy-rgb),0.4)',borderRadius:10,padding:4}}>
          {[['','Todas'],['cotizacion','Cotizaciones'],['nueva','Pólizas Nuevas'],['endoso','Endosos'],['renovacion','Renovaciones']].map(([v,l])=>(
            <button key={v} onClick={()=>{ setShowCanceladas(false); setFilterTipo(v); }} style={{
              padding:'7px 14px',borderRadius:8,border:'none',cursor:'pointer',
              fontFamily:'Outfit,sans-serif',fontSize:13,fontWeight:600,
              background:!showCanceladas && filterTipo===v?'var(--sky)':'transparent',
              color:!showCanceladas && filterTipo===v?'#fff':'var(--silver)',
            }}>{l}</button>
          ))}
        </div>
        <button onClick={()=>{ setShowCanceladas(s=>!s); setFilterEstado(''); setFilterTipo(''); }} style={{
          padding:'7px 16px',borderRadius:10,border:'1px solid',cursor:'pointer',
          fontFamily:'Outfit,sans-serif',fontSize:13,fontWeight:600,
          background: showCanceladas ? 'rgba(239, 68, 68, 0.15)' : 'rgba(var(--navy-rgb),0.4)',
          color: showCanceladas ? '#f87171' : 'var(--silver)',
          borderColor: showCanceladas ? 'rgba(239, 68, 68, 0.4)' : 'rgba(var(--silver-rgb),0.15)',
        }}>Canceladas</button>
      </div>

      <div className="toolbar" style={{flexWrap:'wrap',gap:8}}>
        <PolizaSelect value={filterPoliza} onChange={setFilterPoliza} width={220} />
        <ClienteSelect value={filterCliente} onChange={setFilterCliente} placeholder="Filtrar por cliente..." width={220} />
        {!showCanceladas && <select className="form-select" style={{width:180}} value={filterEstado} onChange={e=>setFilterEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="enviada_aseg">Enviada a Aseguradora</option>
          <option value="propuesta_recibida">Propuesta Recibida</option>
          <option value="comparativo_enviado">Comparativo Enviado</option>
          <option value="poliza_recibida">Póliza Recibida</option>
          <option value="endoso_recibido">Endoso Recibido</option>
          <option value="enviado_cliente">Enviado al Cliente</option>
          <option value="convertida">Convertida</option>
          <option value="cerrada">Cerrada</option>
          <option value="aprobada">Aprobada</option>
          <option value="rechazada">Rechazada</option>
        </select>}
        <select className="form-select" style={{width:160}} value={filterAseguradora} onChange={e=>setFilterAseguradora(e.target.value)}>
          <option value="">Todas las aseguradoras</option>
          {aseguradoras.map(a=><option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <select className="form-select" style={{width:140}} value={filterRamo} onChange={e=>setFilterRamo(e.target.value)}>
          <option value="">Todos los ramos</option>
          {ramos.map(r=><option key={r.id} value={r.id}>{r.nombre}</option>)}
        </select>
        <select className="form-select" style={{width:150}} value={filterFechaTipo} onChange={e=>{ setFilterFechaTipo(e.target.value); setFilterFechaDesde(''); setFilterFechaHasta(''); }}>
          <option value="">Sin filtro de fecha</option>
          <option value="vigencia">Por vigencia</option>
          <option value="creacion">Por fecha creación</option>
        </select>
        {filterFechaTipo && (<>
          <input className="form-input" type="date" style={{width:140}} value={filterFechaDesde} onChange={e=>setFilterFechaDesde(e.target.value)} title="Desde"/>
          <input className="form-input" type="date" style={{width:140}} value={filterFechaHasta} onChange={e=>setFilterFechaHasta(e.target.value)} title="Hasta"/>
        </>)}
        {(filterAseguradora||filterRamo||filterFechaTipo) && (
          <button className="btn btn-secondary btn-sm" onClick={()=>{ setFilterAseguradora(''); setFilterRamo(''); setFilterFechaTipo(''); setFilterFechaDesde(''); setFilterFechaHasta(''); }}>
            ✕ Limpiar
          </button>
        )}
      </div>

      {(() => {
        const handleSort = (col) => {
          setSortCol(c => { if(c===col){ setSortDir(d=>d==='asc'?'desc':'asc'); return c; } setSortDir('asc'); return col; });
        };
        const sortIcon = (col) => sortCol===col
          ? <span style={{marginLeft:4,color:'var(--sky-light)'}}>{sortDir==='asc'?'↑':'↓'}</span>
          : <span style={{opacity:0.3,marginLeft:4}}>↕</span>;

        let rowsFiltrados = [...rows];
        if (filterAseguradora) rowsFiltrados = rowsFiltrados.filter(r=>String(r.aseguradora_id)===filterAseguradora);
        if (filterRamo)        rowsFiltrados = rowsFiltrados.filter(r=>String(r.ramo_id)===filterRamo);
        if (filterFechaTipo && filterFechaDesde) {
          const campo = filterFechaTipo==='vigencia' ? 'fecha_inicio' : 'creado_en';
          rowsFiltrados = rowsFiltrados.filter(r=>r[campo]&&r[campo].slice(0,10)>=filterFechaDesde);
        }
        if (filterFechaTipo && filterFechaHasta) {
          const campo = filterFechaTipo==='vigencia' ? 'fecha_inicio' : 'creado_en';
          rowsFiltrados = rowsFiltrados.filter(r=>r[campo]&&r[campo].slice(0,10)<=filterFechaHasta);
        }
        rowsFiltrados.sort((a,b)=>{
          let va=a[sortCol]??'', vb=b[sortCol]??'';
          if(typeof va==='string') va=va.toLowerCase();
          if(typeof vb==='string') vb=vb.toLowerCase();
          if(va<vb) return sortDir==='asc'?-1:1;
          if(va>vb) return sortDir==='asc'?1:-1;
          return 0;
        });

        // Búsqueda primero: la tabla no muestra solicitudes hasta aplicar un filtro.
        const hayFiltro = !!(filterPoliza || filterEstado || filterAseguradora || filterRamo
          || filterTipo || filterCliente || (filterFechaTipo && (filterFechaDesde || filterFechaHasta)));

        return (<>
        <div style={{fontSize:12,color:'var(--silver)',marginBottom:6,paddingLeft:2}}>
          {hayFiltro
            ? <>{rowsFiltrados.length} solicitud{rowsFiltrados.length!==1?'es':''}{rows.length!==rowsFiltrados.length?` (de ${rows.length})`:''}</>
            : 'Aplica un filtro para ver solicitudes'}
        </div>
      <div className="table-wrapper">
        <table>
          <thead><tr>
            {[['id','N° Sol.'],['tipo','Tipo'],['clienteNombre','Cliente'],['ramoNombre','Ramo'],
              ['prima','Prima'],['ejecutivoNombre','Ejecutivo'],['estado','Estado'],['polizaNumero','Póliza']
            ].map(([col,label])=>(
              <th key={col} onClick={()=>handleSort(col)} style={{cursor:'pointer',userSelect:'none',whiteSpace:'nowrap'}}>
                {label}{sortIcon(col)}
              </th>
            ))}
            <th>Acciones</th>
          </tr></thead>
          <tbody>
            {!hayFiltro ? (
              <tr><td colSpan={9}>
                <div className="empty-state">
                  <div className="empty-state-icon"><Search size={48}/></div>
                  <div className="empty-state-title">Busca una solicitud para verla</div>
                  <div>Filtra por cliente, póliza, tipo, estado, aseguradora, ramo o fecha. La lista no muestra todas las solicitudes de una vez.</div>
                </div>
              </td></tr>
            ) : rowsFiltrados.length===0 ? (
              <tr><td colSpan={9}>
                <div className="empty-state">
                  <div className="empty-state-icon"><CheckSquare size={48}/></div>
                  <div className="empty-state-title">Sin resultados</div>
                </div>
              </td></tr>
            ) : rowsFiltrados.map(r => {
              const est = getEstado(r.tipo, r.estado);
              const proxEstados = getProxEstados(r.tipo, r.estado);
              const puedeConvertir = (r.tipo==='cotizacion' && !['convertida','cerrada'].includes(r.estado))
                || (r.tipo==='nueva' && r.estado==='poliza_recibida')
                || (r.tipo==='nueva' && r.estado==='aprobada')
                // Endoso: "Registrar" solo mientras queden cambios sin facturar. Si el endoso
                // ya está totalmente facturado (endoso_recibido con todos los cambios en
                // 'facturado'), NO se muestra — evita re-facturar y duplicar la prima.
                // Misma condición que el botón "Registrar Endoso" de la vista.
                || (r.tipo==='endoso' && (r.estado==='enviada_aseg'
                    || (r.estado==='endoso_recibido' && cambiosDesde(r).some(ch=>ch.estado!=='facturado'))))
                || (r.tipo==='renovacion' && r.estado==='aprobada');
              return (
                <tr key={r.id}>
                  <td style={{fontWeight:700,color:'var(--sky)'}}>{numSol(r.id)}</td>
                  <td><span className={`badge ${TIPO_BADGE[r.tipo]||'badge-neutral'}`} style={{fontSize:11}}>{TIPOS_LABEL[r.tipo]||r.tipo}</span></td>
                  <td style={{fontWeight:600}}>{r.clienteNombre}</td>
                  <td className="text-silver">{r.ramoNombre}</td>
                  <td className="text-silver">{r.prima||r.prima_estimada ? fmt$(r.prima||r.prima_estimada) : '—'}</td>
                  <td className="text-silver">{r.ejecutivoNombre||'—'}</td>
                  <td><span className={`badge ${est.badge}`}>{est.label}</span></td>
                  <td>
                    {r.polizaNumero && r.polizaIdVinculada ? (
                      <button onClick={()=>openViewPoliza(r.polizaIdVinculada)}
                        style={{ background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:4,
                          color:'var(--sky-light)', fontWeight:700, fontSize:12, fontFamily:'Outfit,sans-serif', padding:0 }}>
                        {r.polizaNumero} <ExternalLink size={11}/>
                      </button>
                    ) : <span className="text-silver" style={{fontSize:11}}>—</span>}
                  </td>
                  <td>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                      <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>openView(r)} title="Ver"><Eye size={13}/></button>
                      {canEdit('solicitudes') && r.estado === 'cancelada' && (
                        <button className="btn btn-sm btn-secondary" style={{fontSize:11,color:'var(--text-success)',borderColor:'rgba(34, 197, 94, 0.4)'}}
                          onClick={()=>{ setReactivarModal(r); setReactivarReason(''); setReactivarConfirm(false); }}
                          title="Reactivar solicitud">
                          <RefreshCw size={12}/> Reactivar
                        </button>
                      )}
                      {canEdit('solicitudes') && r.estado!=='convertida' && r.estado!=='cerrada' && r.estado!=='cancelada' && <>
                        <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>openEdit(r)} title="Editar"><Edit2 size={13}/></button>
                        {proxEstados.length > 0 && (
                          <select className="form-select" style={{fontSize:11,padding:'2px 6px',height:28,width:'auto'}}
                            value="" onChange={e=>{
                              const val = e.target.value; if(!val) return;
                              // Para ENDOSO los pasos del ciclo van por el flujo guiado (igual que
                              // la vista), NO por cambio de estado directo — así la solicitud es
                              // UNA sola y corre el mismo ciclo desde donde se opere:
                              //  · "endoso_recibido" = registrar/facturar → abre el registro.
                              //  · "enviado_cliente" = paso final → EXIGE evidencia del correo y
                              //    bloquea si quedan cambios sin facturar (cierra la tarea del CRM).
                              if(r.tipo==='endoso' && val==='endoso_recibido'){ openConvert(r); return; }
                              if(r.tipo==='endoso' && val==='enviado_cliente'){
                                const pend = cambiosDesde(r).filter(ch=>ch.estado!=='facturado').length;
                                if(pend>0){ alert(`No puedes cerrar el endoso todavía: quedan ${pend} cambio(s) pendiente(s) de registrar/facturar.`); return; }
                                setSelected(r); setEnvioCliente({ modo:'file', texto:'', file:null });
                                return;
                              }
                              handleCambiarEstado(r, val);
                            }}>
                            <option value="">Avanzar →</option>
                            {proxEstados.map(e=><option key={e} value={e}>{getEstado(r.tipo,e).label}</option>)}
                          </select>
                        )}
                        {puedeConvertir && r.tipo!=='cotizacion' && (
                          <button className="btn btn-sm btn-primary" style={{fontSize:11,whiteSpace:'nowrap'}} onClick={()=>openConvert(r)}>
                            <ArrowRight size={12}/> {r.tipo==='endoso'?'Registrar':'Crear Póliza'}
                          </button>
                        )}
                        <button className="btn btn-icon btn-danger btn-sm" onClick={()=>handleDelete(r)} title="Eliminar"><Trash2 size={13}/></button>
                      </>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
        </>);
      })()}

      {/* ══ Modal Crear / Editar ══════════════════════════════════════ */}
      {(modal==='create'||modal==='edit') && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-lg" style={{maxWidth:900}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">{modal==='create'
                  ? ({cotizacion:'Nueva Cotización', nueva:'Nueva Solicitud de Póliza', endoso:'Nuevo Endoso', renovacion:'Nueva Renovación'}[form.tipo]||'Nueva Solicitud')
                  : `Editar ${numSol(selected?.id)}`}</div>
                <div className="text-silver text-sm">{form.tipo==='cotizacion'?'Solicitud de cotización a aseguradoras':form.tipo==='endoso'?'Modificación sobre póliza existente':'Expediente de emisión'}</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={closeModal}><X size={16}/></button>
            </div>
            <div className="modal-body" style={{maxHeight:'80vh',overflowY:'auto'}}>

              {/* Datos básicos */}
              <SectionTitle>DATOS BÁSICOS</SectionTitle>

              {/* ── RENOVACIÓN: referencia de la póliza anterior ── */}
              {form.tipo === 'renovacion' && renovInfo && (
                <div style={{marginBottom:20,padding:'14px 16px',background:'rgba(var(--sky-rgb),0.07)',borderRadius:10,border:'1px solid rgba(var(--sky-rgb),0.3)'}}>
                  <div style={{fontSize:12,fontWeight:700,color:'var(--sky-light)',letterSpacing:1,marginBottom:6}}>RENOVACIÓN DE PÓLIZA ANTERIOR</div>
                  <div style={{fontSize:13}}>
                    Póliza <strong>{renovInfo.numero || '—'}</strong> · {renovInfo.aseg || '—'} · vencía <strong>{fmtFecha(renovInfo.fv)||'—'}</strong>
                  </div>
                  <div style={{fontSize:11,color:'var(--silver)',marginTop:6}}>
                    Puedes renovar con la <strong>misma o con otra aseguradora</strong> — ajusta los datos abajo (aseguradora, prima, coberturas, etc.). El número de la nueva póliza se pone al crearla. El saldo pendiente de la vigencia anterior se sigue cobrando.
                  </div>
                </div>
              )}

              {/* ── ENDOSO: póliza al tope ── */}
              {form.tipo === 'endoso' && (
                <div style={{marginBottom:20,padding:'16px',background:'rgba(245, 158, 11, 0.07)',borderRadius:10,border:'1px solid rgba(245, 158, 11, 0.3)'}}>
                  <div style={{fontSize:12,fontWeight:700,color:'var(--text-warning)',letterSpacing:1,marginBottom:10}}>PÓLIZA A ENDOSAR *</div>
                  <SearchSelect
                    options={polizasEndosoOpts.map(p=>({
                      id: p.id,
                      nombre: `${p.numero} — ${p.cliente || ''}`,
                      meta: p.fecha_inicio || p.fecha_vencimiento ? `Vig: ${fmtFecha(p.fecha_inicio)||'—'} → ${fmtFecha(p.fecha_vencimiento)||'—'}` : '',
                    }))}
                    value={form.poliza_endoso_id}
                    onChange={handlePolizaEndosoChange}
                    placeholder="Buscar póliza por número o cliente..."
                  />
                  {form.poliza_endoso_id && (() => {
                    const pol = polizasEndosoOpts.find(p=>String(p.id)===String(form.poliza_endoso_id));
                    return pol ? (
                      <div style={{marginTop:8,fontSize:12,color:'var(--sky-light)'}}>
                        Vigencia: <strong>{fmtFecha(pol.fecha_inicio)||'—'}</strong> → <strong>{fmtFecha(pol.fecha_vencimiento)||'—'}</strong>
                      </div>
                    ) : null;
                  })()}
                  {form.poliza_endoso_id && (
                    <div style={{marginTop:14}}>
                      <label className="form-label">Descripción general del endoso</label>
                      <textarea className="form-textarea" rows={2} value={form.notas} onChange={e=>f('notas',e.target.value)}
                        placeholder="Resumen de lo que le pediste a la aseguradora (opcional; el detalle exacto va abajo)."/>
                    </div>
                  )}
                  {/* Varios cambios en la misma solicitud (Opción A). Se guardan en cambios_json
                      (lista) y se aplican todos a la póliza al facturar el endoso. */}
                  {form.poliza_endoso_id && (
                    <div style={{marginTop:14, paddingTop:14, borderTop:'1px dashed rgba(245, 158, 11, 0.3)'}}>
                      <label className="form-label" style={{color:'var(--text-warning)'}}>Cambios solicitados (puedes pedir varios)</label>
                      {endosoTipos.length === 0 ? (
                        <div style={{fontSize:11,color:'var(--silver)'}}>No hay tipos de endoso configurados. Agrégalos en Admin → Tipos de Endoso.</div>
                      ) : (() => {
                        // Los tipos de cambio se filtran por el RAMO de la PÓLIZA seleccionada
                        // (resuelto de la póliza, no del form, para que no se cuele el ramo de
                        // otra cosa): genéricos + los propios de ese ramo.
                        const polSel = polizasEndosoOpts.find(p=>String(p.id)===String(form.poliza_endoso_id));
                        const ramoEndoso = polSel?.ramo_id ?? (polizaEndosoActual && String(polizaEndosoActual.id)===String(form.poliza_endoso_id) ? polizaEndosoActual.ramo_id : form.ramo_id);
                        return (
                          <EndosoCambiosMulti tipos={endosoTipos} ramoId={ramoEndoso} polizaId={form.poliza_endoso_id}
                            value={form.cambiosList} onChange={list => f('cambiosList', list)} />
                        );
                      })()}
                      <div style={{fontSize:11,color:'var(--silver)',marginTop:6}}>
                        Cada cambio se aplica a la póliza al facturar. El detalle de dinero (diferencial de prima) se captura al facturar.
                      </div>
                    </div>
                  )}
                  {form.poliza_endoso_id && (
                    <div style={{marginTop:14, textAlign:'right'}}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={handlePrintEndosoFromForm}>
                        <Printer size={13}/> Imprimir solicitud para la aseguradora
                      </button>
                      {!selected?.id && <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>Guarda la solicitud primero para que el documento salga con su número.</div>}
                    </div>
                  )}
                </div>
              )}

              <div className="form-grid form-grid-3">
                <div className="form-group" style={{gridColumn:'span 2'}}>
                  <label className="form-label">Cliente *</label>
                  <div style={{display:'flex',gap:8}}>
                    <div style={{flex:1}}>
                      <ClienteSelect options={clientes} value={form.cliente_id} onChange={handleClienteChange}/>
                    </div>
                    <button type="button" className="btn btn-secondary btn-sm" style={{whiteSpace:'nowrap',flexShrink:0}}
                      onClick={()=>setClienteModal(true)} title="Crear cliente nuevo">
                      <Plus size={13}/> Nuevo
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Ejecutivo de Cuenta</label>
                  <select className="form-select" value={form.ejecutivo_id} onChange={e=>f('ejecutivo_id',e.target.value)}>
                    <option value="">Sin ejecutivo</option>
                    {ejecutivos.map(e=><option key={e.id} value={e.id}>{e.nombre}</option>)}
                  </select>
                  {ejecutivoCambiado && <div style={{display:'flex',alignItems:'center',gap:5,marginTop:5,fontSize:11,color:'var(--text-warning)'}}><AlertTriangle size={12}/>Cambiando ejecutivo del cliente</div>}
                </div>
                {form.tipo !== 'cotizacion' && (
                  <div className="form-group">
                    <label className="form-label">Aseguradora{form.tipo==='endoso'?' (auto-rellenada)':''}</label>
                    <SearchSelect options={aseguradoras} value={form.aseguradora_id} onChange={handleAsegChange} placeholder="Buscar aseguradora..."/>
                  </div>
                )}
                {(form.tipo === 'nueva' || form.tipo === 'renovacion') && (
                  <div className="form-group">
                    <label className="form-label">N° de póliza{form.tipo==='renovacion'?' (de la renovación)':''}</label>
                    <input className="form-input" value={form.numero_poliza||''} onChange={e=>f('numero_poliza', e.target.value)}
                      placeholder="Opcional aquí · requerido al crear la póliza"/>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Ramo *{form.tipo==='endoso'?' (auto-rellenado)':''}</label>
                  <SearchSelect options={ramos} value={form.ramo_id} onChange={handleRamoChange} placeholder="Buscar ramo..."/>
                </div>
                {subramos.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Subramo</label>
                    <select className="form-select" value={form.subramo_id} onChange={e=>handleSubramoChange(e.target.value)}>
                      <option value="">— Seleccionar subramo —</option>
                      {subramos.map(s=><option key={s.id} value={s.id}>{s.nombre}</option>)}
                    </select>
                    {form.subramo_id && polizaCoberturas.length > 0 && (
                      <div style={{fontSize:11,color:'var(--text-success)',marginTop:4}}>
                        ✓ {polizaCoberturas.filter(c=>c.incluida).length} coberturas cargadas ({polizaCoberturas.filter(c=>c.incluida && c.es_basica!==false).length} básicas)
                      </div>
                    )}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">
                    {form.tipo==='cotizacion' ? 'Prima estimada (opcional)' : form.tipo==='endoso' ? 'Diferencial de prima (sin imp.) ($)' : 'Prima *'}
                  </label>
                  <input className="form-input" type="number" step="0.01" value={form.prima} onChange={e=>f('prima',e.target.value)}
                    placeholder={form.tipo==='endoso'?'Ej: 40 (aumento) o -20 (reducción) — SIN impuesto':'0.00'}/>
                  {form.tipo==='endoso' && <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>Monto <strong>sin impuesto</strong>. Positivo = aumento · Negativo = reducción · 0 = sin cambio de prima</div>}
                </div>
                {form.tipo !== 'endoso' && <>
                  <div className="form-group">
                    <label className="form-label">% Impuesto</label>
                    <input className="form-input" type="number" step="0.01" value={form.impuesto} onChange={e=>f('impuesto',e.target.value)}/>
                  </div>
                  <div className="form-group" style={{display:'flex',alignItems:'flex-end'}}>
                    <div style={{fontSize:13,color:'var(--silver)',paddingBottom:8}}>
                      Total: <strong style={{color:'var(--sky-light)'}}>{fmt$(parseFloat(form.prima||0)*(1+parseFloat(form.impuesto||0)/100))}</strong>
                    </div>
                  </div>
                </>}
                {form.tipo !== 'endoso' && <>
                  <div className="form-group"><label className="form-label">Fecha Inicio</label><input className="form-input" type="date" value={form.fecha_inicio} onChange={e=>{f('fecha_inicio',e.target.value);f('fecha_vencimiento',sumarUnAnioISO(e.target.value));}}/></div>
                  <div className="form-group"><label className="form-label">Fecha Vencimiento</label><input className="form-input" type="date" value={form.fecha_vencimiento} onChange={e=>f('fecha_vencimiento',e.target.value)}/></div>
                  {form.tipo !== 'cotizacion' && <div className="form-group"><label className="form-label">Fecha Emisión</label><input className="form-input" type="date" value={form.fecha_emision} onChange={e=>f('fecha_emision',e.target.value)}/></div>}
                </>}
              </div>

              {/* Comisión — solo póliza nueva y renovación */}
              {(form.tipo==='nueva'||form.tipo==='renovacion') && <div style={{marginTop:24}}>
                <SectionTitle>COMISIÓN</SectionTitle>
                {/* Toggle Fee */}
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,padding:'10px 14px',background:'rgba(var(--navy-rgb),0.4)',borderRadius:8,border:'1px solid rgba(var(--silver-rgb),0.12)',width:'fit-content'}}>
                  <input type="checkbox" id="es-fee" checked={form.es_fee}
                    onChange={e=>{ f('es_fee',e.target.checked); if(e.target.checked){ f('comision_ramo',''); } }}
                    style={{accentColor:'var(--sky)',width:15,height:15,cursor:'pointer'}}/>
                  <label htmlFor="es-fee" style={{fontSize:13,fontWeight:600,cursor:'pointer'}}>
                    Esta póliza es por <span style={{color:'var(--sky-light)'}}>Fee</span> (no lleva comisión)
                  </label>
                </div>
                {form.es_fee ? (
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div className="form-group" style={{margin:0,width:220}}>
                      <label className="form-label">Monto del Fee * <span style={{color:'var(--silver)',fontWeight:400}}>(en $)</span></label>
                      <input className="form-input" type="number" step="0.01" value={form.monto_fee}
                        onChange={e=>f('monto_fee',e.target.value)} placeholder="0.00"/>
                    </div>
                    <div style={{marginTop:20,fontSize:12,color:'var(--sky-light)',padding:'8px 12px',background:'rgba(var(--sky-rgb),0.08)',borderRadius:8,border:'1px solid rgba(var(--sky-rgb),0.2)'}}>
                      La comisión se registra como Fee — no genera % sobre la prima.
                    </div>
                  </div>
                ) : (
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div className="form-group" style={{margin:0,width:200}}>
                      <label className="form-label">% Comisión del Ramo *</label>
                      <input className="form-input" type="number" step="0.01" value={form.comision_ramo}
                        onChange={e=>f('comision_ramo',e.target.value)} placeholder="0.00"
                        style={{borderColor: !form.comision_ramo ? 'rgba(239, 68, 68, 0.4)' : ''}}/>
                    </div>
                    {comisionRamoCambiada && (
                      <div style={{marginTop:20,display:'flex',alignItems:'center',gap:6,padding:'8px 12px',background:'rgba(245, 158, 11, 0.1)',borderRadius:8,border:'1px solid rgba(245, 158, 11, 0.3)',fontSize:12,color:'var(--text-warning)'}}>
                        <AlertTriangle size={14}/> Cambiando comisión estándar ({comisionRamoBase}%)
                      </div>
                    )}
                  </div>
                )}
              </div>}

              {/* Plan de Pago — solo póliza nueva y renovación */}
              {(form.tipo==='nueva'||form.tipo==='renovacion') && <div style={{marginTop:24}}>
                <SectionTitle>PLAN DE PAGO</SectionTitle>
                <div className="form-grid form-grid-3">
                  <div className="form-group">
                    <label className="form-label">Forma de Pago</label>
                    <select className="form-select" value={form.forma_pago} onChange={e=>f('forma_pago',e.target.value)}>
                      <option value="">Seleccionar...</option>
                      <option value="ACH">ACH</option>
                      <option value="TC">Tarjeta de Crédito</option>
                      <option value="Yappy a Aseguradora">Yappy a Aseguradora</option>
                      <option value="Aseguradora">Aseguradora (directo)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Frecuencia</label>
                    <select className="form-select" value={form.frecuencia_pago} onChange={e=>{const fr=e.target.value;f('frecuencia_pago',fr);if(fr==='anual')f('num_cuotas','1');}}>
                      {FRECUENCIAS.map(fr=><option key={fr.value} value={fr.value}>{fr.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Número de Cuotas</label>
                    <input className="form-input" type="number" min="1" disabled={form.frecuencia_pago==='anual'} value={form.num_cuotas} onChange={e=>f('num_cuotas',e.target.value)} placeholder="1"/>
                  </div>
                </div>
                {form.prima && form.fecha_inicio && (() => {
                  const {total,cuotas,mesesTotal} = calcularCuotas(form.prima,form.impuesto,form.frecuencia_pago,form.num_cuotas,form.fecha_inicio);
                  const ultima = cuotas[cuotas.length-1];
                  const excedePol = form.fecha_vencimiento && ultima?.fecha && ultima.fecha > form.fecha_vencimiento;
                  const excede12 = mesesTotal > 12;
                  return (
                    <div style={{marginTop:10,padding:'10px 14px',background:'rgba(var(--sky-rgb),0.08)',borderRadius:8,border:'1px solid rgba(var(--sky-rgb),0.2)',fontSize:12}}>
                      <div style={{color:'var(--sky-light)',fontWeight:600,marginBottom:4}}>
                        {cuotas.length} cuota{cuotas.length>1?'s':''} de {fmt$(cuotas[0]?.monto)}
                        {cuotas.length>1&&Math.abs(ultima.monto-cuotas[0].monto)>0.01&&` (última: ${fmt$(ultima.monto)})`}
                        {' · '}Total: {fmt$(total)}
                      </div>
                      <div style={{color:'var(--silver)'}}>Primera: {fmtFecha(cuotas[0]?.fecha)||'—'} · Última: {fmtFecha(ultima?.fecha)||'—'}</div>
                      {(excedePol||excede12) && <div style={{marginTop:6,display:'flex',alignItems:'center',gap:5,color:'var(--text-warning)'}}><AlertTriangle size={12}/>{excede12?'El plan supera 12 meses. ':''}{excedePol?'La última cuota excede el vencimiento.':''}</div>}
                    </div>
                  );
                })()}
              </div>}

              {/* Agentes — no aplica para endoso simple */}
              {form.tipo !== 'endoso' && <div style={{marginTop:24}}>
                <SectionTitle>AGENTES</SectionTitle>
                <div style={{display:'flex',gap:8,marginBottom:12}}>
                  <div style={{flex:1}}>
                    <SearchSelect options={agentesDisponibles} value={agenteSelector} onChange={setAgenteSelector} placeholder="Seleccionar agente..."/>
                  </div>
                  <button className="btn btn-secondary btn-sm" onClick={handleAgenteAdd} disabled={!agenteSelector} style={{whiteSpace:'nowrap'}}>
                    <Plus size={13}/> Agregar
                  </button>
                </div>
                {polizaAgentes.length>0 && (
                  <div style={{background:'rgba(var(--navy-rgb),0.4)',borderRadius:8,overflow:'hidden'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead>
                        <tr style={{borderBottom:'1px solid rgba(var(--silver-rgb),0.1)'}}>
                          <th style={thSm}>Agente</th>
                          <th style={{...thSm,textAlign:'center'}}>Tipo</th>
                          <th style={{...thSm,textAlign:'center'}}>% Comisión</th>
                          <th style={{...thSm,textAlign:'center'}}>Negociado</th>
                          <th style={{width:40}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {polizaAgentes.map((ag,idx)=>{
                          const difiere = ag.comision_negociada!=null && ag.porcentaje!=='' && parseFloat(ag.porcentaje)!==parseFloat(ag.comision_negociada);
                          return (
                            <tr key={idx} style={{borderBottom:'1px solid rgba(var(--silver-rgb),0.05)'}}>
                              <td style={{padding:'8px 12px',fontSize:13,fontWeight:600}}>{ag.nombre}</td>
                              <td style={{padding:'8px 12px',textAlign:'center'}}>
                                <button
                                  onClick={()=>setPolizaAgentes(prev=>prev.map((a,i)=>i===idx?{
                                    ...a,
                                    comision_manual:!a.comision_manual,
                                    porcentaje:!a.comision_manual?a.porcentaje:String(a.comision_negociada??a.porcentaje)
                                  }:a))}
                                  style={{fontSize:11,padding:'2px 8px',borderRadius:6,border:'1px solid',cursor:'pointer',
                                    background: ag.comision_manual?'rgba(var(--sky-rgb),0.2)':'transparent',
                                    color: ag.comision_manual?'var(--sky-light)':'var(--silver)',
                                    borderColor: ag.comision_manual?'var(--sky)':'rgba(var(--silver-rgb),0.3)'}}>
                                  {ag.comision_manual?'🔒 Manual':'General'}
                                </button>
                              </td>
                              <td style={{padding:'8px 12px',textAlign:'center'}}>
                                <input type="number" step="0.01" value={ag.porcentaje}
                                  disabled={!ag.comision_manual}
                                  onChange={e=>setPolizaAgentes(prev=>prev.map((a,i)=>i===idx?{...a,porcentaje:e.target.value}:a))}
                                  style={{width:80,textAlign:'center',...inputSm,opacity:ag.comision_manual?1:0.5}}/>
                              </td>
                              <td style={{padding:'8px 12px',textAlign:'center',fontSize:12,color:'var(--silver)'}}>
                                {ag.comision_negociada!=null?`${ag.comision_negociada}%`:'—'}
                                {difiere&&!ag.comision_manual&&<span title="Cambiando comisión negociada" style={{marginLeft:6,color:'var(--text-warning)'}}><AlertTriangle size={12}/></span>}
                              </td>
                              <td style={{padding:'8px 12px'}}>
                                <button className="btn btn-icon btn-danger btn-sm" onClick={()=>setPolizaAgentes(prev=>prev.filter((_,i)=>i!==idx))}><X size={12}/></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{padding:'10px 14px',borderTop:'1px solid rgba(var(--silver-rgb),0.1)',display:'flex',gap:20,fontSize:12}}>
                      <span style={{color:'var(--silver)'}}>Total agentes: <strong style={{color:'var(--text)'}}>{sumAgentePct.toFixed(2)}%</strong></span>
                      <span style={{color:'var(--silver)'}}>{abrev}: <strong style={{color:pctExcedido?'#ef4444':'#22c55e'}}>{pctRU.toFixed(2)}%</strong></span>
                      {pctExcedido&&<span style={{display:'flex',alignItems:'center',gap:4,color:'var(--text-danger)'}}><AlertTriangle size={12}/> Excede comisión del ramo</span>}
                    </div>
                  </div>
                )}
              </div>}

              {/* Referido (opcional) — no aplica para endoso simple */}
              {form.tipo !== 'endoso' && <div style={{marginTop:24}}>
                <SectionTitle>REFERIDO (opcional)</SectionTitle>
                <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
                  <div style={{flex:'2 1 240px'}}>
                    <label className="form-label">Referidor</label>
                    <SearchSelect
                      options={[{ id:'', nombre:'— Sin referido —' }, ...referidos]}
                      value={form.referido_id}
                      onChange={(val)=>{
                        if (!val) { f('referido_id',''); f('referido_comision_pct',''); return; }
                        f('referido_id', val);
                        const ref = referidos.find(r=>String(r.id)===String(val));
                        if (ref) {
                          const pct = form.tipo === 'renovacion'
                            ? ref.comision_renovacion_pct
                            : (ref.comision_nueva_pct ?? ref.comision_pct);
                          f('referido_comision_pct', pct != null ? String(pct) : '');
                        }
                      }}
                      placeholder="Buscar referido..."/>
                  </div>
                  <div className="form-group" style={{flex:'0 1 140px',margin:0}}>
                    <label className="form-label">% Referido</label>
                    <input className="form-input" type="number" step="0.01"
                      value={form.referido_comision_pct}
                      onChange={e=>f('referido_comision_pct', e.target.value)}
                      disabled={!form.referido_id}/>
                  </div>
                  {form.referido_id && form.referido_comision_pct!=='' && (
                    <div style={{flex:'1 1 180px',fontSize:12,color:'var(--silver)',paddingBottom:10}}>
                      Estimado: <strong style={{color:'var(--sky-light)'}}>
                        {fmt$(parseFloat(form.prima||0) * (parseFloat(form.referido_comision_pct)||0) / 100)}
                      </strong>
                      <span style={{marginLeft:6}}>({form.referido_comision_pct}% × prima)</span>
                    </div>
                  )}
                </div>
              </div>}

              {/* Coberturas — no aplica para endoso */}
              {form.tipo !== 'endoso' && <div style={{marginTop:24}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                  <SectionTitle style={{margin:0}}>COBERTURAS</SectionTitle>
                  <button className="btn btn-sm btn-secondary" onClick={()=>setShowAddCob(s=>!s)}><Plus size={12}/> Agregar cobertura</button>
                </div>
                {showAddCob && (
                  <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap'}}>
                    <div style={{flex:1,minWidth:220}}>
                      <SearchSelect options={coberturasCatalogo.filter(cc=>!polizaCoberturas.find(pc=>pc.cobertura_id===cc.id))}
                        value={cobSelector==='__custom__'?'':cobSelector}
                        onChange={val=>{ setCobSelector(val?String(val):''); setNuevaCobertura(''); }}
                        placeholder="Buscar cobertura…"
                        onCreate={()=>setCobSelector('__custom__')}
                        createLabel="Otra (personalizada)…" />
                    </div>
                    {cobSelector==='__custom__' && (
                      <input className="form-input" style={{flex:1,minWidth:200}} value={nuevaCobertura}
                        onChange={e=>setNuevaCobertura(e.target.value)}
                        onKeyDown={e=>e.key==='Enter'&&handleAddCobertura()} placeholder="Nombre de la cobertura…"/>
                    )}
                    <button className="btn btn-primary btn-sm" onClick={handleAddCobertura} disabled={!cobSelector}>Agregar</button>
                    <button className="btn btn-secondary btn-sm" onClick={()=>{setShowAddCob(false);setCobSelector('');setNuevaCobertura('');}}><X size={12}/></button>
                  </div>
                )}
                {polizaCoberturas.length===0 ? (
                  <div className="text-silver text-sm" style={{textAlign:'center',padding:'16px 0'}}>
                    {form.ramo_id?'Aún no agregas coberturas. Usa "Agregar cobertura" para buscarlas e incluirlas.':'Selecciona un ramo para agregar coberturas.'}
                  </div>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {polizaCoberturas.map((c,idx)=>(
                      <div key={idx} style={{background:'rgba(var(--navy-rgb),0.4)',borderRadius:8,border:'1px solid rgba(var(--sky-rgb),0.2)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px'}}>
                          <button onClick={()=>setPolizaCoberturas(prev=>prev.filter((_,i)=>i!==idx))} title="Quitar cobertura"
                            style={{background:'none',border:'none',cursor:'pointer',color:'var(--silver)',padding:2,flexShrink:0,display:'flex'}}>
                            <TrashIcon size={15}/>
                          </button>
                          <span style={{flex:1,display:'flex',flexDirection:'column',gap:2}}>
                            <span style={{fontSize:13,fontWeight:600}}>{c.nombre}{c.es_personalizada&&<span style={{fontSize:10,marginLeft:6,color:'var(--sky-light)'}}>personalizada</span>}</span>
                            <CoberturaDesc descripcion={c.descripcion} />
                          </span>
                          {isAuto && (() => {
                            const tipo = detectAutoTipo(c.nombre);
                            const ops = autoOpcionesPorTipo(autoLimiteOpciones, tipo);
                            if (!tipo || ops.length === 0) return null;
                            return (
                              <select
                                disabled={!c.incluida}
                                value={c.nivel_limite_id || ''}
                                onChange={e => {
                                  const op = ops.find(o => String(o.id) === e.target.value);
                                  updateCobertura(idx, 'nivel_limite_id', e.target.value);
                                  if (op) {
                                    updateCobertura(idx, 'suma_asegurada',
                                      op.limite_accidente != null ? String(op.limite_accidente)
                                      : op.limite_persona != null ? String(op.limite_persona) : '');
                                    updateCobertura(idx, 'otros', op.etiqueta || '');
                                  }
                                }}
                                style={{...inputSm,width:150,flexShrink:0}}>
                                <option value="">Nivel {tipo}...</option>
                                {ops.map(o => <option key={o.id} value={o.id}>{o.etiqueta}</option>)}
                              </select>
                            );
                          })()}
                          <div style={{display:'flex',borderRadius:6,overflow:'hidden',border:'1px solid rgba(var(--silver-rgb),0.2)',flexShrink:0}}>
                            {['limite','sub_limite'].map(t=>(
                              <button key={t} onClick={()=>updateCobertura(idx,'tipo_limite',t)} style={{padding:'3px 10px',fontSize:11,border:'none',cursor:'pointer',fontFamily:'Outfit,sans-serif',background:c.tipo_limite===t?'var(--sky)':'transparent',color:c.tipo_limite===t?'#fff':'var(--silver)'}}>
                                {t==='limite'?'Límite':'Sub-límite'}
                              </button>
                            ))}
                          </div>
                          <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                            <span style={{fontSize:11,color:'var(--silver)'}}>SA $</span>
                            <input type="number" step="0.01" value={c.suma_asegurada} disabled={!c.incluida} onChange={e=>updateCobertura(idx,'suma_asegurada',e.target.value)} placeholder="0.00" style={{width:110,...inputSm}}/>
                          </div>
                          <button onClick={()=>toggleCobExpanded(idx)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--silver)',padding:4,flexShrink:0}}>
                            {c.expanded?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
                          </button>
                        </div>
                        {c.expanded&&c.incluida&&(
                          <div style={{padding:'0 14px 12px 38px'}}>
                            {renderDeducibleFields(c,idx)}
                            <div style={{marginTop:10}}>
                              <input type="text" value={c.otros} onChange={e=>updateCobertura(idx,'otros',e.target.value)} placeholder="Notas / condiciones adicionales..." style={{width:'100%',...inputSm}}/>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>}

              {/* Vehículo(s) — no aplica para endoso. En emisión se pueden cargar varias unidades (flota). */}
              {isAuto && form.tipo !== 'endoso' && (
                <div style={{marginTop:24}}>
                  <SectionTitle>UNIDADES ASEGURADAS</SectionTitle>
                  {autoWarning && (
                    <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',marginBottom:12,background:'rgba(245, 158, 11, 0.1)',border:'1px solid rgba(245, 158, 11, 0.35)',borderRadius:8,fontSize:13,color:'var(--text-warning)'}}>
                      <AlertTriangle size={15} style={{flexShrink:0}}/>
                      <span>{autoWarning}</span>
                    </div>
                  )}
                  <div style={{fontSize:12,color:'var(--silver)',marginBottom:8}}>Agrega todas las unidades de la póliza. Si es flota, usa "Agregar vehículo".</div>
                  <VehiculoListInput value={autoList} onChange={setAutoList} onFieldBlur={checkAutoExistente} />
                </div>
              )}

              {/* Notas — para endoso se captura en el bloque de póliza */}
              {form.tipo !== 'endoso' && (
                <div className="form-group" style={{marginTop:20}}>
                  <label className="form-label">Notas</label>
                  <textarea className="form-textarea" value={form.notas} onChange={e=>f('notas',e.target.value)}
                    placeholder={form.tipo==='cotizacion'?'Condiciones especiales, restricciones, información del riesgo...':'Instrucciones, condiciones especiales...'}/>
                </div>
              )}

              {/* Documentos (solo en edición cuando ya tiene ID) */}
              {modal==='edit' && selected?.id && (
                <div style={{marginTop:24}}>
                  <SectionTitle>DOCUMENTOS</SectionTitle>
                  <DocManager
                    solicitudId={selected.id}
                    linkedClienteId={form.cliente_id ? parseInt(form.cliente_id) : null}
                    categoriasPermitidas={['solicitud','poliza','cliente_natural','cliente_juridico','otro']}
                    readOnly={!canEdit('solicitudes')}
                  />
                </div>
              )}
              {modal==='create' && (
                <div style={{marginTop:24}}>
                  <SectionTitle>DOCUMENTOS ADJUNTOS</SectionTitle>
                  <input ref={pendingFileRef} type="file" style={{display:'none'}}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) setPendingFiles(prev => [...prev, { file }]);
                      if (pendingFileRef.current) pendingFileRef.current.value = '';
                    }}
                  />
                  <button className="btn btn-secondary btn-sm" onClick={() => pendingFileRef.current?.click()} style={{marginBottom:8}}>
                    <Paperclip size={13}/> Adjuntar documento
                  </button>
                  {pendingFiles.length > 0 && (
                    <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:8}}>
                      {pendingFiles.map((pf, i) => (
                        <div key={i} style={{padding:'8px 12px',background:'rgba(var(--navy-rgb),0.4)',borderRadius:8,border:'1px solid rgba(var(--silver-rgb),0.1)'}}>
                          <div style={{display:'flex',alignItems:'center',gap:10}}>
                            <Paperclip size={13} style={{color:'var(--sky-light)',flexShrink:0}}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{pf.file.name}</div>
                              <div style={{fontSize:11,color:'var(--silver)'}}>{fmtBytes(pf.file.size)}</div>
                            </div>
                            <button className="btn btn-icon btn-danger btn-sm" onClick={() => setPendingFiles(prev => prev.filter((_,j)=>j!==i))}><X size={12}/></button>
                          </div>
                          <div style={{display:'flex',gap:8,marginTop:8}}>
                            <div style={{flex:1}}>
                              <SearchSelect
                                options={catalogoDocs.map(c=>({id:c.nombre, nombre:c.nombre}))}
                                value={pf.tipo_doc||''}
                                onChange={val=>setPendingFiles(prev=>prev.map((x,j)=>j===i?{...x,tipo_doc:val,otro_desc:val==='Otro'?(x.otro_desc||''):''}:x))}
                                placeholder="Tipo de documento..."
                              />
                            </div>
                            {pf.tipo_doc==='Otro' && (
                              <input className="form-input" style={{flex:1}} maxLength={15} placeholder="Descripción (máx 15)"
                                value={pf.otro_desc||''} onChange={e=>setPendingFiles(prev=>prev.map((x,j)=>j===i?{...x,otro_desc:e.target.value}:x))}/>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
              {modal==='edit' && (
                <button className="btn btn-secondary" onClick={handlePrintSlipFromEdit} title="Imprimir slip para enviar a aseguradora" style={{display:'flex',alignItems:'center',gap:6}}>
                  <Printer size={14}/> Imprimir Slip
                </button>
              )}
              {modal==='edit' && (
                <button className="btn btn-secondary" onClick={()=>handleSave('borrador')} disabled={saving} style={{display:'flex',alignItems:'center',gap:6}}>
                  <Save size={14}/> Guardar Borrador
                </button>
              )}
              <button className="btn btn-primary" onClick={()=>handleSave('borrador')} disabled={saving}>
                {saving ? 'Guardando...' : modal==='create'
                  ? ({cotizacion:'Crear Cotización', nueva:'Crear Solicitud de Póliza', endoso:'Crear Endoso', renovacion:'Crear Renovación'}[form.tipo]||'Crear')
                  : 'Guardar Cambios'}
              </button>
              {modal==='create' && form.tipo === 'endoso' && (
                <button className="btn btn-primary" style={{ background:'#22c55e' }} onClick={()=>handleSave('borrador', true)} disabled={saving}
                  title="Para un endoso que la aseguradora YA emitió: crea la solicitud y abre de una el registro (sin pasar por 'enviar a la aseguradora')">
                  {saving ? 'Guardando...' : '✓ Crear y registrar (ya recibido)'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal Ver ════════════════════════════════════════════════ */}
      {modal==='view' && selected && (() => {
        const est = getEstado(selected.tipo, selected.estado);
        const proxEstados = getProxEstados(selected.tipo, selected.estado);
        const convertida = ['convertida','cerrada'].includes(selected.estado);
        const esCotiz = selected.tipo === 'cotizacion';
        return (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-lg" style={{maxWidth:720}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div className="modal-title" style={{color:'var(--sky)'}}>{numSol(selected.id)}</div>
                  <span className={`badge ${TIPO_BADGE[selected.tipo]||'badge-neutral'}`} style={{fontSize:11}}>{TIPOS_LABEL[selected.tipo]||selected.tipo}</span>
                  <span className={`badge ${est.badge}`}>{est.label}</span>
                </div>
                <div className="text-silver text-sm">{selected.clienteNombre} · {selected.ramoNombre}</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={closeModal}><X size={16}/></button>
            </div>

            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              {/* Banner cancelada */}
              {selected.estado === 'cancelada' && (
                <div style={{background:'rgba(239, 68, 68, 0.08)',border:'1px solid rgba(239, 68, 68, 0.25)',borderRadius:8,padding:'10px 16px',marginBottom:16,display:'flex',gap:12,alignItems:'flex-start'}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:'var(--text-danger)',marginBottom:2}}>SOLICITUD CANCELADA</div>
                    <div style={{fontSize:13,color:'#e2e8f0'}}>{selected.motivo_cancelacion || 'Sin motivo registrado'}</div>
                  </div>
                </div>
              )}
              {/* Póliza a endosar (número + vigencia + N° de endoso) — solo endosos */}
              {selected.tipo === 'endoso' && viewPolizaEndoso && (
                <div style={{marginBottom:16,padding:'12px 14px',background:'rgba(245, 158, 11, 0.07)',borderRadius:10,border:'1px solid rgba(245, 158, 11, 0.3)'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--text-warning)',letterSpacing:1,marginBottom:8}}>PÓLIZA A ENDOSAR</div>
                  <div className="form-grid form-grid-3">
                    <div>
                      <div className="text-xs text-silver" style={{marginBottom:2,textTransform:'uppercase'}}>N° de póliza</div>
                      <div style={{fontSize:13,fontWeight:700,color:'var(--sky-light)'}}>{viewPolizaEndoso.numero||'—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-silver" style={{marginBottom:2,textTransform:'uppercase'}}>Vigencia</div>
                      <div style={{fontSize:13,fontWeight:500}}>{fmtFecha(viewPolizaEndoso.fecha_inicio)||'—'} → {fmtFecha(viewPolizaEndoso.fecha_vencimiento)||'—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-silver" style={{marginBottom:2,textTransform:'uppercase'}}>N° de endoso</div>
                      <div style={{fontSize:13,fontWeight:500}}>{selected.numero_poliza || <span style={{color:'var(--silver)'}}>— (se asigna al facturar)</span>}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Datos generales */}
              <div style={{fontSize:11,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:10}}>DATOS GENERALES</div>
              <div className="form-grid form-grid-3" style={{marginBottom:16}}>
                {[
                  ['Cliente',       selected.clienteNombre],
                  ['Ejecutivo',     selected.ejecutivoNombre],
                  ['Aseguradora',   selected.aseguradoraNombre],
                  ['Ramo',          selected.ramoNombre],
                  ['Prima',         selected.prima||selected.prima_estimada?fmt$(selected.prima||selected.prima_estimada):null],
                  ['Comisión',      selected.comision_ramo!=null&&!selected.es_fee?`${selected.comision_ramo}%`:selected.es_fee?`Fee ${selected.monto_fee?fmt$(selected.monto_fee):''}`:null],
                  ['Impuesto',      selected.impuesto!=null?`${selected.impuesto}%`:null],
                  ['Forma de Pago', selected.forma_pago],
                  ['Frecuencia',    selected.frecuencia_pago],
                  ['Cuotas',        selected.num_cuotas?String(selected.num_cuotas):null],
                  ['Fecha Inicio',  fmtFecha(selected.fecha_inicio)],
                  ['Fecha Venc.',   fmtFecha(selected.fecha_vencimiento)],
                ].map(([l,v])=>v?(
                  <div key={l}>
                    <div className="text-xs text-silver" style={{marginBottom:2,textTransform:'uppercase'}}>{l}</div>
                    <div style={{fontSize:13,fontWeight:500}}>{v}</div>
                  </div>
                ):null)}
              </div>

              {/* Agentes */}
              {viewAgentes.length > 0 && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:8}}>AGENTES</div>
                  <div style={{background:'rgba(var(--navy-rgb),0.4)',borderRadius:8,overflow:'hidden'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr style={{borderBottom:'1px solid rgba(var(--silver-rgb),0.1)'}}>
                        <th style={{padding:'8px 12px',textAlign:'left',fontSize:11,color:'var(--silver)',fontWeight:600}}>Agente</th>
                        <th style={{padding:'8px 12px',textAlign:'right',fontSize:11,color:'var(--silver)',fontWeight:600}}>% Comisión</th>
                      </tr></thead>
                      <tbody>
                        {viewAgentes.map((a,i)=>(
                          <tr key={i} style={{borderBottom:'1px solid rgba(var(--silver-rgb),0.05)'}}>
                            <td style={{padding:'8px 12px',fontSize:13,fontWeight:600}}>{a.agentes?.nombre}</td>
                            <td style={{padding:'8px 12px',textAlign:'right',fontSize:13,color:'var(--sky-light)'}}>{a.porcentaje}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Referido */}
              {selected.referido_id && (() => {
                const ref = referidos.find(r=>String(r.id)===String(selected.referido_id));
                const pct = selected.referido_comision_pct!=null ? parseFloat(selected.referido_comision_pct) : 0;
                const primaVal = selected.prima!=null ? selected.prima : selected.prima_estimada;
                const estimado = parseFloat(primaVal||0) * pct / 100;
                return (
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:8}}>REFERIDO</div>
                    <div style={{background:'rgba(var(--navy-rgb),0.4)',borderRadius:8,padding:'10px 14px',fontSize:13}}>
                      <strong>{ref?.nombre || `#${selected.referido_id}`}</strong>
                      <span style={{color:'var(--sky-light)',marginLeft:10}}>{pct}%</span>
                      <span style={{color:'var(--silver)',marginLeft:10}}>Estimado: {fmt$(estimado)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Partes */}
              {(selected.asegurado_nombre||selected.tomador_nombre||selected.beneficiario_nombre) && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:8}}>PARTES</div>
                  <div className="form-grid form-grid-3">
                    {selected.asegurado_nombre && <div>
                      <div className="text-xs text-silver" style={{marginBottom:2}}>ASEGURADO</div>
                      <div style={{fontSize:13,fontWeight:500}}>{selected.asegurado_nombre}</div>
                      {selected.asegurado_cedula && <div style={{fontSize:11,color:'var(--silver)'}}>{selected.asegurado_cedula}</div>}
                    </div>}
                    {selected.tomador_nombre && <div>
                      <div className="text-xs text-silver" style={{marginBottom:2}}>TOMADOR</div>
                      <div style={{fontSize:13,fontWeight:500}}>{selected.tomador_nombre}</div>
                      {selected.tomador_cedula && <div style={{fontSize:11,color:'var(--silver)'}}>{selected.tomador_cedula}</div>}
                    </div>}
                    {selected.beneficiario_nombre && <div>
                      <div className="text-xs text-silver" style={{marginBottom:2}}>BENEFICIARIO</div>
                      <BeneficiarioConfidencial nombre={selected.beneficiario_nombre} cedula={selected.beneficiario_cedula}
                        actualizadoEl={selected.beneficiarios_actualizado_el}
                        nameStyle={{fontSize:13,fontWeight:500}} subStyle={{fontSize:11,color:'var(--silver)'}} />
                    </div>}
                  </div>
                </div>
              )}

              {selected.descripcion_riesgo && (
                <div style={{padding:'10px 14px',background:'rgba(var(--steel-rgb),0.3)',borderRadius:8,marginBottom:12}}>
                  <div className="text-xs text-silver" style={{marginBottom:4}}>DESCRIPCIÓN DEL RIESGO</div>
                  <div style={{fontSize:13}}>{selected.descripcion_riesgo}</div>
                </div>
              )}
              {selected.tipo==='endoso' && (() => {
                const lista = cambiosDesde(selected);
                if (!lista.length) return null;
                return (
                  <div style={{padding:'10px 14px',background:'rgba(var(--steel-rgb),0.3)',borderRadius:8,marginBottom:16}}>
                    <style>{`.endoso-det{border-collapse:collapse;margin-top:4px;font-size:12px} .endoso-det td{padding:2px 10px 2px 0} .endoso-det .lbl{color:var(--silver);white-space:nowrap}`}</style>
                    <div className="text-xs text-silver" style={{marginBottom:8}}>CAMBIOS DEL ENDOSO</div>
                    <div style={{display:'flex',flexDirection:'column',gap:10}}>
                      {lista.map((ch, i) => {
                        const t = (endosoTipos||[]).find(x=>String(x.id)===String(ch.tipo_endoso_id));
                        const detalle = t?.target ? describirCambioHTML(t.target, ch.cambios) : '';
                        return (
                          <div key={i}>
                            <div style={{fontSize:13,fontWeight:600}}>{t?.nombre || 'Cambio'}
                              {t?.comportamiento==='cancelacion'?' · cancelación':t?.comportamiento==='prorroga'?' · prórroga':''}</div>
                            {detalle
                              ? <table className="endoso-det"><tbody dangerouslySetInnerHTML={{__html: detalle}} /></table>
                              : <div style={{fontSize:12,color:'var(--silver)'}}>Sin detalle capturado.</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              {selected.notas && (
                <div style={{padding:'10px 14px',background:'rgba(var(--steel-rgb),0.3)',borderRadius:8,marginBottom:16}}>
                  <div className="text-xs text-silver" style={{marginBottom:4}}>NOTAS</div>
                  <div style={{fontSize:13}}>{selected.notas}</div>
                </div>
              )}

              {/* ── Bien asegurado (Auto) ── */}
              {viewAutoData && (
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:12,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:10}}>DATOS DEL VEHÍCULO</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px 20px',background:'rgba(var(--steel-rgb),0.25)',borderRadius:8,padding:'14px 16px'}}>
                    {[
                      ['Año',         viewAutoData.anio],
                      ['Marca',       viewAutoData.marca],
                      ['Modelo',      viewAutoData.modelo],
                      ['Color',       viewAutoData.color],
                      ['Tipo',        viewAutoData.tipo_vehiculo],
                      ['Pasajeros',   viewAutoData.pasajeros],
                      ['No. Motor',   viewAutoData.no_motor],
                      ['Chasis',      viewAutoData.chasis_igual_motor ? viewAutoData.no_motor : viewAutoData.chasis],
                      ['Placa',       viewAutoData.placa_por_reportar ? 'Por reportar' : viewAutoData.placa],
                    ].filter(([,v])=>v!=null&&v!=='').map(([l,v])=>(
                      <div key={l}>
                        <div style={{fontSize:10,color:'var(--silver)',textTransform:'uppercase',marginBottom:2}}>{l}</div>
                        <div style={{fontSize:13,fontWeight:500}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Coberturas ── */}
              {!esCotiz && (
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:12,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:10}}>COBERTURAS</div>
                  {viewCoberturas.length === 0 && (
                    <div className="text-silver text-sm" style={{padding:'10px 0'}}>Sin coberturas registradas en la solicitud.</div>
                  )}
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {viewCoberturas.map((c,i)=>(
                      <div key={i} style={{padding:'10px 14px',background:'rgba(var(--steel-rgb),0.25)',borderRadius:8,border:'1px solid rgba(var(--sky-rgb),0.15)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                          <span style={{flex:1,fontSize:13,fontWeight:600}}>{c.nombre}</span>
                          {c.tipo_limite && (
                            <span style={{fontSize:10,padding:'2px 8px',borderRadius:4,background:'rgba(var(--sky-rgb),0.15)',color:'var(--sky-light)',fontWeight:600}}>
                              {c.tipo_limite === 'sub_limite' ? 'Sub-límite' : 'Límite'}
                            </span>
                          )}
                          {c.suma_asegurada != null && (
                            <span style={{fontSize:13,color:'var(--sky-light)',fontWeight:600}}>{fmt$(c.suma_asegurada)}</span>
                          )}
                        </div>
                        {c.deducible && (
                          <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>Deducible: {c.deducible}</div>
                        )}
                        {c.otros && (
                          <div style={{fontSize:11,color:'var(--silver)',marginTop:2}}>{c.otros}</div>
                        )}
                        {c._descripcion && (
                          <div style={{marginTop:4}}><CoberturaDesc descripcion={c._descripcion} /></div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Sección Cotización por Aseguradora ── */}
              {esCotiz && (
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:12,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:10}}>COTIZACIONES POR ASEGURADORA</div>

                  {/* Formulario agregar */}
                  {canEdit('solicitudes') && !convertida && (
                    <div style={{background:'rgba(var(--steel-rgb),0.3)',borderRadius:8,padding:'12px 14px',marginBottom:12}}>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 120px 1fr auto',gap:8,alignItems:'flex-end'}}>
                        <div className="form-group" style={{margin:0}}>
                          <label className="form-label">Aseguradora</label>
                          <select className="form-select" value={cotizAsegForm.aseguradora_id}
                            onChange={e=>setCotizAsegForm(f=>({...f,aseguradora_id:e.target.value}))}>
                            <option value="">— Seleccionar —</option>
                            {aseguradoras.filter(a=>!cotizAsegList.find(c=>c.aseguradora_id===a.id)).map(a=>(
                              <option key={a.id} value={a.id}>{a.nombre}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group" style={{margin:0}}>
                          <label className="form-label">Prima cotizada</label>
                          <input className="form-input" type="number" step="0.01" value={cotizAsegForm.prima_cotizada}
                            onChange={e=>setCotizAsegForm(f=>({...f,prima_cotizada:e.target.value}))} placeholder="0.00"/>
                        </div>
                        <div className="form-group" style={{margin:0}}>
                          <label className="form-label">Notas</label>
                          <input className="form-input" value={cotizAsegForm.notas}
                            onChange={e=>setCotizAsegForm(f=>({...f,notas:e.target.value}))} placeholder="Observaciones..."/>
                        </div>
                        <button className="btn btn-primary btn-sm" onClick={saveCotizAseg} disabled={!cotizAsegForm.aseguradora_id||savingCotizAseg}>
                          <Plus size={13}/> Agregar
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Lista de cotizaciones por aseg */}
                  {cotizAsegList.length === 0
                    ? <div style={{fontSize:12,color:'var(--silver)',textAlign:'center',padding:'16px 0'}}>Sin aseguradoras cotizadas aún</div>
                    : (
                      <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        {cotizAsegList.map(item=>(
                          <div key={item.id} style={{
                            padding:'12px 14px',borderRadius:8,
                            background: item.seleccionada ? 'rgba(34, 197, 94, 0.08)' : 'rgba(var(--steel-rgb),0.3)',
                            border: `1px solid ${item.seleccionada ? 'rgba(34,197,94,0.3)' : 'rgba(143,168,192,0.1)'}`,
                          }}>
                            <div style={{display:'flex',alignItems:'center',gap:12}}>
                              <div style={{flex:1}}>
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                  <span style={{fontWeight:600,fontSize:14}}>{item.aseguradoras?.nombre}</span>
                                  {item.seleccionada && <span className="badge badge-success" style={{fontSize:10}}>Seleccionada</span>}
                                </div>
                                {item.prima_cotizada && <div style={{fontSize:13,color:'var(--sky-light)',marginTop:2}}>{fmt$(item.prima_cotizada)}</div>}
                                {item.notas && <div style={{fontSize:12,color:'var(--silver)',marginTop:2}}>{item.notas}</div>}
                              </div>
                              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                                {/* Subir archivo */}
                                <input type="file" style={{display:'none'}} id={`cotiz-file-${item.id}`}
                                  onChange={e=>{ if(e.target.files?.[0]) uploadCotizAsegFile(e.target.files[0], item.id); e.target.value=''; }}/>
                                {item.archivo_url
                                  ? <a href="#" onClick={(e)=>{ e.preventDefault(); abrirDocumento(item.archivo_url); }} className="btn btn-sm btn-secondary"><Download size={12}/> {item.archivo_nombre?.slice(0,15)||'Ver'}</a>
                                  : canEdit('solicitudes') && !convertida && (
                                    <label htmlFor={`cotiz-file-${item.id}`} className="btn btn-sm btn-secondary" style={{cursor:'pointer'}}>
                                      <Paperclip size={12}/> Subir
                                    </label>
                                  )
                                }
                                {canEdit('solicitudes') && !convertida && (
                                  <button className="btn btn-sm btn-secondary" style={{fontSize:11}}
                                    onClick={()=>toggleSeleccionada(item)}
                                    title={item.seleccionada ? 'Desmarcar' : 'Marcar como seleccionada por el cliente'}>
                                    {item.seleccionada ? '★' : '☆'} {item.seleccionada ? 'Elegida' : 'Elegir'}
                                  </button>
                                )}
                                {canEdit('solicitudes') && !convertida && (
                                  <button className="btn btn-icon btn-danger btn-sm" onClick={()=>deleteCotizAseg(item.id)}><Trash2 size={12}/></button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  }

                  {/* Comparativo */}
                  <div style={{marginTop:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:8}}>COMPARATIVO ENVIADO AL CLIENTE</div>
                    <input ref={comparativoFileRef} type="file" style={{display:'none'}}
                      onChange={e=>setComparativoFile(e.target.files?.[0]||null)}/>
                    {selected.comparativo_url ? (
                      <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'rgba(34, 197, 94, 0.08)',borderRadius:8,border:'1px solid rgba(34, 197, 94, 0.2)'}}>
                        <Paperclip size={13} style={{color:'var(--text-success)'}}/>
                        <span style={{flex:1,fontSize:13}}>{selected.comparativo_nombre||'Comparativo'}</span>
                        <a href="#" onClick={(e)=>{ e.preventDefault(); abrirDocumento(selected.comparativo_url); }} className="btn btn-sm btn-secondary"><Download size={12}/> Abrir</a>
                      </div>
                    ) : canEdit('solicitudes') && !convertida && (
                      comparativoFile ? (
                        <div style={{display:'flex',gap:8,alignItems:'center'}}>
                          <span style={{fontSize:13,color:'var(--text-success)'}}>{comparativoFile.name}</span>
                          <button className="btn btn-primary btn-sm" onClick={()=>uploadComparativo(comparativoFile)} disabled={uploadingComparativo}>
                            {uploadingComparativo ? 'Subiendo...' : 'Guardar'}
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={()=>setComparativoFile(null)}>Cancelar</button>
                        </div>
                      ) : (
                        <button className="btn btn-secondary btn-sm" onClick={()=>comparativoFileRef.current?.click()}>
                          <Paperclip size={13}/> Subir comparativo
                        </button>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Documentos adjuntos */}
              <div style={{marginTop:16}}>
                <div style={{fontSize:12,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:10}}>DOCUMENTOS</div>
                {archivos.length > 0 && (
                  <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:10}}>
                    {archivos.map(a => (
                      <a key={a.id} href="#" onClick={(e)=>{ e.preventDefault(); abrirDocumento(a.url); }}
                        style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'rgba(var(--sky-rgb),0.08)',borderRadius:8,border:'1px solid rgba(var(--sky-rgb),0.18)',fontSize:13,color:'var(--sky-light)',textDecoration:'none'}}>
                        <Paperclip size={13}/> {a.nombre}
                        {a.tipo_doc ? <span style={{color:'var(--silver)',marginLeft:6}}>· {a.tipo_doc}</span> : null}
                      </a>
                    ))}
                  </div>
                )}
                <DocManager
                  solicitudId={selected.id}
                  linkedClienteId={selected.cliente_id ? parseInt(selected.cliente_id) : null}
                  categoriasPermitidas={['solicitud','poliza','cliente_natural','cliente_juridico','otro']}
                  readOnly={true}
                />
              </div>

              {/* Avanzar estado */}
              {canEdit('solicitudes') && !convertida && proxEstados.length > 0 && (
                <div style={{marginTop:20,padding:'12px 14px',background:'rgba(var(--steel-rgb),0.25)',borderRadius:8,display:'flex',alignItems:'center',gap:12}}>
                  <span style={{fontSize:12,color:'var(--silver)'}}>Avanzar estado:</span>
                  {proxEstados.map(e=>{
                    // Marcar "Endoso recibido de la aseguradora" = REGISTRAR/FACTURAR en ese
                    // mismo paso (no hay paso aparte): abre el registro directo, que aplica los
                    // cambios y deja la solicitud en endoso_recibido. "Enviado al Cliente" es el
                    // paso final (ya registrado, se le pasa al cliente y cierra la tarea).
                    // IMPORTANTE: NO usar closeModal() aquí — si se abrió desde la póliza,
                    // closeModal navega de vuelta a Pólizas y mata el modal de registro.
                    const esFacturarEndoso = selected.tipo === 'endoso' && e === 'endoso_recibido';
                    const esEnvioClienteEndoso = selected.tipo === 'endoso' && e === 'enviado_cliente';
                    return (
                    <button key={e} className={`btn btn-sm ${esFacturarEndoso ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={async()=>{
                        if (esFacturarEndoso) { openConvert(selected); return; }
                        if (esEnvioClienteEndoso) {
                          // Paso final: exige evidencia del correo al cliente. Y no se puede
                          // cerrar si quedan cambios sin facturar (facturación por partes).
                          const pend = cambiosDesde(selected).filter(ch=>ch.estado!=='facturado').length;
                          if (pend > 0) { alert(`No puedes cerrar el endoso todavía: quedan ${pend} cambio(s) pendiente(s) de registrar/facturar.`); return; }
                          setEnvioCliente({ modo:'file', texto:'', file:null });
                          return;
                        }
                        await handleCambiarEstado(selected,e); setSelected(s=>({...s,estado:e}));
                      }}>
                      <ArrowRight size={12}/> {esFacturarEndoso ? 'Endoso recibido de la aseguradora' : getEstado(selected.tipo,e).label}
                    </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-footer">
              {sessionStorage.getItem('backFrom') === 'polizas' && (
                <button className="btn btn-secondary btn-sm" style={{marginRight:'auto'}}
                  onClick={closeModal}>
                  ← Volver a Póliza
                </button>
              )}
              {sessionStorage.getItem('backFrom') === 'crm' && (
                <button className="btn btn-secondary btn-sm" style={{marginRight:'auto'}}
                  onClick={()=>{ sessionStorage.removeItem('backFrom'); closeModal(); navigate('/crm'); }}>
                  ← Volver al CRM
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={handlePrintSlip} title="Imprimir slip para enviar a aseguradora">
                <Printer size={13}/> Imprimir Slip
              </button>
              {canEdit('solicitudes') && !convertida && (
                <button className="btn btn-secondary btn-sm" onClick={()=>{closeModal();setTimeout(()=>openEdit(selected),50);}}>
                  <Edit2 size={13}/> Editar
                </button>
              )}
              {/* Convertir cotización → solicitud póliza */}
              {esCotiz && canEdit('solicitudes') && !convertida && (
                <button className="btn btn-primary" onClick={handleConvertCotizToSol}>
                  <ArrowRight size={14}/> Convertir a Solicitud de Póliza
                </button>
              )}
              {/* Convertir solicitud póliza → póliza activa */}
              {!esCotiz && canEdit('solicitudes') && (selected.estado==='aprobada'||selected.estado==='poliza_recibida'||(selected.tipo==='endoso'&&(selected.estado==='enviada_aseg'
                // Tras registrar (endoso_recibido) el botón solo reaparece si quedan PARTES
                // sin facturar; si todo está facturado ya no hay nada que registrar.
                // Igual en enviado_cliente (rescate de solicitudes que avanzaron sin registrar).
                || (['endoso_recibido','enviado_cliente'].includes(selected.estado) && cambiosDesde(selected).some(ch=>ch.estado!=='facturado'))))) && (
                // Transición directa view → convert (sin closeModal: si se abrió desde la
                // póliza, closeModal navega de vuelta y mata el modal de registro).
                <button className="btn btn-primary" onClick={()=>openConvert(selected)}>
                  <ArrowRight size={14}/> {selected.tipo==='endoso'?'Registrar Endoso':'Crear Póliza'}
                </button>
              )}
              <button className="btn btn-secondary" onClick={closeModal}>Cerrar</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ══ Modal Convertir a Póliza ══════════════════════════════════ */}
      {modal==='convert' && selected && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-lg" style={{maxWidth:600}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">{selected.tipo === 'endoso' ? 'Registrar Endoso' : 'Crear Póliza'}</div>
                <div className="text-silver text-sm">{numSol(selected.id)} · {selected.ramoNombre} · {selected.clienteNombre}</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={closeModal}><X size={16}/></button>
            </div>
            <div className="modal-body">

              {/* ── FLUJO ENDOSO ── */}
              {selected.tipo === 'endoso' ? (
                <div>
                  <div style={{background:'rgba(245, 158, 11, 0.08)',borderRadius:8,padding:'12px 16px',marginBottom:20,border:'1px solid rgba(245, 158, 11, 0.2)'}}>
                    <div style={{fontSize:12,color:'var(--text-warning)',marginBottom:4,fontWeight:700}}>ENDOSO SOBRE PÓLIZA EXISTENTE</div>
                    <div style={{fontSize:13}}>Prima actual del endoso: <strong style={{color:'var(--sky-light)'}}>{fmt$(selected.prima||0)}</strong></div>
                  </div>
                  <div className="form-grid form-grid-2">
                    {polForm.tipo_endoso_nombre && (
                      <div className="form-group" style={{gridColumn:'span 2'}}>
                        <div style={{fontSize:12,color:'var(--silver)'}}>Tipo solicitado: <strong style={{color:'var(--sky-light)'}}>{polForm.tipo_endoso_nombre}</strong></div>
                      </div>
                    )}
                    <div className="form-group" style={{gridColumn:'span 2'}}>
                      <label className="form-label">Comportamiento</label>
                      <select className="form-select" value={polForm.tipo_endoso||'normal'} onChange={e=>pf('tipo_endoso',e.target.value)}>
                        <option value="normal">Normal (cambio / movimiento de prima)</option>
                        <option value="prorroga">Prórroga de vigencia (fianza)</option>
                        <option value="cancelacion">Cancelación de póliza</option>
                      </select>
                      <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>Se prellena según el tipo elegido en la solicitud; ajústalo solo si hace falta.</div>
                    </div>
                    <div className="form-group" style={{gridColumn:'span 2'}}>
                      <label className="form-label">Razón del Endoso *</label>
                      <textarea className="form-textarea" rows={3} value={polForm.descripcion_endoso||''} onChange={e=>pf('descripcion_endoso',e.target.value)} placeholder="Describe el motivo del endoso (cambio de placa, aumento de cobertura, cambio de datos, etc.)"/>
                    </div>
                    {/* Facturación por PARTES: marca los cambios que llegaron en ESTE endoso.
                        Los facturados salen ✓; los demás quedan pendientes para otra parte. */}
                    {(polForm.cambiosList || []).length > 0 && selected?.poliza_endoso_id && (
                      <div className="form-group" style={{gridColumn:'span 2', paddingTop:10, borderTop:'1px dashed rgba(var(--sky-rgb),0.3)'}}>
                        <label className="form-label" style={{color:'var(--sky-light)'}}>Cambios recibidos en este endoso (marca los que llegaron)</label>
                        {polForm.cambiosList.map((ch, idx) => {
                          const facturado = ch.estado === 'facturado';
                          const tCat = endosoTipos.find(t => String(t.id) === String(ch.tipo_endoso_id));
                          const target = tCat?.target;
                          const checked = recibidosSel.includes(idx);
                          return (
                            <div key={idx} style={{ border:'1px solid rgba(var(--silver-rgb),0.15)', borderRadius:8, padding:'10px 12px', marginBottom:8,
                              background: facturado ? 'rgba(34, 197, 94, 0.06)' : 'rgba(var(--navy-rgb),0.35)' }}>
                              {facturado ? (
                                <div style={{ fontSize:13, color:'var(--text-success)', fontWeight:600 }}>✓ {tCat?.nombre || 'Cambio'} — ya facturado</div>
                              ) : (
                                <>
                                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600 }}>
                                    <input type="checkbox" checked={checked} style={{ accentColor:'var(--sky)', width:15, height:15 }}
                                      onChange={() => setRecibidosSel(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx])} />
                                    {tCat?.nombre || 'Cambio'} <span style={{ color:'var(--silver)', fontWeight:400 }}>— recibido en este endoso</span>
                                  </label>
                                  {checked && target && targetAplica(target) && (
                                    <div style={{ marginTop:8 }}>
                                      <EndosoCambioForm target={target} polizaId={selected.poliza_endoso_id}
                                        value={ch.cambios || {}}
                                        onChange={c => pf('cambiosList', polForm.cambiosList.map((x, i) => i === idx ? { ...x, cambios: c } : x))} />
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })}
                        <div style={{ fontSize:11, color:'var(--silver)', marginTop:2 }}>
                          Los que no marques quedan <strong>pendientes</strong>: podrás registrarlos en otra parte cuando lleguen.
                        </div>
                      </div>
                    )}
                    <div className="form-group" style={{gridColumn:'span 2'}}>
                      <label className="form-label">No. de Endoso / Factura</label>
                      <input className="form-input" value={polForm.numero_endoso||''} onChange={e=>pf('numero_endoso',e.target.value)} placeholder="N° que asigna la aseguradora al endoso"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{polForm.tipo_endoso==='cancelacion' ? 'Fecha de cancelación' : 'Fecha del Endoso'}</label>
                      <input className="form-input" type="date" value={polForm.fecha_endoso||''} onChange={e=>pf('fecha_endoso',e.target.value)}/>
                    </div>

                    {polForm.tipo_endoso==='cancelacion' ? (
                      <>
                        <div className="form-group">
                          <label className="form-label">Prima devuelta ($)</label>
                          <input className="form-input" type="number" step="0.01" min="0" value={polForm.prima_devuelta||''} onChange={e=>pf('prima_devuelta',e.target.value)} placeholder="Monto que la aseguradora devuelve al cliente"/>
                          <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>Monto sin impuesto. {abrev} no devuelve dinero — la aseguradora lo maneja. Genera comisión negativa (clawback).</div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Método de devolución</label>
                          <select className="form-select" value={polForm.metodo_cancelacion||'prorrata'} onChange={e=>pf('metodo_cancelacion',e.target.value)}>
                            <option value="prorrata">Prorrata (sin penalización)</option>
                            <option value="short_rate">Tasa corta (con penalización)</option>
                          </select>
                        </div>
                        {polForm.metodo_cancelacion==='short_rate' && (
                          <div className="form-group">
                            <label className="form-label">Penalización (%) — opcional</label>
                            <input className="form-input" type="number" step="0.01" value={polForm.factor_penalizacion||''} onChange={e=>pf('factor_penalizacion',e.target.value)} placeholder="Ej: 10 (informativo)"/>
                            <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>Solo informativo: la prima devuelta ya viene calculada de la tabla de la aseguradora.</div>
                          </div>
                        )}
                        <div className="form-group" style={{gridColumn:'span 2'}}>
                          <div style={{fontSize:12,color:'var(--text-danger)',background:'rgba(239, 68, 68, 0.08)',border:'1px solid rgba(239, 68, 68, 0.2)',borderRadius:8,padding:'10px 12px'}}>
                            La póliza quedará <strong>cancelada</strong>: saldrá de Cobros/Morosidad y dejará de devengar deuda futura.
                          </div>
                        </div>
                      </>
                    ) : polForm.cambio_mensualidad ? null : (
                      <div className="form-group">
                        <label className="form-label">{polForm.tipo_endoso==='prorroga' ? 'Comisión por la prórroga (sin imp.) ($)' : 'Cambio de Prima (sin imp.) ($)'}</label>
                        <input className="form-input" type="number" step="0.01" value={polForm.delta_prima||''} onChange={e=>pf('delta_prima',e.target.value)} placeholder={polForm.tipo_endoso==='prorroga' ? 'monto afianzado × tarifa × período (sin imp.)' : 'Ej: 40 (aumento) o -20 (reducción) — SIN impuesto'}/>
                        <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>{polForm.tipo_endoso==='prorroga' ? 'Comisión de garantía por el período de extensión (sin impuesto). El sistema le suma el impuesto al diluirla.' : 'Monto SIN impuesto (el sistema agrega el impuesto al aplicarla). Positivo = aumento · Negativo = reducción · 0 = sin cambio'}</div>
                      </div>
                    )}

                    {/* Vida/Salud individual: cambiar la MENSUALIDAD completa a un monto nuevo (como el botón directo de la póliza) */}
                    {polForm.tipo_endoso==='normal' && !endosoDeclarativa && /salud|m[eé]dic|vida/i.test(selected.ramoNombre||'') && (
                      <div className="form-group" style={{gridColumn:'span 2', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8, padding:'10px 12px'}}>
                        <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:12.5,fontWeight:600}}>
                          <input type="checkbox" checked={!!polForm.cambio_mensualidad} onChange={e=>pf('cambio_mensualidad', e.target.checked)} style={{accentColor:'var(--sky)',width:15,height:15}}/>
                          Es un <span style={{color:'var(--sky-light)'}}>cambio de la mensualidad</span> (Vida/Salud) — poner el monto nuevo completo
                        </label>
                        {polForm.cambio_mensualidad && (
                          <div style={{marginTop:10}}>
                            <label className="form-label">Nueva mensualidad (lo que paga el cliente, con imp.) *</label>
                            <input className="form-input" type="number" step="0.01" value={polForm.nueva_mensualidad||''} onChange={e=>pf('nueva_mensualidad',e.target.value)} placeholder="Ej: 70.00 (con impuesto)"/>
                            <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>Actualiza las cuotas <strong>pendientes</strong> con vencimiento desde la <strong>Fecha del Endoso</strong> de arriba a este monto. Las pagadas no se tocan.</div>
                          </div>
                        )}
                      </div>
                    )}

                    {polForm.tipo_endoso==='normal' && !endosoDeclarativa && !polForm.cambio_mensualidad && parseFloat(polForm.delta_prima||0) !== 0 && (
                      <div className="form-group" style={{gridColumn:'span 2', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8, padding:'10px 12px'}}>
                        <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:12.5,fontWeight:600}}>
                          <input type="checkbox" checked={!!polForm.prima_recurrente_mensual} onChange={e=>pf('prima_recurrente_mensual', e.target.checked)} style={{accentColor:'var(--sky)',width:15,height:15}}/>
                          El cambio de prima es <span style={{color:'var(--sky-light)'}}>mensual recurrente</span> (se suma a cada letra futura)
                        </label>
                        <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>Márcalo cuando el cambio sube/baja la <strong>cuota mensual</strong> (ej. incluir un dependiente en Vida/Salud individual): el monto (con imp.) se suma a <strong>cada letra futura pendiente</strong>, no se diluye una sola vez. Déjalo sin marcar para un ajuste único.</div>
                      </div>
                    )}

                    {polForm.tipo_endoso==='prorroga' && (
                      <div className="form-group">
                        <label className="form-label">Nueva fecha de vencimiento</label>
                        <input className="form-input" type="date" value={polForm.nueva_fecha_vencimiento||''} onChange={e=>pf('nueva_fecha_vencimiento',e.target.value)}/>
                        <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>La vigencia de la fianza se extiende hasta esta fecha.</div>
                      </div>
                    )}

                    {polForm.tipo_endoso==='normal' && parseFloat(polForm.delta_prima) > 0 && endosoDeclarativa ? (
                      <div className="form-group" style={{gridColumn:'span 2'}}>
                        <div style={{fontSize:12,color:'var(--silver)',background:'rgba(var(--sky-rgb),0.08)',border:'1px solid rgba(var(--sky-rgb),0.2)',borderRadius:8,padding:'10px 12px'}}>
                          <strong>Declarativa:</strong> esta declaración se registra como su <strong>propia letra</strong>, con vencimiento en la <strong>Fecha del Endoso</strong> de arriba (el mes que se declara). Un mismo mes admite varias declaraciones — cada una su letra.
                        </div>
                      </div>
                    ) : polForm.tipo_endoso==='normal' && !polForm.cambio_mensualidad && parseFloat(polForm.delta_prima) > 0 && !polForm.prima_recurrente_mensual ? (
                      <div className="form-group" style={{gridColumn:'span 2'}}>
                        <label className="form-label">Vencimiento del cobro</label>
                        <input className="form-input" type="date" value={polForm.fecha_vencimiento_cobro||''} onChange={e=>pf('fecha_vencimiento_cobro',e.target.value)}/>
                        <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>Fecha en que vence la letra/saldo que genera este endoso. Si la dejas vacía, se usa la fecha del endoso.</div>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
              {/* Datos financieros — prellenados de la solicitud, EDITABLES por si la
                  aseguradora renovó/emitió con valores distintos. */}
              <div style={{background:'rgba(var(--sky-rgb),0.08)',borderRadius:8,padding:'12px 16px',marginBottom:20,border:'1px solid rgba(var(--sky-rgb),0.2)'}}>
                <div style={{fontSize:12,color:'var(--silver)',marginBottom:10,fontWeight:700}}>DATOS DE LA PÓLIZA (revisa y ajusta si cambió en la renovación)</div>
                <div className="form-grid form-grid-3">
                  {/* Aseguradora + cómo la registra la compañía. En una renovación colocada en
                      OTRA aseguradora: para nosotros es renovación, para ella es emisión nueva. */}
                  <div className="form-group" style={{margin:0, gridColumn:'span 2'}}><label className="form-label">Aseguradora *</label>
                    <select className="form-select" value={polForm.aseguradora_id || ''} onChange={e=>aplicarAseguradoraConvert(e.target.value)}>
                      <option value="">— Selecciona —</option>
                      {aseguradoras.map(a=><option key={a.id} value={a.id}>{a.nombre}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{margin:0}}><label className="form-label">Emisión p/ aseguradora</label>
                    <select className="form-select" value={polForm.tipo_emision || 'nueva'} onChange={e=>pf('tipo_emision', e.target.value)}>
                      <option value="nueva">Nueva</option>
                      <option value="renovada">Renovación</option>
                    </select>
                  </div>
                  {selected?.tipo === 'renovacion' && polForm.aseguradora_anterior_id && polForm.aseguradora_id &&
                    String(polForm.aseguradora_id) !== String(polForm.aseguradora_anterior_id) && (
                    <div style={{gridColumn:'1 / -1', display:'flex', alignItems:'flex-start', gap:6, fontSize:12, color:'var(--text-warning)'}}>
                      <AlertTriangle size={13} style={{flexShrink:0, marginTop:2}}/>
                      <span>Cambio de aseguradora: la vigencia anterior era de <strong>{aseguradoras.find(a => String(a.id) === String(polForm.aseguradora_anterior_id))?.nombre || 'otra compañía'}</strong>. Queda como <strong>renovación para nosotros</strong> y <strong>emisión nueva para la aseguradora</strong>, sin arrastre de saldo. Revisa el % de comisión.</span>
                    </div>
                  )}
                  <div className="form-group" style={{margin:0}}><label className="form-label">Prima (sin imp.) *</label><input className="form-input" type="number" step="0.01" value={polForm.prima} onChange={e=>pf('prima',e.target.value)} placeholder="0.00"/></div>
                  <div className="form-group" style={{margin:0}}><label className="form-label">Impuesto (%)</label><input className="form-input" type="number" step="0.01" value={polForm.impuesto} onChange={e=>pf('impuesto',e.target.value)}/></div>
                  {!selected.es_fee && (
                    <div className="form-group" style={{margin:0}}><label className="form-label">Comisión ramo (%)</label><input className="form-input" type="number" step="0.01" value={polForm.comision_ramo} onChange={e=>pf('comision_ramo',e.target.value)}/></div>
                  )}
                  <div className="form-group" style={{margin:0}}><label className="form-label">Forma de pago</label><input className="form-input" value={polForm.forma_pago} onChange={e=>pf('forma_pago',e.target.value)} placeholder="Transferencia, TC..."/></div>
                  <div className="form-group" style={{margin:0}}><label className="form-label">Frecuencia</label>
                    <select className="form-select" value={polForm.frecuencia_pago} onChange={e=>pf('frecuencia_pago',e.target.value)}>
                      <option value="mensual">Mensual</option>
                      <option value="trimestral">Trimestral</option>
                      <option value="semestral">Semestral</option>
                      <option value="anual">Anual</option>
                    </select>
                  </div>
                  <div className="form-group" style={{margin:0}}><label className="form-label">N° de cuotas</label><input className="form-input" type="number" min="1" value={polForm.num_cuotas} onChange={e=>pf('num_cuotas',e.target.value)}/></div>
                </div>
              </div>

              <div className="form-grid form-grid-2">
                <div className="form-group" style={{gridColumn:'span 2'}}>
                  <label className="form-label">Número de Póliza *</label>
                  <input className="form-input" value={polForm.numero} onChange={e=>checkPolizaNumero(e.target.value)} placeholder="POL-2026-001" style={{fontSize:16,fontWeight:600,borderColor: polNumWarning ? 'rgba(239, 68, 68, 0.6)' : ''}}/>
                  {polNumWarning && (
                    <div style={{display:'flex',alignItems:'center',gap:6,marginTop:6,fontSize:12,color:'var(--text-danger)'}}>
                      <AlertTriangle size={13}/> {polNumWarning}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de Inicio *</label>
                  <input className="form-input" type="date" value={polForm.fecha_inicio} onChange={e=>pf('fecha_inicio',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de Vencimiento *</label>
                  <input className="form-input" type="date" value={polForm.fecha_vencimiento} onChange={e=>pf('fecha_vencimiento',e.target.value)}/>
                </div>
                <div className="form-group" style={{gridColumn:'span 2'}}>
                  <label className="form-label">Fecha de Emisión</label>
                  <input className="form-input" type="date" value={polForm.fecha_emision} onChange={e=>pf('fecha_emision',e.target.value)}/>
                </div>
                {selected?.tipo === 'renovacion' && (
                  <div className="form-group" style={{gridColumn:'span 2', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8, padding:'10px 12px'}}>
                    {/* Vigencia que se renueva: prellenada con la póliza de la solicitud y
                        corregible aquí. Es el enlace entre la póliza vieja y la nueva; si
                        queda vacío la cadena de renovación se pierde. */}
                    <label className="form-label" style={{marginBottom:6}}>Esta póliza renueva a *</label>
                    <PolizaSelect
                      value={polForm.poliza_anterior_id || ''}
                      onChange={v => pf('poliza_anterior_id', v ? String(v) : '')}
                      placeholder="Escribe el número de la póliza que se renueva..."
                    />
                    {!polForm.poliza_anterior_id && (
                      <div style={{display:'flex',alignItems:'center',gap:6,marginTop:6,fontSize:12,color:'var(--text-warning)'}}>
                        <AlertTriangle size={13}/> Sin vincular la vigencia anterior, esta renovación queda suelta: el CRM seguirá pidiendo renovar la póliza vieja y no se arrastra su saldo.
                      </div>
                    )}
                    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:12.5,fontWeight:600,marginTop:12}}>
                      <input type="checkbox" checked={polForm.arrastra_saldo !== false}
                        onChange={e=>pf('arrastra_saldo', e.target.checked)} style={{accentColor:'var(--sky)',width:15,height:15}}/>
                      La aseguradora <span style={{color:'var(--sky-light)'}}>arrastra el saldo pendiente</span> de la vigencia anterior
                    </label>
                    <div style={{fontSize:11,color:'var(--silver)',marginTop:4}}>
                      Desmárcalo si la aseguradora emitió una <strong>póliza nueva</strong> (no renovó): la renovación queda <strong>mapeada igual</strong>, pero el cobro arranca <strong>independiente</strong> (sin arrastrar el saldo viejo).
                    </div>
                  </div>
                )}
              </div>
              </>
              )}

              {/* Documentos que el usuario adjuntó al crear la solicitud (solo lectura) */}
              {archivos.length > 0 && (
                <div style={{marginTop:20}}>
                  <div style={{fontSize:12,fontWeight:700,letterSpacing:1,marginBottom:10,color:'var(--silver)'}}>
                    DOCUMENTOS DE LA SOLICITUD
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {archivos.map(a => (
                      <a key={a.id} href="#" onClick={(e)=>{ e.preventDefault(); abrirDocumento(a.url); }}
                        style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'rgba(var(--sky-rgb),0.08)',borderRadius:8,border:'1px solid rgba(var(--sky-rgb),0.18)',fontSize:13,color:'var(--sky-light)',textDecoration:'none'}}>
                        <Paperclip size={13}/> {a.nombre}
                        {a.tipo_doc ? <span style={{color:'var(--silver)',marginLeft:6}}>· {a.tipo_doc}</span> : null}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Adjuntar documentos — obligatorio */}
              {(() => {
                const esEndoso   = selected.tipo === 'endoso';
                const esAutoConv = selected.ramoNombre?.toLowerCase().includes('auto');
                const DocRow = ({ file, onClear, label, required }) => (
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:11,fontWeight:700,letterSpacing:1,marginBottom:6,color: file ? '#22c55e' : required ? '#ef4444' : 'var(--silver)'}}>
                      <Paperclip size={11} style={{display:'inline',marginRight:4}}/>
                      {label}{required && !file && <span style={{fontWeight:400}}> — Requerido</span>}
                    </div>
                    {file ? (
                      <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'rgba(34, 197, 94, 0.08)',borderRadius:8,border:'1px solid rgba(34, 197, 94, 0.2)'}}>
                        <Paperclip size={13} style={{color:'var(--text-success)'}}/>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{file.name}</div>
                          <div style={{fontSize:11,color:'var(--silver)'}}>{fmtBytes(file.size)}</div>
                        </div>
                        <button className="btn btn-icon btn-danger btn-sm" onClick={onClear}><X size={12}/></button>
                      </div>
                    ) : null}
                  </div>
                );
                return (
                  <div style={{marginTop:20}}>
                    <div style={{fontSize:12,fontWeight:700,letterSpacing:1,marginBottom:12,color:'var(--silver)'}}>
                      {esEndoso ? 'DOCUMENTO DEL ENDOSO' : 'DOCUMENTOS DE PÓLIZA'} *
                    </div>
                    <input ref={convertFileFacturaRef} type="file" style={{display:'none'}} onChange={e=>setConvertFileFactura(e.target.files?.[0]||null)}/>
                    <input ref={convertFileCondRef}    type="file" style={{display:'none'}} onChange={e=>setConvertFileCond(e.target.files?.[0]||null)}/>

                    {/* Endoso ya adjunto en el ciclo del CRM: se reutiliza, no hay que re-adjuntar */}
                    {esEndoso && endosoDocExistente && !convertFileFactura && (
                      <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'rgba(34, 197, 94, 0.08)',borderRadius:8,border:'1px solid rgba(34, 197, 94, 0.2)',marginBottom:8}}>
                        <Paperclip size={13} style={{color:'var(--text-success)'}}/>
                        <a href="#" onClick={(e)=>{ e.preventDefault(); abrirDocumento(endosoDocExistente.url); }} style={{flex:1,minWidth:0,fontSize:13,color:'var(--sky-light)',textDecoration:'none',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {endosoDocExistente.nombre || 'Endoso recibido adjunto'}
                        </a>
                        <span style={{fontSize:11,color:'var(--text-success)'}}>ya adjunto</span>
                      </div>
                    )}

                    {/* Factura / documento (todos los tipos) */}
                    <DocRow
                      file={convertFileFactura} required={!(esEndoso && endosoDocExistente)}
                      onClear={()=>setConvertFileFactura(null)}
                      label={esEndoso ? 'DOCUMENTO DEL ENDOSO' : esAutoConv ? 'FACTURA / PÓLIZA' : 'FACTURA'}
                    />
                    {!convertFileFactura && (
                      <button className="btn btn-secondary btn-sm" style={{borderColor: (esEndoso && endosoDocExistente) ? 'rgba(var(--silver-rgb),0.3)' : 'rgba(239, 68, 68, 0.4)',marginBottom:12}}
                        onClick={()=>convertFileFacturaRef.current?.click()}>
                        <Paperclip size={13}/> {esEndoso ? (endosoDocExistente ? 'Reemplazar endoso' : 'Adjuntar endoso') : esAutoConv ? 'Adjuntar Factura / Póliza' : 'Adjuntar Factura'}
                      </button>
                    )}
                    {/* Leer el documento adjunto con IA y autocompletar los datos de la póliza */}
                    {!esEndoso && convertFileFactura && (
                      <button className="btn btn-primary btn-sm" style={{marginBottom:12}} disabled={leyendoPolizaIA}
                        onClick={autocompletarConvertIA}>
                        {leyendoPolizaIA ? 'Leyendo…' : '✨ Leer con IA (autocompleta número, fechas y prima)'}
                      </button>
                    )}
                    {/* Validar con IA que el endoso recibido coincide con lo solicitado (opcional) */}
                    {esEndoso && (convertFileFactura || endosoDocExistente) && (
                      <div style={{marginBottom:12}}>
                        <button className="btn btn-secondary btn-sm" disabled={validandoEndosoIA}
                          onClick={validarEndosoConvertIA}>
                          {validandoEndosoIA ? 'Analizando…' : '✨ Validar con IA (compara el endoso vs lo solicitado)'}
                        </button>
                        {iaEndosoResultado && (
                          iaEndosoResultado.error ? (
                            <div style={{marginTop:8,padding:'8px 12px',background:'rgba(239, 68, 68, 0.08)',border:'1px solid rgba(239, 68, 68, 0.25)',borderRadius:8,fontSize:12,color:'var(--text-danger)'}}>
                              {iaEndosoResultado.error}
                            </div>
                          ) : (
                            <div style={{marginTop:8,padding:'8px 12px',borderRadius:8,fontSize:12,
                              background: iaEndosoResultado.coincide ? 'rgba(34, 197, 94, 0.08)' : 'rgba(245, 158, 11, 0.1)',
                              border: iaEndosoResultado.coincide ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(245, 158, 11, 0.35)',
                              color: iaEndosoResultado.coincide ? '#22c55e' : '#f59e0b'}}>
                              <strong>{iaEndosoResultado.coincide ? '✓ El endoso coincide con lo solicitado.' : '⚠ El endoso NO coincide del todo con lo solicitado.'}</strong>
                              {iaEndosoResultado.resumen && <div style={{marginTop:4,color:'var(--silver)'}}>{iaEndosoResultado.resumen}</div>}
                              {Array.isArray(iaEndosoResultado.discrepancias) && iaEndosoResultado.discrepancias.length > 0 && (
                                <ul style={{margin:'6px 0 0 16px',color:'var(--silver)'}}>
                                  {iaEndosoResultado.discrepancias.map((x,i)=><li key={i}>{typeof x === 'string' ? x : JSON.stringify(x)}</li>)}
                                </ul>
                              )}
                              {!iaEndosoResultado.coincide && <div style={{marginTop:6,color:'var(--silver)'}}>Igual puedes registrarlo (la aseguradora ya lo emitió) y pedir la corrección después.</div>}
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {/* Condiciones Particulares — solo non-Auto pólizas nuevas */}
                    {!esEndoso && !esAutoConv && (
                      <>
                        <DocRow
                          file={convertFileCond} required
                          onClear={()=>setConvertFileCond(null)}
                          label="CONDICIONES PARTICULARES"
                        />
                        {!convertFileCond && (
                          <button className="btn btn-secondary btn-sm" style={{borderColor:'rgba(239, 68, 68, 0.4)'}}
                            onClick={()=>convertFileCondRef.current?.click()}>
                            <Paperclip size={13}/> Adjuntar Condiciones Particulares
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
              {(() => {
                const esEndoso   = selected.tipo === 'endoso';
                const esAutoConv = selected.ramoNombre?.toLowerCase().includes('auto');
                const docsOk = esEndoso
                  ? (!!convertFileFactura || !!endosoDocExistente)
                  : esAutoConv
                    ? !!convertFileFactura
                    : !!convertFileFactura && !!convertFileCond;
                return (
                  <button className="btn btn-primary" onClick={handleConvert}
                    disabled={converting||uploadingConvert||!docsOk}
                    title={!docsOk ? 'Debes adjuntar los documentos requeridos' : ''}>
                    {converting||uploadingConvert
                      ? (esEndoso ? 'Registrando endoso...' : 'Creando póliza...')
                      : esEndoso
                        ? <><RefreshCw size={14}/> Registrar Endoso</>
                        : <><RefreshCw size={14}/> Confirmar y Crear Póliza</>}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {/* ══ Modal: Cancelar Solicitud ════════════════════════════ */}
      {/* ══ Modal: Enviado al Cliente (endoso) — evidencia del correo OBLIGATORIA ══ */}
      {envioCliente && selected && (
        <div className="modal-overlay" style={{zIndex:1200}} onClick={()=>setEnvioCliente(null)}>
          <div className="modal" style={{maxWidth:520}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Enviado al Cliente</div>
                <div className="text-silver text-sm">{numSol(selected.id)} · paso final del endoso</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setEnvioCliente(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{fontSize:13,color:'var(--silver)',marginBottom:14}}>
                Adjunta la evidencia del correo con el que le enviaste el endoso al asegurado:
                el <strong>PDF/imagen del correo</strong> o <strong>pega el texto</strong>. Queda guardada en la solicitud y cierra la tarea del CRM.
              </div>
              <div style={{display:'flex',gap:8,marginBottom:14}}>
                {[['file','📎 Adjuntar PDF / imagen'],['texto','✍️ Pegar texto del correo']].map(([m,l])=>(
                  <button key={m} className={`btn btn-sm ${envioCliente.modo===m?'btn-primary':'btn-secondary'}`}
                    onClick={()=>setEnvioCliente(p=>({ ...p, modo:m }))}>{l}</button>
                ))}
              </div>
              {envioCliente.modo === 'file' ? (
                <div className="form-group">
                  <label className="form-label">Correo enviado (PDF o imagen) *</label>
                  <input className="form-input" type="file" accept=".pdf,image/*"
                    onChange={e=>setEnvioCliente(p=>({ ...p, file: e.target.files?.[0]||null }))} />
                  {envioCliente.file && <div style={{fontSize:12,color:'var(--text-success)',marginTop:6}}>{envioCliente.file.name}</div>}
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Texto del correo enviado *</label>
                  <textarea className="form-textarea" rows={6} value={envioCliente.texto}
                    onChange={e=>setEnvioCliente(p=>({ ...p, texto: e.target.value }))}
                    placeholder="Pega aquí el contenido del correo que le enviaste al asegurado..."/>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setEnvioCliente(null)}>Volver</button>
              <button className="btn btn-primary" onClick={confirmarEnvioClienteSol}
                disabled={enviandoCliente || (envioCliente.modo==='texto' ? !envioCliente.texto.trim() : !envioCliente.file)}>
                {enviandoCliente ? 'Guardando...' : 'Confirmar envío al cliente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelModal && (
        <div className="modal-overlay" style={{zIndex:1200}} onClick={()=>setCancelModal(null)}>
          <div className="modal" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Cancelar Solicitud</div>
                <div className="text-silver text-sm">{numSol(cancelModal.id)} · {cancelModal.clienteNombre}</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setCancelModal(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{background:'rgba(239, 68, 68, 0.08)',border:'1px solid rgba(239, 68, 68, 0.25)',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'var(--text-danger)'}}>
                La solicitud no se elimina — queda como <strong>Cancelada</strong> y puedes consultarla cuando necesites.
              </div>
              <div className="form-group">
                <label className="form-label">Motivo de cancelación * <span style={{fontSize:11,fontWeight:400,color:'var(--silver)'}}>(requerido — queda registrado)</span></label>
                <textarea className="form-textarea" rows={3} value={cancelReason}
                  onChange={e=>setCancelReason(e.target.value)}
                  placeholder="Ej: Cliente desistió, duplicada por error, condiciones no aceptadas..."/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setCancelModal(null)}>Volver</button>
              <button className="btn btn-danger" onClick={confirmCancel} disabled={!cancelReason.trim()}>
                Confirmar Cancelación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Reactivar solicitud ════════════════════════════ */}
      {reactivarModal && (
        <div className="modal-overlay" style={{zIndex:1200}} onClick={()=>{ setReactivarModal(null); setReactivarConfirm(false); setReactivarReason(''); }}>
          <div className="modal" style={{maxWidth:460}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Reactivar Solicitud</div>
                <div className="text-silver text-sm">{numSol(reactivarModal.id)} · {reactivarModal.clienteNombre}</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>{ setReactivarModal(null); setReactivarConfirm(false); setReactivarReason(''); }}><X size={16}/></button>
            </div>
            <div className="modal-body">
              {reactivarModal.motivo_cancelacion && (
                <div style={{background:'rgba(239, 68, 68, 0.08)',border:'1px solid rgba(239, 68, 68, 0.2)',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12}}>
                  <div style={{color:'var(--text-danger)',fontWeight:700,marginBottom:2}}>CANCELADA POR</div>
                  <div style={{color:'#e2e8f0'}}>{reactivarModal.motivo_cancelacion}</div>
                </div>
              )}
              <div className="form-group" style={{marginBottom:16}}>
                <label className="form-label">Motivo de reactivación * <span style={{fontSize:11,fontWeight:400,color:'var(--silver)'}}>(requerido)</span></label>
                <textarea className="form-textarea" rows={3} value={reactivarReason}
                  onChange={e=>setReactivarReason(e.target.value)}
                  placeholder="Ej: Cliente retomó el proceso, error al cancelar, nueva negociación..."/>
              </div>
              <div style={{background:'rgba(245, 158, 11, 0.08)',border:'1px solid rgba(245, 158, 11, 0.25)',borderRadius:8,padding:'12px 14px'}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <input type="checkbox" id="reactivar-confirm" checked={reactivarConfirm}
                    onChange={e=>setReactivarConfirm(e.target.checked)}
                    style={{accentcolor:'var(--text-warning)',width:16,height:16,cursor:'pointer',flexShrink:0}}/>
                  <label htmlFor="reactivar-confirm" style={{fontSize:13,color:'var(--text-warning)',cursor:'pointer',fontWeight:500}}>
                    Confirmo que quiero reactivar esta solicitud como <strong>Borrador</strong>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>{ setReactivarModal(null); setReactivarConfirm(false); setReactivarReason(''); }}>Cancelar</button>
              <button
                className="btn btn-primary"
                onClick={confirmReactivar}
                disabled={!reactivarReason.trim() || !reactivarConfirm}
                style={{background:'rgba(34, 197, 94, 0.15)',borderColor:'rgba(34, 197, 94, 0.4)',color:'var(--text-success)'}}>
                <RefreshCw size={14}/> Reactivar Solicitud
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Mini-modal: Crear cliente rápido ══════════════════════ */}
      {clienteModal && (
        <div className="modal-overlay" style={{zIndex:1100}} onClick={()=>setClienteModal(false)}>
          <div className="modal" style={{maxWidth:520}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Nuevo Cliente</div>
                <div className="text-silver text-sm">Datos mínimos — completa el expediente después</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setClienteModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              {/* Tipo */}
              <div style={{display:'flex',gap:8,marginBottom:16}}>
                {[['natural','Persona Natural'],['juridica','Persona Jurídica']].map(([v,l])=>(
                  <button key={v} onClick={()=>setClienteForm(p=>({...p,tipo:v}))}
                    style={{flex:1,padding:'8px 0',borderRadius:8,border:'1px solid',cursor:'pointer',fontFamily:'Outfit,sans-serif',fontSize:13,fontWeight:600,
                      background:clienteForm.tipo===v?'var(--sky)':'transparent',
                      color:clienteForm.tipo===v?'#fff':'var(--silver)',
                      borderColor:clienteForm.tipo===v?'var(--sky)':'rgba(var(--silver-rgb),0.2)'}}>
                    {l}
                  </button>
                ))}
              </div>

              {clienteForm.tipo === 'natural' ? (
                <div className="form-grid form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Primer nombre *</label>
                    <input className="form-input" value={clienteForm.primer_nombre} onChange={e=>setClienteForm(p=>({...p,primer_nombre:e.target.value}))} placeholder="Juan"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Segundo nombre</label>
                    <input className="form-input" value={clienteForm.segundo_nombre} onChange={e=>setClienteForm(p=>({...p,segundo_nombre:e.target.value}))} placeholder="Carlos"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Primer apellido *</label>
                    <input className="form-input" value={clienteForm.primer_apellido} onChange={e=>setClienteForm(p=>({...p,primer_apellido:e.target.value}))} placeholder="Pérez"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Segundo apellido</label>
                    <input className="form-input" value={clienteForm.segundo_apellido} onChange={e=>setClienteForm(p=>({...p,segundo_apellido:e.target.value}))} placeholder="González"/>
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Razón Social *</label>
                  <input className="form-input" value={clienteForm.nombre} onChange={e=>setClienteForm(p=>({...p,nombre:e.target.value}))} placeholder="Empresa S.A."/>
                </div>
              )}

              <div style={{marginTop:8,padding:'10px 14px',background:'rgba(var(--sky-rgb),0.08)',borderRadius:8,border:'1px solid rgba(var(--sky-rgb),0.2)',fontSize:12,color:'var(--silver)',marginBottom:12}}>
                Al menos un contacto es requerido para dar seguimiento
              </div>
              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input className="form-input" value={clienteForm.telefono} onChange={e=>setClienteForm(p=>({...p,telefono:e.target.value}))} placeholder="507-6000-0000"/>
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp</label>
                  <input className="form-input" value={clienteForm.whatsapp} onChange={e=>setClienteForm(p=>({...p,whatsapp:e.target.value}))} placeholder="507-6000-0000"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Correo</label>
                  <input className="form-input" type="email" value={clienteForm.email} onChange={e=>setClienteForm(p=>({...p,email:e.target.value}))} placeholder="correo@ejemplo.com"/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setClienteModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSaveClienteInline} disabled={savingCliente}>
                {savingCliente ? 'Guardando...' : 'Crear y Seleccionar'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Helpers de estilo ────────────────────────────────────────────
const inputSm = {
  padding:'4px 8px', background:'rgba(var(--steel-rgb),0.5)',
  border:'1px solid rgba(var(--silver-rgb),0.2)', borderRadius:6,
  color:'var(--text)', fontFamily:'Outfit,sans-serif', fontSize:12,
};

const thSm = {
  padding:'8px 12px', textAlign:'left',
  fontSize:11, color:'var(--silver)', fontWeight:600,
};

function SectionTitle({ children, style }) {
  return (
    <div style={{fontSize:12,fontWeight:700,color:'var(--silver)',letterSpacing:1,marginBottom:10,...style}}>
      {children}
    </div>
  );
}
